import React, { useMemo, useState, useEffect, useRef } from 'react';
import { auth, db } from '../../../../firebase/config';
import { collection, getDocs, doc as fsDoc, getDoc, query, where, orderBy, limit } from 'firebase/firestore';
import { useAppSelector } from '../../../../store';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import Toast from '../../../../components/Toast';
import Modal from '../../../../components/Modal';
import Icon from '../../../../components/Icon';
import { FiSearch, FiEye, FiCheck, FiX, FiClock, FiAlertCircle, FiFile, FiFileText, FiDownload, FiPlus, FiRotateCcw, FiMail, FiCalendar, FiChevronDown, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { DatePicker, TimePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import TableComp, { Column } from '../../../../components/Table';
import {
    fetchCompanyIdAndPsoInfo,
    fetchBranches,
    fetchManagedPsos,
    fetchRequests,
    createRequest,
    deleteRequest,
    updateRequestStatus,
    forwardRequestToHead,
    resubmitRequest,
    fetchAvailableCompanies,
    fetchGuardRate,
    updateGuardRate,
    isRegionalLeadDesignation,
    isHeadPsoDesignation,
    isBasePsoDesignation,
    normalizePsoDesignationLabel,
    type Request
} from '../service/requestManagementService';
import { fetchPSOEmail, sendEmailsWithErrorHandling, validateEmails } from '../../../../api/emailService';
import { fetchVendors, getVendorById } from '../../../branch_vendors/service/branchVendorService';
import { fetchBranches as fetchBankBranchesDeploymentStyle } from '../../../deployments/service/deploymentsService';
import { fetchCompaniesData } from '../../../companies/service/companiesService';

/** When search/filters narrow the list, cap PDF detail rows (full data in Excel). Same idea as Surveys. */
const REQUEST_PDF_FILTERED_ROW_CAP = 45;
class ExportCancelledError extends Error {
    constructor() {
        super('Export cancelled');
        this.name = 'ExportCancelledError';
    }
}

function isExportCancelledError(e: unknown): boolean {
    return e instanceof ExportCancelledError;
}

/** Load remote bank logo for jsPDF (CORS must allow canvas read). Same pattern as Surveys. */
function tryLoadImageForPdf(url: string): Promise<{ dataUrl: string; format: 'PNG'; w: number; h: number } | null> {
    if (!url || typeof url !== 'string') return Promise.resolve(null);
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    resolve(null);
                    return;
                }
                ctx.drawImage(img, 0, 0);
                resolve({
                    dataUrl: canvas.toDataURL('image/png'),
                    format: 'PNG',
                    w: img.naturalWidth,
                    h: img.naturalHeight,
                });
            } catch {
                resolve(null);
            }
        };
        img.onerror = () => resolve(null);
        img.src = url;
    });
}

const recipientChipClass =
    'inline-flex max-w-full min-w-0 items-start gap-2 rounded-lg border border-slate-200/90 bg-white py-2 pl-2.5 pr-1.5 text-left shadow-sm ring-1 ring-slate-100/80 dark:border-slate-600 dark:bg-slate-800/90 dark:ring-slate-700/80';
const recipientChipTextClass =
    'min-w-0 max-w-full flex-1 break-words text-xs font-medium leading-relaxed text-slate-800 dark:text-slate-100';

function escapeHtml(text: string): string {
    if (!text) return '';
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function getFormattedPurpose(request: Request): string {
    const p = String((request as any).purposeOfFootage || request.purpose || '').trim();
    const other = String(request.purposeOther || (request as any).purpose_other || '').trim();
    if (p.toLowerCase() === 'other' && other) {
        return other;
    }
    if (p.toLowerCase() === 'police pressure' && other) {
        return `${p}: ${other}`;
    }
    return p || 'N/A';
}

function getEmailDateFields(request: Request): { dateFrom: unknown; dateTo: unknown } {
    const requestType = request.requestType?.toLowerCase();
    if (requestType === 'guard') {
        return {
            dateFrom: request.guardDateFrom || request.cctvDateFrom || (request as any).dateFrom,
            dateTo: request.guardDateTo || request.cctvDateTo || (request as any).dateTo,
        };
    }
    if (requestType === 'bams') {
        return {
            dateFrom: request.bamsDateFrom || request.cctvDateFrom || (request as any).dateFrom,
            dateTo: request.bamsDateTo || request.cctvDateTo || (request as any).dateTo,
        };
    }
    return {
        dateFrom: request.cctvDateFrom || (request as any).dateFrom,
        dateTo: request.cctvDateTo || (request as any).dateTo,
    };
}

function parseEmailDateValue(date: unknown): Date | null {
    if (!date) return null;
    try {
        let d: Date;
        if (typeof date === 'object' && date !== null && 'toDate' in date && typeof (date as { toDate: () => Date }).toDate === 'function') {
            d = (date as { toDate: () => Date }).toDate();
        } else if (typeof date === 'object' && date !== null && 'seconds' in date) {
            d = new Date((date as { seconds: number }).seconds * 1000);
        } else {
            d = new Date(date as string | number);
        }
        if (Number.isNaN(d.getTime())) return null;
        return d;
    } catch {
        return null;
    }
}

function formatEmailDateOnly(date: unknown): string {
    const d = parseEmailDateValue(date);
    if (!d) return 'N/A';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
}

function formatEmailDateIso(date: unknown): string {
    const d = parseEmailDateValue(date);
    if (!d) return 'N/A';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
}

function pickEmailText(...vals: unknown[]): string {
    for (const v of vals) {
        if (v == null) continue;
        const s = String(v).trim();
        if (s) return s;
    }
    return '';
}

/**
 * Branch label for email — same idea as Guard table / form (`branchName || address`).
 * Avoid using `location` as address; it is often region and made CCTV rows look wrong.
 */
function getEmailBranchNameWithAddress(request: Request): string {
    const r = request as Record<string, unknown>;
    const primary = pickEmailText(
        request.branchName,
        r.branchTitle,
        r.name,
        r.branch_title
    );
    const secondary = pickEmailText(r.branchAddress, r.address, r.branchLocation);
    if (primary && secondary && primary !== secondary) return `${primary} (${secondary})`;
    return pickEmailText(primary, secondary) || 'N/A';
}

/** Inclusive day span between from/to — aligned with requestManagementService guard billing. */
function formatInclusiveNoOfDays(dateFrom: unknown, dateTo: unknown): string {
    const a = parseEmailDateValue(dateFrom);
    const b = parseEmailDateValue(dateTo);
    if (!a || !b) return 'N/A';
    const diffTime = Math.abs(b.getTime() - a.getTime());
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return String(days);
}

function formatEmailGeneratedOn(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function resolveActionAt(request: Request, explicit?: Date): Date {
    if (explicit) return explicit;
    const u = request.updatedAt;
    if (u && typeof u === 'object') {
        if ('toDate' in u && typeof (u as { toDate: () => Date }).toDate === 'function') {
            return (u as { toDate: () => Date }).toDate();
        }
        if ('seconds' in u) {
            return new Date((u as { seconds: number }).seconds * 1000);
        }
    }
    return new Date();
}

function buildRequestDetailsTableBlock(request: Request): { sectionTitle: string; tableHtml: string } {
    const dates = getEmailDateFields(request);
    const df = formatEmailDateOnly(dates.dateFrom);
    const dt = formatEmailDateOnly(dates.dateTo);
    const noOfDays = escapeHtml(formatInclusiveNoOfDays(dates.dateFrom, dates.dateTo));
    const branchName = escapeHtml(getEmailBranchNameWithAddress(request));
    const branchCode = escapeHtml(pickEmailText(request.branchCode, (request as any).branch_code) || 'N/A');
    const purposeRaw = getFormattedPurpose(request);
    const purpose = escapeHtml(purposeRaw || 'N/A');
    const rt = (request.requestType || '').toLowerCase();
    const td = 'border:1px solid #d1d5db;padding:10px 8px;font-size:12px;color:#333;';
    const th = 'border:1px solid #1e3a5f;background:#1e3a5f;color:#fff;padding:10px 8px;font-size:11px;font-weight:bold;text-align:left;';

    if (rt === 'guard') {
        const guards = escapeHtml(String(request.numberOfGuards ?? 'N/A'));
        return {
            sectionTitle: 'DETAILS OF GUARD REQUEST',
            tableHtml: `
<table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:1px solid #d1d5db;">
<tr>
<th style="${th}">BRANCH CODE</th><th style="${th}">NAME OF BRANCH</th><th style="${th}">NO. OF GUARDS</th>
<th style="${th}">DATE FROM</th><th style="${th}">DATE TO</th><th style="${th}">NO. OF DAYS</th><th style="${th}">PURPOSE</th>
</tr>
<tr>
<td style="${td}">${branchCode}</td><td style="${td}">${branchName}</td><td style="${td}">${guards}</td>
<td style="${td}">${escapeHtml(df)}</td><td style="${td}">${escapeHtml(dt)}</td><td style="${td}">${noOfDays}</td><td style="${td}">${purpose}</td>
</tr>
</table>`,
        };
    }

    if (rt === 'bams') {
        const hw = escapeHtml(String(request.hardwareName || 'N/A'));
        return {
            sectionTitle: 'DETAILS OF BAMS REQUEST',
            tableHtml: `
<table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:1px solid #d1d5db;">
<tr>
<th style="${th}">BRANCH CODE</th><th style="${th}">NAME OF BRANCH</th><th style="${th}">DATE FROM</th><th style="${th}">DATE TO</th>
<th style="${th}">NO. OF DAYS</th><th style="${th}">HARDWARE</th><th style="${th}">PURPOSE</th>
</tr>
<tr>
<td style="${td}">${branchCode}</td><td style="${td}">${branchName}</td><td style="${td}">${escapeHtml(df)}</td><td style="${td}">${escapeHtml(dt)}</td>
<td style="${td}">${noOfDays}</td><td style="${td}">${hw}</td><td style="${td}">${purpose}</td>
</tr>
</table>`,
        };
    }

    const timeFrom = escapeHtml(
        pickEmailText((request as any).cctvTimeFrom, request.timeFrom, (request as any).time_from) || 'N/A'
    );
    const timeTo = escapeHtml(
        pickEmailText((request as any).cctvTimeTo, request.timeTo, (request as any).time_to) || 'N/A'
    );
    const dateFromIso = escapeHtml(formatEmailDateIso(dates.dateFrom));
    const dateToIso = escapeHtml(formatEmailDateIso(dates.dateTo));
    const thSub = `${th};text-align:center;font-size:10px;`;
    const colCount = 6;
    return {
        sectionTitle: 'Details of CCTV Footage',
        tableHtml: `
<table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:1px solid #d1d5db;">
<tr>
<th rowspan="2" style="${th};vertical-align:middle;">BRANCH CODE</th>
<th rowspan="2" style="${th};vertical-align:middle;">NAME OF BRANCH</th>
<th colspan="2" style="${th};text-align:center;">TIME</th>
<th colspan="2" style="${th};text-align:center;">DATE</th>
</tr>
<tr>
<th style="${thSub}">FROM</th><th style="${thSub}">TO</th>
<th style="${thSub}">FROM</th><th style="${thSub}">TO</th>
</tr>
<tr>
<td style="${td}">${branchCode}</td>
<td style="${td}">${branchName}</td>
<td style="${td}">${timeFrom}</td>
<td style="${td}">${timeTo}</td>
<td style="${td}">${dateFromIso}</td>
<td style="${td}">${dateToIso}</td>
</tr>
<tr>
<td colspan="${colCount}" style="${td};background:#f8fafc;"><strong>Purpose:</strong> ${purpose}</td>
</tr>
</table>`,
    };
}

/** HBL-style HTML (table layout) — matches PSO Request System Support branding. */
function buildPsoRequestStatusEmailHtml(request: Request, variant: 'approved' | 'rejected', actionAt?: Date): string {
    const id = escapeHtml(String(request.requestId || request.id || ''));
    const rtRaw = (request.requestType || 'REQUEST').toUpperCase();
    const rt = escapeHtml(rtRaw);
    const bannerTitle = variant === 'approved' ? `${rtRaw} APPROVAL` : `${rtRaw} REJECTION`;
    const statusLabel = variant === 'approved' ? 'APPROVED' : 'REJECTED';
    const statusBg = variant === 'approved' ? '#2e7d32' : '#c62828';
    const dateFieldLabel = variant === 'approved' ? 'Approval Date' : 'Rejection Date';
    const actionDateStr = formatEmailDateOnly(resolveActionAt(request, actionAt));
    const generatedOn = formatEmailGeneratedOn(new Date());
    const intro =
        variant === 'approved'
            ? `The following ${rt} request has been successfully processed.`
            : `The following ${rt} request has been <strong>rejected</strong>.`;

    const { sectionTitle, tableHtml } = buildRequestDetailsTableBlock(request);
    const sectionEsc = escapeHtml(sectionTitle);

    let resubmissionHtml = '';
    if (request.resubmitted && request.resubmissionReason) {
        resubmissionHtml = `
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#fffbeb;border:1px solid #fef3c7;border-radius:6px;margin-bottom:20px;font-family:Arial,Helvetica,sans-serif;">
<tr><td style="padding:16px 18px;">
<strong style="color:#d97706;font-size:13px;display:block;margin-bottom:6px;">Resubmission Information:</strong>
<div style="font-size:13px;color:#451a03;line-height:1.45;">
<strong>Reason:</strong> ${escapeHtml(request.resubmissionReason)}
</div>
${request.resubmittedAt ? `<div style="margin-top:6px;font-size:11px;color:#78350f;">Resubmitted on: ${escapeHtml(formatEmailDateOnly(request.resubmittedAt))}</div>` : ''}
</td></tr>
</table>`;
    }

    return `<!DOCTYPE html>
<html><body style="margin:0;padding:0;background:#eef1f5;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#eef1f5;padding:24px 12px;">
<tr><td align="center">
<table role="presentation" width="640" cellpadding="0" cellspacing="0" style="max-width:640px;width:100%;background:#ffffff;border-radius:8px;overflow:hidden;border:1px solid #e2e8f0;">
<tr><td align="center" style="padding:24px 20px 8px;">
<div style="color:#00838f;font-size:28px;font-weight:800;letter-spacing:2px;line-height:1;">HBL</div>
<div style="color:#1e3a5f;font-size:11px;font-weight:700;letter-spacing:3px;margin-top:6px;">HBL PSO SUPPORT</div>
</td></tr>
<tr><td style="background:#1e5aa8;background:linear-gradient(135deg,#1e5aa8 0%,#2d7bc4 100%);padding:22px 24px;text-align:center;">
<div style="color:#ffffff;font-size:20px;font-weight:800;letter-spacing:1px;line-height:1.2;">${escapeHtml(bannerTitle)}</div>
<div style="color:#e3f2fd;font-size:14px;margin-top:10px;font-weight:600;">Request ID: ${id}</div>
</td></tr>
<tr><td style="padding:24px 22px;font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#333;line-height:1.55;">
<p style="margin:0 0 10px;">Dear Team,</p>
<p style="margin:0 0 20px;">${intro}</p>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f1f5f9;border-radius:6px;margin-bottom:20px;">
<tr><td style="padding:16px 18px;">
<strong style="color:#475569;">Status:</strong>
<span style="display:inline-block;margin-left:10px;padding:7px 16px;background:${statusBg};color:#ffffff;font-weight:800;font-size:11px;letter-spacing:1px;border-radius:999px;">${statusLabel}</span>
<div style="margin-top:12px;font-size:14px;color:#334155;"><strong>${dateFieldLabel}:</strong> <span style="color:#0f172a;font-weight:600;">${escapeHtml(actionDateStr)}</span></div>
</td></tr>
</table>
${resubmissionHtml}
<div style="background:#1e3a5f;color:#ffffff;font-weight:700;font-size:12px;padding:11px 14px;letter-spacing:0.5px;">${sectionEsc}</div>
${tableHtml}
</td></tr>
<tr><td style="padding:22px 20px;text-align:center;font-size:11px;color:#64748b;border-top:1px solid #e2e8f0;font-family:Arial,Helvetica,sans-serif;">
<div style="font-weight:700;color:#475569;margin-bottom:6px;letter-spacing:1px;">HBL PSO SUPPORT</div>
<div>This is an automated email from PSO Request Management System.</div>
<div style="margin-top:8px;color:#475569;">Generated on: ${escapeHtml(generatedOn)}</div>
<div style="margin-top:14px;font-size:10px;color:#94a3b8;">PSO Request System Support</div>
</td></tr>
</table>
</td></tr>
</table>
</body></html>`;
}

function buildRequestApprovalEmailHtml(request: Request, actionAt?: Date): string {
    return buildPsoRequestStatusEmailHtml(request, 'approved', actionAt);
}

function buildRequestRejectionEmailHtml(request: Request, actionAt?: Date): string {
    return buildPsoRequestStatusEmailHtml(request, 'rejected', actionAt);
}

function normalizeBranchCodeForVendorMatch(code: string | undefined): string {
    return String(code || '').trim().toUpperCase();
}

/** Match branch codes when punctuation/spacing differs (e.g. deployments vs requests). */
function normalizeBranchCodeLoose(code: string | undefined): string {
    return String(code || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
}

function pickVendorContactEmail(v: Record<string, unknown> | null | undefined): string {
    if (!v) return '';
    const from =
        (v.contactEmail as string) ||
        (v.email as string) ||
        (v.vendorEmail as string) ||
        (v.contactPersonEmail as string) ||
        '';
    return String(from).trim();
}

/**
 * Load branch by `branchCode` from Firestore (exact match tries common variants).
 * Vendor management stores `branchCode` on each branch doc under `banks/{bankId}/branch`.
 */
async function fetchBranchDocByBranchCodeFromFirestore(
    bankId: string,
    branchCode: string | undefined
): Promise<Record<string, unknown> | null> {
    const bid = String(bankId || '').trim();
    const raw = String(branchCode || '').trim();
    if (!bid || !raw) return null;

    const variants = Array.from(
        new Set([raw, raw.toUpperCase(), raw.toLowerCase(), normalizeBranchCodeForVendorMatch(raw)])
    );
    for (const code of variants) {
        if (!code) continue;
        try {
            const snap = await getDocs(
                query(collection(db, 'banks', bid, 'branch'), where('branchCode', '==', code), limit(3))
            );
            if (!snap.empty) {
                const d = snap.docs[0];
                return { id: d.id, ...d.data() } as Record<string, unknown>;
            }
        } catch (e) {
            console.error('Error querying branch by branchCode:', e);
        }
    }
    return null;
}

/**
 * Resolve branch document for vendor lookup — same `cctvVendorId` / `bamsVendorId` / `guardVendorId`
 * fields as branch vendor management (`BranchVendorManagement` / `fetchBranchVendorAssignments`).
 */
async function resolveBranchForVendorEmail(
    request: Request,
    bankId: string,
    branches?: unknown[]
): Promise<Record<string, unknown> | null> {
    const bid = String(bankId || '').trim();
    if (!bid) return null;

    const list = Array.isArray(branches) ? branches : [];
    const branchId = typeof request.branchId === 'string' ? request.branchId.trim() : '';

    if (branchId && list.length > 0) {
        const found = list.find((b: any) => String(b?.id || '').trim() === branchId);
        if (found) return found as Record<string, unknown>;
    }

    const codeNorm = normalizeBranchCodeForVendorMatch(request.branchCode);
    const codeLoose = normalizeBranchCodeLoose(request.branchCode);
    if (list.length > 0) {
        if (codeNorm) {
            const found = list.find(
                (b: any) => normalizeBranchCodeForVendorMatch(b?.branchCode as string | undefined) === codeNorm
            );
            if (found) return found as Record<string, unknown>;
        }
        if (codeLoose) {
            const foundLoose = list.find(
                (b: any) =>
                    normalizeBranchCodeLoose(b?.branchCode as string | undefined) === codeLoose
            );
            if (foundLoose) return foundLoose as Record<string, unknown>;
        }
    }

    if (branchId) {
        try {
            const snap = await getDoc(fsDoc(db, 'banks', bid, 'branch', branchId));
            if (snap.exists()) return { id: snap.id, ...snap.data() } as Record<string, unknown>;
        } catch (e) {
            console.error('Error loading branch by id for vendor email:', e);
        }
    }

    const fromCode = await fetchBranchDocByBranchCodeFromFirestore(bid, request.branchCode);
    if (fromCode) return fromCode;

    return null;
}

function vendorKeyFromRequestType(request: Request): 'cctv' | 'bams' | 'guard' | 'unknown' {
    const rt = String(request.requestType || '').trim().toLowerCase();
    if (rt === 'cctv' || rt.includes('cctv')) return 'cctv';
    if (rt === 'bams' || rt.includes('bams')) return 'bams';
    if (rt === 'guard' || rt.includes('guard') || rt.includes('guarding')) return 'guard';
    return 'unknown';
}

function companyTypeMatchesVendorKey(companyType: string | undefined, key: 'cctv' | 'bams' | 'guard' | 'unknown'): boolean {
    const t = String(companyType || '').trim().toLowerCase();
    if (key === 'cctv') return t === 'cctv' || t.includes('cctv');
    if (key === 'bams') return t === 'bams' || t.includes('bams');
    if (key === 'guard') return t.includes('guard') || t === 'guarding' || t === 'guard company';
    return true;
}

/**
 * Emails from companies deployed on this branch (`banks/{bankId}/companyDeployment`), same join as
 * {@link ../../../deployments/service/deploymentsService.ts} `fetchDeploymentsData` (branch + companies).
 * Does **not** filter by deployment.psoId — any deployment on the branch counts for vendor email.
 */
async function collectDeployedCompanyEmailsForBranch(
    bankId: string,
    branchId: string,
    key: 'cctv' | 'bams' | 'guard' | 'unknown',
    uid: string,
    isBankUser: boolean
): Promise<string[]> {
    const bid = String(bankId || '').trim();
    const brid = String(branchId || '').trim();
    if (!bid || !brid || !uid) return [];
    try {
        const depSnap = await getDocs(collection(db, 'banks', bid, 'companyDeployment'));
        const deployments = depSnap.docs.map((d) => ({ id: d.id, ...d.data() })) as Array<{
            id: string;
            branchId?: string;
            companiesId?: string;
        }>;
        const onBranch = deployments.filter((d) => String(d.branchId || '').trim() === brid);
        const companies = await fetchCompaniesData(uid, isBankUser);
        const emails: string[] = [];
        const matchedRows: Array<{
            deploymentId: string;
            companiesId: string;
            companyName?: string;
            companyType?: string;
            email?: string | null;
        }> = [];
        for (const d of onBranch) {
            const cid = String(d.companiesId || '').trim();
            if (!cid) continue;
            const company = companies.find((c) => c.id === cid);
            if (!company) continue;
            if (!companyTypeMatchesVendorKey(company.companyType, key)) continue;
            const em = pickVendorContactEmail(company as unknown as Record<string, unknown>);
            matchedRows.push({
                deploymentId: String(d.id),
                companiesId: cid,
                companyName: company.companyName,
                companyType: company.companyType,
                email: em || null,
            });
            if (em) emails.push(em);
        }
        console.log('[RequestForm][Email modal][Vendor] companyDeployment for branch', {
            branchId: brid,
            deploymentsOnBranch: onBranch.length,
            matchedCompaniesForRequestType: matchedRows,
        });
        return Array.from(new Set(emails.filter(Boolean)));
    } catch (e) {
        console.error('[RequestForm][Email modal][Vendor] companyDeployment lookup failed', e);
        return [];
    }
}

/** Same branch collection as Deployments: `banks/{bankId}/branch` with no PSO filter (full bank list for vendor ids). */
async function mergeBranchesWithDeploymentStyleBankList(bankId: string, branches: unknown[] | undefined): Promise<unknown[]> {
    const bid = String(bankId || '').trim();
    const base = Array.isArray(branches) ? [...branches] : [];
    if (!bid) return base;
    try {
        const all = await fetchBankBranchesDeploymentStyle(bid);
        const byId = new Map<string, unknown>();
        for (const b of base) {
            const id = String((b as { id?: string })?.id || '').trim();
            if (id) byId.set(id, b);
        }
        for (const b of all) {
            const id = String(b.id || '').trim();
            if (id && !byId.has(id)) byId.set(id, b);
        }
        return Array.from(byId.values());
    } catch (e) {
        console.error('[RequestForm][Email modal][Vendor] mergeBranchesWithDeploymentStyleBankList failed', e);
        return base;
    }
}

/** Vendor email: branch `*VendorId` → global `companies`, plus `companyDeployment` on branch (Deployements screen). */
async function collectVendorEmailsForRequest(
    request: Request,
    bankId: string,
    branches: unknown[] | undefined,
    uid: string,
    isBankUser: boolean
): Promise<string[]> {
    const bid = String(bankId || '').trim();
    const logTag = '[RequestForm][Email modal][Vendor]';

    const mergedBranches = await mergeBranchesWithDeploymentStyleBankList(bid, branches);

    console.groupCollapsed(`${logTag} ${request.requestId || request.id || 'request'}`);
    console.log('request snapshot', {
        requestId: request.requestId,
        id: request.id,
        branchId: request.branchId,
        branchCode: request.branchCode,
        requestType: request.requestType,
        bankIdUsed: bid,
        branchesFromPage: Array.isArray(branches) ? branches.length : 0,
        branchesMergedWithBankWideList: mergedBranches.length,
    });

    const branch = await resolveBranchForVendorEmail(request, bid, mergedBranches);
    if (!branch) {
        console.warn('no branch resolved — cannot read vendor ids or companyDeployment for branch');
        console.groupEnd();
        return [];
    }

    console.log('resolved branch (vendor id fields)', {
        id: branch.id,
        branchCode: branch.branchCode,
        cctvVendorId: branch.cctvVendorId ?? null,
        bamsVendorId: branch.bamsVendorId ?? null,
        guardVendorId: branch.guardVendorId ?? null,
    });

    const key = vendorKeyFromRequestType(request);
    const ids: string[] = [];
    const pushId = (id: unknown) => {
        const s = typeof id === 'string' ? id.trim() : '';
        if (s) ids.push(s);
    };
    if (key === 'cctv') pushId(branch.cctvVendorId);
    else if (key === 'guard') pushId(branch.guardVendorId);
    else if (key === 'bams') pushId(branch.bamsVendorId);
    else {
        pushId(branch.cctvVendorId);
        pushId(branch.bamsVendorId);
        pushId(branch.guardVendorId);
    }

    const uniqueIds = Array.from(new Set(ids));
    console.log('requestType vendor key', key, 'branchDoc vendorIds', uniqueIds);

    const out: string[] = [];

    if (uniqueIds.length > 0) {
        try {
            const vendors = await fetchVendors(['CCTV', 'BAMS', 'Guard Company']);
            for (const vid of uniqueIds) {
                const row = vendors.find((x) => x.id === vid || String(x.companyId || '').trim() === vid);
                let em = pickVendorContactEmail(row as unknown as Record<string, unknown>);
                let via = em ? 'fetchVendors list' : '';
                if (!em) {
                    const one = await getVendorById(vid);
                    em = pickVendorContactEmail(one as unknown as Record<string, unknown>);
                    if (em) via = 'getVendorById(companies/{id})';
                }
                console.log('branch *VendorId lookup', {
                    vendorId: vid,
                    matchedListCompanyName: row?.companyName ?? null,
                    resolvedEmail: em || null,
                    via: em ? via : 'none',
                });
                if (em) out.push(em);
            }
        } catch (e) {
            console.error(`${logTag} fetchVendors / getVendorById failed`, e);
        }
    } else {
        console.warn(
            'no cctvVendorId / bamsVendorId / guardVendorId on branch — using companyDeployment + banks/.../companies (same as Deployments screen)'
        );
    }

    const depEmails = await collectDeployedCompanyEmailsForBranch(bid, String(branch.id), key, uid, isBankUser);
    for (const em of depEmails) {
        if (em && !out.some((x) => x.toLowerCase() === em.toLowerCase())) {
            out.push(em);
        }
    }

    console.log('combined vendor emails (branch vendor ids + companyDeployment)', out);
    console.groupEnd();
    return out;
}

async function getDefaultRecipientsForRequest(
    request: Request,
    companyId: string,
    branches: unknown[] | undefined,
    uid: string,
    isBankUser: boolean
): Promise<string[]> {
    const emails: string[] = [];
    const contact = (request.requesterContact || '').trim();
    if (contact) {
        emails.push(...validateEmails(contact).valid);
    }
    if (request.createdBy) {
        try {
            const psoMail = await fetchPSOEmail(db, companyId, request.createdBy);
            if (psoMail && psoMail.trim()) {
                emails.push(psoMail.trim());
            }
        } catch (e) {
            console.error('Error fetching creator email:', e);
        }
    }

    const bankId = String((request as { bankId?: string }).bankId || companyId).trim();
    const vendorEmails = await collectVendorEmailsForRequest(request, bankId, branches, uid, isBankUser);
    for (const e of vendorEmails) {
        emails.push(e);
    }

    return Array.from(new Set(emails.filter(Boolean)));
}

export default function RequestForm() {
    const pso = useAppSelector((s) => s.user.pso);
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [psoId, setPsoId] = useState<string | null>(null);
    const [psoDesignation, setPsoDesignation] = useState<string | null>(null);
    /** Firestore pos document id (banks/.../pos/{id}); may differ from auth uid for legacy staff docs. */
    const [posFirestoreDocId, setPosFirestoreDocId] = useState<string | null>(null);
    const [managedPsoIds, setManagedPsoIds] = useState<string[]>([]);
    const [psoNameByCreatorId, setPsoNameByCreatorId] = useState<Record<string, string>>({});
    const [psoEmailById, setPsoEmailById] = useState<Record<string, string>>({});

    // Check if user can create requests
    // Regular PSO can create requests, but for incharge users, only Incharge MIS can create
    const designation = pso?.designation || psoDesignation || '';
    /** Redux-first designation (Firestore uses underscores, e.g. Regional_Head). */
    const roleDesignation = designation.trim();
    const isIncharge = /incharge/i.test(designation);
    const inchargeType = designation.replace(/_/g, ' ').toLowerCase();
    const isHead = inchargeType === 'head' || inchargeType === 'head pso';
    const isInchargeMIS = inchargeType.includes('mis');
    const isInchargeCCTV = inchargeType.includes('cctv');
    const isInchargeGuard = inchargeType.includes('guarding') || inchargeType.includes('guard');
    const isInchargeBAMS = inchargeType.includes('bams');
    const [isBankUser, setIsBankUser] = useState(false);
    /** Only base PSO (not Regional / Head / Incharge) may open Add Request. */
    const showAddRequestButton = isBasePsoDesignation(roleDesignation) && !isBankUser;

    // Determine automatic request type filter for incharge users
    // PSO and Incharge MIS see all requests, other incharge types see only their type
    const getAutoRequestTypeFilter = (): 'All' | 'CCTV' | 'Guard' | 'BAMS' => {
        if (!designation || !isIncharge || isInchargeMIS) {
            return 'All'; // PSO and Incharge MIS see all, or if designation not loaded yet
        }
        if (isInchargeCCTV) {
            return 'CCTV'; // Incharge CCTV sees only CCTV requests
        }
        if (isInchargeGuard) {
            return 'Guard'; // Incharge Guard sees only Guard requests
        }
        if (isInchargeBAMS) {
            return 'BAMS'; // Incharge BAMS sees only BAMS requests
        }
        return 'All'; // Default
    };
    const [requests, setRequests] = useState<Request[]>([]);
    const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const [showRequestFilterModal, setShowRequestFilterModal] = useState(false);
    const [dateFilter, setDateFilter] = useState<'all' | 'daily' | 'weekly' | 'monthly'>('all');
    const [dateFromFilter, setDateFromFilter] = useState<string>('');
    const [dateToFilter, setDateToFilter] = useState<string>('');
    const [draftDateFilter, setDraftDateFilter] = useState<'all' | 'daily' | 'weekly' | 'monthly'>('all');
    const [draftCreatedFrom, setDraftCreatedFrom] = useState<string | null>(null);
    const [draftCreatedTo, setDraftCreatedTo] = useState<string | null>(null);
    // Default filter status: 'pending' for Regional PSO / Regional Head, 'All' for others
    const [filterStatus, setFilterStatus] = useState('All');
    const [draftFilterStatus, setDraftFilterStatus] = useState('All');
    const [requestTypeFilter, setRequestTypeFilter] = useState<'All' | 'CCTV' | 'Guard' | 'BAMS'>('All');
    const [draftRequestTypeFilter, setDraftRequestTypeFilter] = useState<'All' | 'CCTV' | 'Guard' | 'BAMS'>('All');
    const [psoFilterRequest, setPsoFilterRequest] = useState('all');
    const [draftPsoFilterRequest, setDraftPsoFilterRequest] = useState('all');
    const [requiredByFilter, setRequiredByFilter] = useState('All');
    const [draftRequiredByFilter, setDraftRequiredByFilter] = useState('All');
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const exportMenuRef = useRef<HTMLDivElement | null>(null);
    const exportCancelledRef = useRef(false);
    const [exportProgress, setExportProgress] = useState<{
        active: boolean;
        format: 'excel' | 'pdf' | null;
        message: string;
        percent: number;
    }>({ active: false, format: null, message: '', percent: 0 });
    const [requestRowContextMenu, setRequestRowContextMenu] = useState<null | { request: Request; clientX: number; clientY: number }>(
        null
    );
    const [statuses, setStatuses] = useState<string[]>([]);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('success');
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Add Request Form states
    const [showAddModal, setShowAddModal] = useState(false);
    const [psoName, setPsoName] = useState('');
    const [psoEmail, setPsoEmail] = useState('');
    const [branches, setBranches] = useState<any[]>([]);
    const [selectedBranch, setSelectedBranch] = useState<any | null>(null);
    const [branchSearch, setBranchSearch] = useState('');
    const [showBranchModal, setShowBranchModal] = useState(false);
    const [showCompanyModal, setShowCompanyModal] = useState(false);
    const [availableCompanies, setAvailableCompanies] = useState<any[]>([]);
    const [fetchingCompany, setFetchingCompany] = useState(false);
    const [companySearch, setCompanySearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [branchDetailsCache, setBranchDetailsCache] = useState<Record<string, any>>({});
    const [locationDetailsCache, setLocationDetailsCache] = useState<Record<string, any>>({});
    const [displayBranchName, setDisplayBranchName] = useState<string>('N/A');
    const [displayRegionName, setDisplayRegionName] = useState<string>('N/A');

    // Guard Rate State
    const [guardRate, setGuardRate] = useState<number>(0);
    const [showRateModal, setShowRateModal] = useState(false);
    const [newRate, setNewRate] = useState<string>('');
    const [settingRate, setSettingRate] = useState(false);

    // Guard request constraints
    const [showGuardRangeReasonModal, setShowGuardRangeReasonModal] = useState(false);
    const [guardRangeReasonDraft, setGuardRangeReasonDraft] = useState('');
    const [guardDateRangeReason, setGuardDateRangeReason] = useState('');

    const [showGuardRepeatReasonModal, setShowGuardRepeatReasonModal] = useState(false);
    const [guardRepeatReasonDraft, setGuardRepeatReasonDraft] = useState('');
    const [guardRepeatReason, setGuardRepeatReason] = useState('');
    const [activeStartDate, setActiveStartDate] = useState<Dayjs | null>(null);

    // Editing states for Bank users
    const [isEditing, setIsEditing] = useState(false);
    const [editingRequestId, setEditingRequestId] = useState<string | null>(null);

    // Resubmission states
    const [showResubmitModal, setShowResubmitModal] = useState(false);
    const [resubmitReasonDraft, setResubmitReasonDraft] = useState('');
    const [resubmitRequestObj, setResubmitRequestObj] = useState<Request | null>(null);

    // Regional Rejection states (for Regional PSO / Regional Head)
    const [showRegionalRejectModal, setShowRegionalRejectModal] = useState(false);
    const [regionalRejectReasonDraft, setRegionalRejectReasonDraft] = useState('');
    const [regionalRejectRequestObj, setRegionalRejectRequestObj] = useState<Request | null>(null);
    const [regionalRejectSending, setRegionalRejectSending] = useState(false);

    // Guard Count Confirmation states
    const [showGuardConfirmModal, setShowGuardConfirmModal] = useState(false);

    const [showApproveEmailStep, setShowApproveEmailStep] = useState(false);
    const [pendingApproveRequest, setPendingApproveRequest] = useState<Request | null>(null);
    const [approveRecipients, setApproveRecipients] = useState<string[]>([]);
    const [approveEmailDraft, setApproveEmailDraft] = useState('');
    const [approveLoadingRecipients, setApproveLoadingRecipients] = useState(false);
    const [approveSending, setApproveSending] = useState(false);

    const [showRejectEmailStep, setShowRejectEmailStep] = useState(false);
    const [pendingRejectRequest, setPendingRejectRequest] = useState<Request | null>(null);
    const [rejectRecipients, setRejectRecipients] = useState<string[]>([]);
    const [rejectEmailDraft, setRejectEmailDraft] = useState('');
    const [rejectLoadingRecipients, setRejectLoadingRecipients] = useState(false);
    const [rejectSending, setRejectSending] = useState(false);

    const [showStatusEmailModal, setShowStatusEmailModal] = useState(false);
    const [statusEmailRequest, setStatusEmailRequest] = useState<Request | null>(null);
    const [statusEmailRecipients, setStatusEmailRecipients] = useState<string[]>([]);
    const [statusEmailDraft, setStatusEmailDraft] = useState('');
    const [statusEmailLoadingDefaults, setStatusEmailLoadingDefaults] = useState(false);
    const [statusEmailSending, setStatusEmailSending] = useState(false);

    // Form fields
    const [formData, setFormData] = useState({
        requestType: '',
        branchCode: '',
        branchName: '',
        company: '',
        selectedCompanyId: '',
        requesterName: '',
        requesterContact: '',
        requiredBy: '',
        cctvDateFrom: '',
        cctvDateTo: '',
        cctvDateRanges: [] as { dateFrom: string; dateTo: string; timeFrom: string; timeTo: string }[],
        timeFrom: '',
        timeTo: '',
        numberOfGuards: '1',
        guardCompany: '',
        hardwareName: '',
        purpose: '',
        purposeOther: '',
        requestId: '',
    });

    const [tempDateRange, setTempDateRange] = useState<any>(null);
    const [tempTimeFrom, setTempTimeFrom] = useState('');
    const [tempTimeTo, setTempTimeTo] = useState('');

    const requiredByOptions = ['HR', 'Admin', 'Police', 'FIA', 'FRMU', 'DRU', 'CIU', 'CIO', 'Region/Branch', 'Audit', 'Others'];
    // Show all request types for Incharge MIS, otherwise show only CCTV and Guard
    const requestTypeOptions = isInchargeMIS ? ['CCTV', 'Guard', 'BAMS'] : ['CCTV', 'Guard'];
    const purposeOptionsCCTV = ['ATM transection Issue', 'HR Enquiry', 'Audit observation', 'Police Investigation', 'Main Glass Door Broken', 'ATM Glass Door Broken', 'ATM Attempt', 'Police Pressure', 'Other'];
    const purposeOptionsGuard = ['BAMS Failure', 'Electrical Issue', 'Renovation Work', 'BISP Camp', 'Shutter Out of order', 'Main Glass Door Broken', 'ATM Glass Door Broken', 'ATM Attempt', 'Police Pressure', 'Religious Event / Public Holiday / Procession', 'Other'];

    const uid = auth.currentUser?.uid;
    const posDocPathId = posFirestoreDocId ?? uid ?? '';

    // Step 1: Fetch companyId
    useEffect(() => {
        if (!uid) return;

        const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso' or 'incharge'
        const isBank = userRole === 'bank';
        setIsBankUser(isBank);

        const loadCompanyInfo = async () => {
            try {
                if (isBank) {
                    // For bank users, check if uid is a bank document ID or if there's a bank with userId == uid
                    const directBank = await getDoc(fsDoc(db, "banks", uid));
                    if (directBank.exists()) {
                        setCompanyId(uid);
                        return;
                    }
                    // Try query by userId field
                    const banksSnap = await getDocs(collection(db, "banks"));
                    for (const bankDoc of banksSnap.docs) {
                        const bankData = bankDoc.data();
                        if (bankData.userId === uid) {
                            setCompanyId(bankDoc.id);
                            return;
                        }
                    }
                } else {
                    // PSO or Incharge users
                    const companyInfo = await fetchCompanyIdAndPsoInfo(uid);
                    if (companyInfo) {
                        setCompanyId(companyInfo.companyId);
                        setPsoId(companyInfo.psoId);
                        setPosFirestoreDocId(companyInfo.posDocId);
                        setPsoDesignation(companyInfo.psoDesignation);
                        setPsoName(companyInfo.psoName);
                        setPsoEmail(companyInfo.psoEmail);
                    }
                }
            } catch (err) {
                console.error('Error fetching company info:', err);
            }
        };

        loadCompanyInfo();
    }, [uid]);

    // Fetch branches for add request modal
    // For Regional PSO / Regional Head and Head PSO, fetch all branches since they can see requests from other PSOs
    useEffect(() => {
        if (!companyId || !uid || !psoId || !roleDesignation) return;

        const loadBranches = async () => {
            try {
                // For Regional PSO / Regional Head and Head PSO, we need all branches to display branch names for requests from other PSOs
                // For regular PSO, we only need their own branches
                if (isRegionalLeadDesignation(roleDesignation) || isHeadPsoDesignation(roleDesignation)) {
                    // Fetch all branches for the company
                    const branchSnap = await getDocs(collection(db, 'banks', companyId, 'branch'));
                    const allBranches = branchSnap.docs.map(doc => ({
                        id: doc.id,
                        companyId,
                        ...doc.data(),
                    }));
                    setBranches(allBranches);
                } else {
                    // Regular PSO - only fetch their own branches
                    const branchesList = await fetchBranches(companyId, psoId);
                    setBranches(branchesList);
                }
            } catch (error) {
                console.error('Error fetching branches:', error);
            }
        };

        loadBranches();
    }, [companyId, uid, psoId, roleDesignation]);

    // Step 2: Fetch managed PSOs (if Regional PSO / Regional Head or Head PSO)
    useEffect(() => {
        if (!companyId || !psoId || !roleDesignation || !uid) return;

        const loadManagedPsos = async () => {
            try {
                const managedIds = await fetchManagedPsos(companyId, psoId, roleDesignation, posDocPathId);
                setManagedPsoIds(managedIds);
            } catch (error) {
                console.error('Error fetching managed PSOs:', error);
                setManagedPsoIds([]);
            }
        };

        loadManagedPsos();
    }, [companyId, psoId, roleDesignation, posDocPathId]);

    // Step 2.5: Fetch Guard Rate
    useEffect(() => {
        if (!uid) return;
        const loadRate = async () => {
            const rate = await fetchGuardRate();
            setGuardRate(rate);
            setNewRate(rate.toString());
        };
        loadRate();
    }, [uid, showRateModal]);

    // Set default filter status to 'pending' for Regional PSO / Regional Head
    useEffect(() => {
        if (isRegionalLeadDesignation(roleDesignation)) {
            setFilterStatus('pending');
            setDraftFilterStatus('pending');
        }
    }, [roleDesignation]);

    // Step 3: Fetch requests
    useEffect(() => {
        if (!companyId) return;

        const loadRequests = async () => {
            setLoading(true);
            try {
                const requestsList = await fetchRequests(companyId, psoId || '', roleDesignation || '', managedPsoIds);
                setRequests(requestsList);

                // Extract unique statuses
                const statusList = Array.from(new Set(requestsList.map(request => request.status).filter((status): status is string => Boolean(status))));
                setStatuses(statusList);
            } catch (err) {
                console.error(err);
                setToastMessage('Failed to fetch requests');
                setToastType('error');
                setShowToast(true);
            } finally {
                setLoading(false);
            }
        };

        loadRequests();
    }, [companyId, psoId, managedPsoIds, roleDesignation]);

    // Resolve creator PSO display names: banks/{companyId}/pos/{createdBy}
    useEffect(() => {
        if (!companyId || !requests.length) return;
        const ids = Array.from(new Set(requests.flatMap((r) => [r.createdBy, r.forwardedBy, r.updatedBy]).filter(Boolean))) as string[];
        let cancelled = false;
        void (async () => {
            const batchName: Record<string, string> = {};
            const batchEmail: Record<string, string> = {};
            for (const id of ids) {
                try {
                    let s = await getDoc(fsDoc(db, 'banks', companyId, 'pos', id));
                    let foundDoc = s.exists() ? s : null;
                    if (!foundDoc) {
                        const q = query(collection(db, 'banks', companyId, 'pos'), where('authUid', '==', id));
                        const snap = await getDocs(q);
                        if (!snap.empty) {
                            foundDoc = snap.docs[0];
                        }
                    }
                    if (foundDoc) {
                        const data = foundDoc.data();
                        batchName[id] = String((data as { name?: string }).name || id);
                        batchEmail[id] = String((data as { email?: string }).email || 'N/A');
                    } else {
                        batchName[id] = id;
                        batchEmail[id] = 'N/A';
                    }
                } catch {
                    batchName[id] = id;
                    batchEmail[id] = 'N/A';
                }
            }
            if (!cancelled) {
                setPsoNameByCreatorId((prev) => {
                    const next = { ...prev };
                    let changed = false;
                    for (const [k, v] of Object.entries(batchName)) {
                        if (prev[k] !== v) {
                            next[k] = v;
                            changed = true;
                        }
                    }
                    return changed ? next : prev;
                });
                setPsoEmailById((prev) => {
                    const next = { ...prev };
                    let changed = false;
                    for (const [k, v] of Object.entries(batchEmail)) {
                        if (prev[k] !== v) {
                            next[k] = v;
                            changed = true;
                        }
                    }
                    return changed ? next : prev;
                });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [companyId, requests]);

    // Dev: always log for any signed-in user (bank, PSO, incharge) — uses console.log for default DevTools levels.
    useEffect(() => {
        if (!uid) {
            console.log('[RequestForm] visibility debug — not signed in (no Firebase auth uid).');
            return;
        }

        let cancelled = false;
        const run = async () => {
            const u = auth.currentUser;
            const basePayload = {
                auth: {
                    uid: u?.uid,
                    email: u?.email ?? null,
                    displayName: u?.displayName ?? null,
                },
                sessionRole: sessionStorage.getItem('role'),
                isBankUser,
                reduxPso: pso
                    ? {
                        authUid: pso.authUid,
                        name: pso.name,
                        designation: pso.designation,
                        email: pso.email,
                        contact: pso.contact,
                    }
                    : null,
                firestoreFromFetchCompanyInfo: {
                    companyId: companyId ?? null,
                    psoId,
                    psoDesignationFromFirestore: psoDesignation,
                    psoName,
                    psoEmail,
                },
                resolvedRole: {
                    designationMerged: designation,
                    roleDesignationTrimmed: roleDesignation,
                    normalizedLabel: normalizePsoDesignationLabel(roleDesignation),
                    flags: {
                        isRegionalLead: isRegionalLeadDesignation(roleDesignation),
                        isHeadPso: isHeadPsoDesignation(roleDesignation),
                        isBasePso: isBasePsoDesignation(roleDesignation),
                        isIncharge,
                    },
                },
                managedPsoIds_afterServiceExpand: managedPsoIds,
                filteredRequestsCount: requests.length,
                posFirestoreDocId,
                posDocPathId_forManagedPsos: posDocPathId,
            };

            if (!companyId) {
                console.log('[RequestForm] visibility debug — companyId not loaded yet', basePayload);
                return;
            }

            try {
                console.log('[RequestForm] visibility debug — loading Firestore (managedPsos + requests peek)…', {
                    companyId,
                    isBankUser,
                });

                let rawManagedFromFirestore: { subDocId: string; psoId: string }[] = [];
                try {
                    const ms = await getDocs(collection(db, 'banks', companyId, 'pos', posDocPathId, 'managedPsos'));
                    rawManagedFromFirestore = ms.docs.map((d) => ({
                        subDocId: d.id,
                        psoId: String((d.data() as { psoId?: string }).psoId || ''),
                    }));
                } catch {
                    rawManagedFromFirestore = [];
                }

                let recentRequestsPeek: { requestId?: string; createdBy?: string; status?: string; requestType?: string }[] = [];
                try {
                    const peekSnap = await getDocs(
                        query(collection(db, 'banks', companyId, 'requests'), orderBy('createdAt', 'desc'), limit(20))
                    );
                    recentRequestsPeek = peekSnap.docs.map((d) => {
                        const x = d.data() as Record<string, unknown>;
                        return {
                            requestId: (x.requestId as string) || d.id,
                            createdBy: x.createdBy as string | undefined,
                            status: x.status as string | undefined,
                            requestType: x.requestType as string | undefined,
                        };
                    });
                } catch {
                    recentRequestsPeek = [];
                }

                if (cancelled) return;

                const managedSet = new Set(managedPsoIds);
                const peekMatchHints = recentRequestsPeek.map((row) => ({
                    ...row,
                    createdByInManagedList: row.createdBy ? managedSet.has(row.createdBy) : false,
                }));

                console.log('[RequestForm] Current user & request visibility debug (full)', {
                    ...basePayload,
                    managedPsos_rawSubcollection_posUid: rawManagedFromFirestore,
                    firestorePathManagedPsos: `banks/${companyId}/pos/${posDocPathId}/managedPsos`,
                    posFirestoreDocId,
                    authUidForLogin: uid,
                    recentRequestsPeek_createdBy_check: peekMatchHints,
                    hint:
                        !isBankUser &&
                            isRegionalLeadDesignation(roleDesignation) &&
                            managedPsoIds.length === 0
                            ? 'Regional role but managedPsoIds is empty — check managedPsos under your pos doc (path above) and designation (Regional_Head vs Regional Head).'
                            : !isBankUser &&
                                isRegionalLeadDesignation(roleDesignation) &&
                                recentRequestsPeek.length > 0 &&
                                !peekMatchHints.some((h) => h.createdByInManagedList)
                                ? 'Recent requests exist but no createdBy matches managed list — createdBy on requests may use a different id than managedPsos.psoId (compare peek vs managedPsoIds).'
                                : undefined,
                });
            } catch (e) {
                console.warn('[RequestForm] visibility debug failed', e);
                console.log('[RequestForm] visibility debug — partial (after error)', basePayload);
            }
        };

        void run();
        return () => {
            cancelled = true;
        };
    }, [
        uid,
        companyId,
        isBankUser,
        pso,
        psoId,
        psoDesignation,
        psoName,
        psoEmail,
        designation,
        roleDesignation,
        managedPsoIds,
        requests.length,
        isIncharge,
        posFirestoreDocId,
        posDocPathId,
    ]);

    // Get date fields based on request type
    const getDateFields = (request: Request) => {
        const requestType = request.requestType?.toLowerCase();

        if (requestType === 'guard') {
            return {
                dateFrom: request.guardDateFrom || request.cctvDateFrom || request.dateFrom,
                dateTo: request.guardDateTo || request.cctvDateTo || request.dateTo
            };
        } else if (requestType === 'bams') {
            return {
                dateFrom: request.bamsDateFrom || request.cctvDateFrom || request.dateFrom,
                dateTo: request.bamsDateTo || request.cctvDateTo || request.dateTo
            };
        } else {
            // CCTV or default
            const ranges = request.cctvDateRanges;
            if (ranges && ranges.length > 0) {
                const startDates = ranges.map((r: any) => r.dateFrom).filter(Boolean);
                const endDates = ranges.map((r: any) => r.dateTo).filter(Boolean);
                const minDate = startDates.length > 0 ? startDates.reduce((min: string, d: string) => d < min ? d : min, startDates[0]) : '';
                const maxDate = endDates.length > 0 ? endDates.reduce((max: string, d: string) => d > max ? d : max, endDates[0]) : '';
                return {
                    dateFrom: minDate || request.cctvDateFrom || request.dateFrom,
                    dateTo: maxDate || request.cctvDateTo || request.dateTo
                };
            }
            return {
                dateFrom: request.cctvDateFrom || request.dateFrom,
                dateTo: request.cctvDateTo || request.dateTo
            };
        }
    };

    const requestPsoFilterOptions = useMemo(() => {
        const labels = new Set<string>();
        requests.forEach((r) => {
            const id = r.createdBy || '';
            const label = (id && psoNameByCreatorId[id]) || r.requesterName || id || '';
            const t = String(label).trim();
            if (t) labels.add(t);
        });
        return Array.from(labels).sort((a, b) => a.localeCompare(b));
    }, [requests, psoNameByCreatorId]);

    const requestRequiredByFilterOptions = useMemo(() => {
        const s = new Set<string>();
        requests.forEach((r) => {
            const v = String(r.requiredBy || '').trim();
            if (v) s.add(v);
        });
        return Array.from(s).sort((a, b) => a.localeCompare(b));
    }, [requests]);

    const isDefaultRequestFilterStatus = (s: string) =>
        isRegionalLeadDesignation(roleDesignation) ? s === 'pending' : s === 'All';

    const hasClearableRequestFilters =
        dateFilter !== 'all' ||
        !!dateFromFilter ||
        !!dateToFilter ||
        !isDefaultRequestFilterStatus(filterStatus) ||
        ((!isIncharge || isInchargeMIS) && requestTypeFilter !== 'All') ||
        psoFilterRequest !== 'all' ||
        requiredByFilter !== 'All';

    const applyRequestSearch = () => {
        setAppliedSearchTerm(searchInput.trim());
        setCurrentPage(1);
    };

    const openRequestFilterModal = () => {
        setDraftDateFilter(dateFilter);
        setDraftCreatedFrom(dateFromFilter ? dateFromFilter : null);
        setDraftCreatedTo(dateToFilter ? dateToFilter : null);
        setDraftFilterStatus(filterStatus);
        setDraftRequestTypeFilter(requestTypeFilter);
        setDraftPsoFilterRequest(psoFilterRequest);
        setDraftRequiredByFilter(requiredByFilter);
        setShowRequestFilterModal(true);
    };

    const applyRequestFiltersModal = () => {
        const useRange = !!(draftCreatedFrom && draftCreatedTo);
        if (useRange) {
            setDateFilter('all');
            setDateFromFilter(draftCreatedFrom);
            setDateToFilter(draftCreatedTo);
        } else {
            setDateFromFilter('');
            setDateToFilter('');
            setDateFilter(draftDateFilter);
        }
        setFilterStatus(draftFilterStatus);
        if (!isIncharge || isInchargeMIS) {
            setRequestTypeFilter(draftRequestTypeFilter);
        }
        setPsoFilterRequest(draftPsoFilterRequest);
        setRequiredByFilter(draftRequiredByFilter);
        setCurrentPage(1);
        setShowRequestFilterModal(false);
    };

    const clearRequestFilters = () => {
        setDateFilter('all');
        setDateFromFilter('');
        setDateToFilter('');
        setDraftDateFilter('all');
        setDraftCreatedFrom(null);
        setDraftCreatedTo(null);
        if (isRegionalLeadDesignation(roleDesignation)) {
            setFilterStatus('pending');
            setDraftFilterStatus('pending');
        } else {
            setFilterStatus('All');
            setDraftFilterStatus('All');
        }
        setRequestTypeFilter('All');
        setDraftRequestTypeFilter('All');
        setPsoFilterRequest('all');
        setDraftPsoFilterRequest('all');
        setRequiredByFilter('All');
        setDraftRequiredByFilter('All');
        setCurrentPage(1);
    };

    // Filter requests
    const filteredRequests = requests.filter(request => {
        const requestId = request.requestId || '';
        const branchCode = request.branchCode || '';
        const requesterName = request.requesterName || '';
        const createdBy = request.createdBy || '';
        const psoLabel = (createdBy && psoNameByCreatorId[createdBy]) || requesterName || createdBy;
        const term = appliedSearchTerm.toLowerCase();

        const matchesSearch = !appliedSearchTerm ||
            requestId.toLowerCase().includes(term) ||
            branchCode.toLowerCase().includes(term) ||
            requesterName.toLowerCase().includes(term) ||
            createdBy.toLowerCase().includes(term) ||
            psoLabel.toLowerCase().includes(term);

        const matchesStatus = filterStatus === 'All' || request.status === filterStatus;

        const matchesPso = psoFilterRequest === 'all' || psoLabel === psoFilterRequest;
        const matchesRequiredBy =
            requiredByFilter === 'All' || String(request.requiredBy || '').trim() === requiredByFilter;

        // For incharge users (except MIS), automatically filter by their type
        // For PSO and Incharge MIS, use the selected filter
        const autoFilter = getAutoRequestTypeFilter();
        const effectiveFilter = (isIncharge && !isInchargeMIS && !!designation) ? autoFilter : requestTypeFilter;

        // Case-insensitive comparison for request type
        const requestType = (request.requestType || '').trim();
        const matchesRequestType = effectiveFilter === 'All' ||
            requestType.toLowerCase() === effectiveFilter.toLowerCase();

        // Apply date filter
        let matchesDate = true;

        // Check date range filters (dateFromFilter and dateToFilter) - filter by created date
        if (dateFromFilter || dateToFilter) {
            if (!request.createdAt) {
                matchesDate = false;
            } else {
                // Convert filter dates to Date objects
                const filterFrom = dateFromFilter ? new Date(dateFromFilter) : null;
                const filterTo = dateToFilter ? new Date(dateToFilter) : null;

                // Convert request created date to Date object
                let createdDate: Date;
                if (request.createdAt.toDate) {
                    createdDate = request.createdAt.toDate();
                } else if (request.createdAt.seconds) {
                    createdDate = new Date(request.createdAt.seconds * 1000);
                } else {
                    createdDate = new Date(request.createdAt);
                }

                // Set to start of day for comparison
                const createdDateOnly = new Date(createdDate.getFullYear(), createdDate.getMonth(), createdDate.getDate());

                if (filterFrom && filterTo) {
                    // Both filter dates provided - check if created date is within range
                    const filterFromDate = new Date(filterFrom.getFullYear(), filterFrom.getMonth(), filterFrom.getDate());
                    const filterToDate = new Date(filterTo.getFullYear(), filterTo.getMonth(), filterTo.getDate(), 23, 59, 59);
                    matchesDate = createdDateOnly >= filterFromDate && createdDateOnly <= filterToDate;
                } else if (filterFrom) {
                    // Only from date provided - check if created date is on or after filter date
                    const filterFromDate = new Date(filterFrom.getFullYear(), filterFrom.getMonth(), filterFrom.getDate());
                    matchesDate = createdDateOnly >= filterFromDate;
                } else if (filterTo) {
                    // Only to date provided - check if created date is on or before filter date
                    const filterToDate = new Date(filterTo.getFullYear(), filterTo.getMonth(), filterTo.getDate(), 23, 59, 59);
                    matchesDate = createdDateOnly <= filterToDate;
                }
            }
        } else if (dateFilter !== 'all') {
            // Use period filter (daily, weekly, monthly) if date range filters are not set
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

            if (!request.createdAt) {
                matchesDate = false;
            } else {
                let requestDate: Date;
                if (request.createdAt.toDate) {
                    requestDate = request.createdAt.toDate();
                } else if (request.createdAt.seconds) {
                    requestDate = new Date(request.createdAt.seconds * 1000);
                } else {
                    requestDate = new Date(request.createdAt);
                }

                const requestDateOnly = new Date(requestDate.getFullYear(), requestDate.getMonth(), requestDate.getDate());

                switch (dateFilter) {
                    case 'daily':
                        matchesDate = requestDateOnly.getTime() === today.getTime();
                        break;
                    case 'weekly':
                        const weekAgo = new Date(today);
                        weekAgo.setDate(today.getDate() - 7);
                        matchesDate = requestDateOnly.getTime() >= weekAgo.getTime();
                        break;
                    case 'monthly':
                        const monthAgo = new Date(today);
                        monthAgo.setMonth(today.getMonth() - 1);
                        matchesDate = requestDateOnly.getTime() >= monthAgo.getTime();
                        break;
                    default:
                        matchesDate = true;
                }
            }
        }

        return matchesSearch && matchesStatus && matchesRequestType && matchesDate && matchesPso && matchesRequiredBy;
    });

    // Pagination logic
    const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedRequests = filteredRequests.slice(startIndex, endIndex);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [
        appliedSearchTerm,
        filterStatus,
        requestTypeFilter,
        dateFilter,
        dateFromFilter,
        dateToFilter,
        itemsPerPage,
        psoFilterRequest,
        requiredByFilter,
        isIncharge,
        isInchargeMIS,
        designation,
    ]);

    useEffect(() => {
        if (!exportMenuOpen) return;
        const onDocMouseDown = (e: MouseEvent) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
                setExportMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [exportMenuOpen]);

    useEffect(() => {
        if (!exportProgress.active) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = 'An export is still running. Leaving now may interrupt the download.';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [exportProgress.active]);

    useEffect(() => {
        if (!requestRowContextMenu) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setRequestRowContextMenu(null);
        };
        const onDown = (e: MouseEvent) => {
            const menu = document.getElementById('requests-table-row-context');
            if (menu?.contains(e.target as Node)) return;
            setRequestRowContextMenu(null);
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onDown);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onDown);
        };
    }, [requestRowContextMenu]);

    // Fetch location details by locationId
    const fetchLocationDetails = async (locationId: string): Promise<any | null> => {
        // Check cache first
        if (locationDetailsCache[locationId]) {
            return locationDetailsCache[locationId];
        }

        try {
            const locationDoc = await getDoc(fsDoc(db, 'locations', locationId));
            if (locationDoc.exists()) {
                const locationData = { id: locationDoc.id, ...locationDoc.data() };
                // Cache the location data
                setLocationDetailsCache(prev => ({ ...prev, [locationId]: locationData }));
                return locationData;
            }
        } catch (error) {
            console.error('Error fetching location details:', error);
        }
        return null;
    };

    // Fetch branch details by branchId
    const fetchBranchDetails = async (branchId: string, bankId: string): Promise<any | null> => {
        // Check cache first
        if (branchDetailsCache[branchId]) {
            return branchDetailsCache[branchId];
        }

        try {
            const branchDoc = await getDoc(fsDoc(db, 'banks', bankId, 'branch', branchId));
            if (branchDoc.exists()) {
                const branchData: any = { id: branchDoc.id, ...branchDoc.data() };

                // If branch has locationId, fetch location details
                if (branchData.locationId) {
                    const locationData = await fetchLocationDetails(branchData.locationId);
                    if (locationData) {
                        // Store location name in branch data for easy access
                        branchData.locationName = locationData.name;
                    }
                }

                // Cache the branch data (with locationName if fetched)
                setBranchDetailsCache(prev => ({ ...prev, [branchId]: branchData }));

                return branchData;
            }
        } catch (error) {
            console.error('Error fetching branch details:', error);
        }
        return null;
    };

    // Get branch name from branches list or fetch if needed
    const getBranchName = async (request: Request): Promise<string> => {
        // First check if branchName is already in the request and is valid
        if (request.branchName && request.branchName.trim() !== '' && request.branchName !== 'N/A') {
            return request.branchName;
        }

        // Try to find branch from branches list by branchCode
        if (request.branchCode) {
            const branch = branches.find(b => b.branchCode === request.branchCode);
            if (branch) {
                return branch.branchName || branch.address || branch.name || 'N/A';
            }
        }

        // Try to find by branchId from branches list
        if (request.branchId) {
            const branch = branches.find(b => b.id === request.branchId);
            if (branch) {
                return branch.branchName || branch.address || branch.name || 'N/A';
            }
        }

        // If branchId exists and we have companyId, try to fetch branch details
        if (request.branchId && companyId) {
            const branchData = await fetchBranchDetails(request.branchId, companyId);
            if (branchData) {
                return branchData.address || branchData.branchName || branchData.name || 'N/A';
            }
        }

        return 'N/A';
    };

    // Synchronous version for immediate display (will be updated when async fetch completes)
    const getBranchNameSync = (request: Request): string => {
        // First check if branchName is already in the request and is valid
        if (request.branchName && request.branchName.trim() !== '' && request.branchName !== 'N/A') {
            return request.branchName;
        }

        // Try to find branch from branches list by branchCode
        if (request.branchCode) {
            const branch = branches.find(b => b.branchCode === request.branchCode);
            if (branch) {
                return branch.branchName || branch.address || branch.name || 'N/A';
            }
        }

        // Try to find by branchId from branches list
        if (request.branchId) {
            const branch = branches.find(b => b.id === request.branchId);
            if (branch) {
                return branch.branchName || branch.address || branch.name || 'N/A';
            }
        }

        // Check cache
        if (request.branchId && branchDetailsCache[request.branchId]) {
            const branchData = branchDetailsCache[request.branchId];
            return branchData.address || branchData.branchName || branchData.name || 'N/A';
        }

        return 'N/A';
    };

    const resetApproveEmailStep = () => {
        setShowApproveEmailStep(false);
        setPendingApproveRequest(null);
        setApproveRecipients([]);
        setApproveEmailDraft('');
        setApproveLoadingRecipients(false);
    };

    const resetRejectEmailStep = () => {
        setShowRejectEmailStep(false);
        setPendingRejectRequest(null);
        setRejectRecipients([]);
        setRejectEmailDraft('');
        setRejectLoadingRecipients(false);
    };

    const closeRequestDetailsModal = () => {
        resetApproveEmailStep();
        resetRejectEmailStep();
        setShowDetailsModal(false);
        setSelectedRequest(null);
    };

    const inEmailRecipientStep = showApproveEmailStep || showRejectEmailStep;

    // Open details modal
    const handleViewDetails = async (request: Request) => {
        resetApproveEmailStep();
        resetRejectEmailStep();
        setSelectedRequest(request);
        setShowDetailsModal(true);

        // Get initial branch name synchronously
        const initialBranchName = getBranchNameSync(request);
        setDisplayBranchName(initialBranchName);
        setDisplayRegionName('N/A');

        // Use bankId from request if available, otherwise use companyId
        const bankId = (request as any).bankId || companyId;

        // Try to get branch data and location
        let branchData: any = null;

        if (request.branchId) {
            try {
                // Check if branch is already in our list
                const existingBranch = branches.find(b => b.id === request.branchId);
                if (existingBranch) {
                    branchData = existingBranch;
                } else if (branchDetailsCache[request.branchId]) {
                    // Use cached branch data
                    branchData = branchDetailsCache[request.branchId];
                } else if (bankId) {
                    // Fetch branch details
                    branchData = await fetchBranchDetails(request.branchId, bankId);
                }

                // Update branch name if we have branch data
                if (branchData) {
                    const branchName = branchData.address || branchData.branchName || branchData.name || 'N/A';
                    setDisplayBranchName(branchName);

                    // Get location/region name
                    if (branchData.locationName) {
                        // Location name already in branch data
                        setDisplayRegionName(branchData.locationName);
                    } else if (branchData.locationId) {
                        // Fetch location details
                        const locationData = await fetchLocationDetails(branchData.locationId);
                        if (locationData && locationData.name) {
                            setDisplayRegionName(locationData.name);
                            // Update branch data cache with location name
                            if (branchDetailsCache[request.branchId]) {
                                branchDetailsCache[request.branchId].locationName = locationData.name;
                            }
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching branch/location data:', error);
            }
        }
    };

    // Update request status
    const handleUpdateStatus = async (request: Request, newStatus: string) => {
        if (!companyId || !uid || (!roleDesignation && !isBankUser)) return;

        setUpdating(true);
        try {
            await updateRequestStatus(companyId, request.id, newStatus, uid);

            setToastMessage(`Request status updated to ${newStatus}!`);
            setToastType('success');
            setShowToast(true);

            // Refresh requests list
            const requestsList = await fetchRequests(companyId, psoId || uid, roleDesignation || '', managedPsoIds);
            setRequests(requestsList);
            const statusList = Array.from(new Set(requestsList.map(request => request.status).filter((status): status is string => Boolean(status))));
            setStatuses(statusList);

            closeRequestDetailsModal();
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to update request status');
            setToastType('error');
            setShowToast(true);
        } finally {
            setUpdating(false);
        }
    };

    const addApproveEmailRecipient = () => {
        const trimmed = approveEmailDraft.trim();
        if (!trimmed) return;
        const v = validateEmails(trimmed);
        if (v.invalid.length > 0) {
            setToastMessage(`Invalid email(s): ${v.invalid.join(', ')}`);
            setToastType('error');
            setShowToast(true);
            return;
        }
        setApproveRecipients((prev) => {
            const next = [...prev];
            for (const email of v.valid) {
                if (!next.some((e) => e.toLowerCase() === email.toLowerCase())) {
                    next.push(email);
                }
            }
            return next;
        });
        setApproveEmailDraft('');
    };

    const removeApproveEmailRecipient = (email: string) => {
        setApproveRecipients((prev) => prev.filter((e) => e !== email));
    };

    const addRejectEmailRecipient = () => {
        const trimmed = rejectEmailDraft.trim();
        if (!trimmed) return;
        const v = validateEmails(trimmed);
        if (v.invalid.length > 0) {
            setToastMessage(`Invalid email(s): ${v.invalid.join(', ')}`);
            setToastType('error');
            setShowToast(true);
            return;
        }
        setRejectRecipients((prev) => {
            const next = [...prev];
            for (const email of v.valid) {
                if (!next.some((e) => e.toLowerCase() === email.toLowerCase())) {
                    next.push(email);
                }
            }
            return next;
        });
        setRejectEmailDraft('');
    };

    const removeRejectEmailRecipient = (email: string) => {
        setRejectRecipients((prev) => prev.filter((e) => e !== email));
    };

    const closeStatusEmailModal = () => {
        setShowStatusEmailModal(false);
        setStatusEmailRequest(null);
        setStatusEmailRecipients([]);
        setStatusEmailDraft('');
        setStatusEmailLoadingDefaults(false);
    };

    const openStatusEmailModal = async (request: Request) => {
        if (!companyId) {
            setToastMessage('Company ID not found');
            setToastType('error');
            setShowToast(true);
            return;
        }
        setStatusEmailRequest(request);
        setStatusEmailRecipients([]);
        setStatusEmailDraft('');
        setShowStatusEmailModal(true);
        setStatusEmailLoadingDefaults(true);
        try {
            const defaults = await getDefaultRecipientsForRequest(request, companyId, branches, uid ?? '', isBankUser);
            console.log('[RequestForm][Email modal] final recipient list (all users)', {
                count: defaults.length,
                emails: defaults,
            });
            setStatusEmailRecipients(defaults);
        } catch (e) {
            console.error(e);
            setToastMessage('Could not load suggested recipients');
            setToastType('warning');
            setShowToast(true);
        } finally {
            setStatusEmailLoadingDefaults(false);
        }
    };

    const addStatusEmailRecipient = () => {
        const trimmed = statusEmailDraft.trim();
        if (!trimmed) return;
        const v = validateEmails(trimmed);
        if (v.invalid.length > 0) {
            setToastMessage(`Invalid email(s): ${v.invalid.join(', ')}`);
            setToastType('error');
            setShowToast(true);
            return;
        }
        if (v.valid.length === 0) {
            setToastMessage('Enter at least one valid email address');
            setToastType('error');
            setShowToast(true);
            return;
        }
        setStatusEmailRecipients((prev) => {
            const next = [...prev];
            for (const email of v.valid) {
                if (!next.some((e) => e.toLowerCase() === email.toLowerCase())) {
                    next.push(email);
                }
            }
            return next;
        });
        setStatusEmailDraft('');
    };

    const removeStatusEmailRecipient = (email: string) => {
        setStatusEmailRecipients((prev) => prev.filter((e) => e !== email));
    };

    const sendStatusNotificationEmail = async () => {
        if (!statusEmailRequest || !companyId) return;
        const st = (statusEmailRequest.status || '').toLowerCase();
        if (st !== 'approved' && st !== 'rejected') return;
        if (statusEmailRecipients.length === 0) {
            setToastMessage('Add at least one recipient');
            setToastType('warning');
            setShowToast(true);
            return;
        }
        const validation = validateEmails(statusEmailRecipients.join(', '));
        if (validation.invalid.length > 0) {
            setToastMessage(`Invalid email(s): ${validation.invalid.join(', ')}`);
            setToastType('error');
            setShowToast(true);
            return;
        }

        setStatusEmailSending(true);
        try {
            const rid = statusEmailRequest.requestId || statusEmailRequest.id;
            const html =
                st === 'approved'
                    ? buildRequestApprovalEmailHtml(statusEmailRequest)
                    : buildRequestRejectionEmailHtml(statusEmailRequest);
            const subject =
                st === 'approved' ? `Request Approval - Request ID: ${rid}` : `Request Rejection - Request ID: ${rid}`;
            const results = await sendEmailsWithErrorHandling({
                to: validation.valid,
                subject,
                body: html,
                isHtml: true,
                companyName: 'PSO Request System Support',
            });
            if (results.failed.length > 0 && results.successful.length === 0) {
                setToastMessage(`Email failed: ${results.failed.map((f) => f.email).join(', ')}`);
                setToastType('error');
                setShowToast(true);
                return;
            }
            if (results.failed.length > 0) {
                setToastMessage(
                    `Sent to ${results.successful.length}. Failed: ${results.failed.map((f) => f.email).join(', ')}`
                );
                setToastType('warning');
            } else {
                setToastMessage(`Email sent to ${results.successful.length} recipient(s).`);
                setToastType('success');
            }
            setShowToast(true);
            closeStatusEmailModal();
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to send email');
            setToastType('error');
            setShowToast(true);
        } finally {
            setStatusEmailSending(false);
        }
    };

    const cancelApproveEmailStep = () => {
        resetApproveEmailStep();
    };

    const cancelRejectEmailStep = () => {
        resetRejectEmailStep();
    };

    const handleRejectEmailDone = async () => {
        if (!companyId || !uid || !pendingRejectRequest || !roleDesignation) return;

        const validation = validateEmails(rejectRecipients.join(', '));
        if (validation.invalid.length > 0) {
            setToastMessage(`Invalid email address(es): ${validation.invalid.join(', ')}`);
            setToastType('error');
            setShowToast(true);
            return;
        }

        setRejectSending(true);
        let partialEmailFailure = '';
        try {
            if (validation.valid.length > 0) {
                const rejectedAt = new Date();
                const html = buildRequestRejectionEmailHtml(pendingRejectRequest, rejectedAt);
                const rid = pendingRejectRequest.requestId || pendingRejectRequest.id;
                const results = await sendEmailsWithErrorHandling({
                    to: validation.valid,
                    subject: `Request Rejection - Request ID: ${rid}`,
                    body: html,
                    isHtml: true,
                    companyName: 'PSO Request System Support',
                });
                if (results.failed.length > 0 && results.successful.length === 0) {
                    setToastMessage(
                        `Could not send email. Request was not rejected. Failed: ${results.failed.map((f) => f.email).join(', ')}`
                    );
                    setToastType('error');
                    setShowToast(true);
                    return;
                }
                if (results.failed.length > 0) {
                    partialEmailFailure = ` Some recipients failed: ${results.failed.map((f) => f.email).join(', ')}.`;
                }
            }

            await updateRequestStatus(companyId, pendingRejectRequest.id, 'rejected', uid);

            const requestsList = await fetchRequests(companyId, psoId || uid, roleDesignation, managedPsoIds);
            setRequests(requestsList);
            const statusList = Array.from(
                new Set(requestsList.map((r) => r.status).filter((status): status is string => Boolean(status)))
            );
            setStatuses(statusList);

            closeRequestDetailsModal();

            if (validation.valid.length === 0) {
                setToastMessage('Request rejected. No email was sent (no recipients).');
                setToastType('success');
            } else if (partialEmailFailure) {
                setToastMessage(`Request rejected.${partialEmailFailure}`);
                setToastType('warning');
            } else {
                setToastMessage('Request rejected and notification email sent.');
                setToastType('success');
            }
            setShowToast(true);
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to reject request or send email');
            setToastType('error');
            setShowToast(true);
        } finally {
            setRejectSending(false);
        }
    };

    const handleApproveEmailDone = async () => {
        if (!companyId || !uid || !pendingApproveRequest || !roleDesignation) return;

        const validation = validateEmails(approveRecipients.join(', '));
        if (validation.invalid.length > 0) {
            setToastMessage(`Invalid email address(es): ${validation.invalid.join(', ')}`);
            setToastType('error');
            setShowToast(true);
            return;
        }

        setApproveSending(true);
        let partialEmailFailure = '';
        try {
            if (validation.valid.length > 0) {
                const approvalAt = new Date();
                const html = buildRequestApprovalEmailHtml(pendingApproveRequest, approvalAt);
                const rid = pendingApproveRequest.requestId || pendingApproveRequest.id;
                const results = await sendEmailsWithErrorHandling({
                    to: validation.valid,
                    subject: `Request Approval - Request ID: ${rid}`,
                    body: html,
                    isHtml: true,
                    companyName: 'PSO Request System Support',
                });
                if (results.failed.length > 0 && results.successful.length === 0) {
                    setToastMessage(
                        `Could not send email. Request was not approved. Failed: ${results.failed.map((f) => f.email).join(', ')}`
                    );
                    setToastType('error');
                    setShowToast(true);
                    return;
                }
                if (results.failed.length > 0) {
                    partialEmailFailure = ` Some recipients failed: ${results.failed.map((f) => f.email).join(', ')}.`;
                }
            }

            await updateRequestStatus(companyId, pendingApproveRequest.id, 'approved', uid);

            const requestsList = await fetchRequests(companyId, psoId || uid, roleDesignation, managedPsoIds);
            setRequests(requestsList);
            const statusList = Array.from(
                new Set(requestsList.map((r) => r.status).filter((status): status is string => Boolean(status)))
            );
            setStatuses(statusList);

            closeRequestDetailsModal();

            if (validation.valid.length === 0) {
                setToastMessage('Request approved. No email was sent (no recipients).');
                setToastType('success');
            } else if (partialEmailFailure) {
                setToastMessage(`Request approved.${partialEmailFailure}`);
                setToastType('warning');
            } else {
                setToastMessage('Request approved and notification email sent.');
                setToastType('success');
            }
            setShowToast(true);
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to approve request or send email');
            setToastType('error');
            setShowToast(true);
        } finally {
            setApproveSending(false);
        }
    };

    // Resubmit request handlers
    const handleOpenResubmitModal = (request: Request) => {
        setResubmitRequestObj(request);
        setResubmitReasonDraft('');
        setShowResubmitModal(true);
    };

    const handleCloseResubmitModal = () => {
        setShowResubmitModal(false);
        setResubmitReasonDraft('');
        setResubmitRequestObj(null);
    };

    const handleConfirmResubmit = async () => {
        if (!companyId || !uid || !resubmitRequestObj || !roleDesignation) return;

        const reason = resubmitReasonDraft.trim();

        setUpdating(true);
        try {
            await resubmitRequest(companyId, resubmitRequestObj.id, reason, uid);
            setToastMessage('Request resubmitted successfully!');
            setToastType('success');
            setShowToast(true);

            handleCloseResubmitModal();
            closeRequestDetailsModal();

            // Refresh requests list
            const requestsList = await fetchRequests(companyId, psoId || uid, roleDesignation, managedPsoIds);
            setRequests(requestsList);
            const statusList = Array.from(new Set(requestsList.map(r => r.status).filter((status): status is string => Boolean(status))));
            setStatuses(statusList);
        } catch (error) {
            console.error('Error resubmitting request:', error);
            setToastMessage('Failed to resubmit request');
            setToastType('error');
            setShowToast(true);
        } finally {
            setUpdating(false);
        }
    };

    // Forward request to Head
    const handleForwardToHead = async (request: Request) => {
        if (!companyId || !uid || !roleDesignation) return;

        setUpdating(true);
        try {
            await forwardRequestToHead(companyId, request.id, uid);

            setToastMessage('Request forwarded to Head!');
            setToastType('success');
            setShowToast(true);

            // Refresh requests list
            const requestsList = await fetchRequests(companyId, psoId || uid, roleDesignation, managedPsoIds);
            setRequests(requestsList);
            const statusList = Array.from(new Set(requestsList.map(request => request.status).filter((status): status is string => Boolean(status))));
            setStatuses(statusList);

            closeRequestDetailsModal();
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to forward request');
            setToastType('error');
            setShowToast(true);
        } finally {
            setUpdating(false);
        }
    };

    // Regional PSO / Regional Head: Reject with mandatory reason
    const handleOpenRegionalRejectModal = (request: Request) => {
        setRegionalRejectRequestObj(request);
        setRegionalRejectReasonDraft('');
        setShowRegionalRejectModal(true);
    };

    const handleCloseRegionalRejectModal = () => {
        setShowRegionalRejectModal(false);
        setRegionalRejectReasonDraft('');
        setRegionalRejectRequestObj(null);
    };

    const handleConfirmRegionalReject = async () => {
        if (!companyId || !uid || !regionalRejectRequestObj || !roleDesignation) return;

        const reason = regionalRejectReasonDraft.trim();
        if (!reason) {
            setToastMessage('Rejection reason is required');
            setToastType('error');
            setShowToast(true);
            return;
        }

        setRegionalRejectSending(true);
        try {
            // Resolve rejector's display name
            const rejectorName = psoName || psoNameByCreatorId[uid] || uid;

            await updateRequestStatus(companyId, regionalRejectRequestObj.id, 'rejected', uid, {
                rejectedReason: reason,
                rejectedByName: rejectorName,
            });

            setToastMessage('Request rejected successfully!');
            setToastType('success');
            setShowToast(true);

            handleCloseRegionalRejectModal();
            closeRequestDetailsModal();

            // Refresh requests list
            const requestsList = await fetchRequests(companyId, psoId || uid, roleDesignation, managedPsoIds);
            setRequests(requestsList);
            const statusList = Array.from(new Set(requestsList.map(r => r.status).filter((status): status is string => Boolean(status))));
            setStatuses(statusList);
        } catch (error) {
            console.error('Error rejecting request:', error);
            setToastMessage('Failed to reject request');
            setToastType('error');
            setShowToast(true);
        } finally {
            setRegionalRejectSending(false);
        }
    };

    // Get status badge
    const getStatusBadge = (status?: string) => {
        if (!status) return null;

        const statusLower = status.toLowerCase();
        let bgColor = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

        if (statusLower === 'pending') {
            bgColor = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
        } else if (statusLower === 'approved') {
            bgColor = 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
        } else if (statusLower === 'rejected') {
            bgColor = 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
        } else if (statusLower === 'in_progress') {
            bgColor = 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
        } else if (statusLower === 'completed') {
            bgColor = 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
        } else if (statusLower === 'forwarded_to_head') {
            bgColor = 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400';
        }

        return (
            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${bgColor}`}>
                {status === 'forwarded_to_head' ? 'Forwarded To Head' : status}
            </span>
        );
    };

    // Get request type badge
    const getRequestTypeBadge = (type?: string) => {
        if (!type) return null;

        let bgColor = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

        if (type === 'CCTV') {
            bgColor = 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
        } else if (type === 'Guard') {
            bgColor = 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
        } else if (type === 'BAMS') {
            bgColor = 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400';
        }

        return (
            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${bgColor}`}>
                {type}
            </span>
        );
    };

    // Format date
    const formatDate = (date: any) => {
        if (!date) return 'N/A';
        if (date.toDate) {
            return date.toDate().toLocaleDateString();
        }
        if (date.seconds) {
            return new Date(date.seconds * 1000).toLocaleDateString();
        }
        return new Date(date).toLocaleDateString();
    };

    // Format datetime
    const formatDateTime = (date: any) => {
        if (!date) return 'N/A';
        if (date.toDate) {
            return date.toDate().toLocaleString();
        }
        if (date.seconds) {
            return new Date(date.seconds * 1000).toLocaleString();
        }
        return new Date(date).toLocaleString();
    };

    // Generate Request ID
    const generateRequestId = () => {
        if (!psoName || psoName.length < 2) {
            const timestamp = Date.now().toString().slice(-4);
            return `REQ-XX-${timestamp}`;
        }
        const psoInitials = psoName.substring(0, 2).toUpperCase();
        const timestamp = Date.now().toString().slice(-4);
        return `REQ-${psoInitials}-${timestamp}`;
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            requestType: '',
            branchCode: '',
            branchName: '',
            company: '',
            selectedCompanyId: '',
            requesterName: '',
            requesterContact: '',
            requiredBy: '',
            cctvDateFrom: '',
            cctvDateTo: '',
            cctvDateRanges: [],
            timeFrom: '',
            timeTo: '',
            numberOfGuards: '1',
            guardCompany: '',
            hardwareName: '',
            purpose: '',
            purposeOther: '',
            requestId: '',
        });
        setTempDateRange(null);
        setTempTimeFrom('');
        setTempTimeTo('');
        setSelectedBranch(null);
        setBranchSearch('');
        setIsEditing(false);
        setEditingRequestId(null);
        setGuardDateRangeReason('');
        setGuardRepeatReason('');
    };

    const handleEditRequest = (request: Request) => {
        setIsEditing(true);
        setEditingRequestId(request.id);

        const branch = branches.find(b => b.id === request.branchId || b.branchCode === request.branchCode);
        if (branch) {
            setSelectedBranch(branch);
        } else {
            setSelectedBranch({
                id: request.branchId || '',
                branchCode: request.branchCode || '',
                branchName: request.branchName || '',
                cctvVendorId: (request as any).vendorId,
                bamsVendorId: (request as any).vendorId,
                guardVendorId: (request as any).vendorId,
            });
        }

        setFormData({
            requestType: request.requestType || '',
            branchCode: request.branchCode || '',
            branchName: request.branchName || '',
            company: request.company || '',
            selectedCompanyId: request.companyId || '',
            requesterName: request.requesterName || '',
            requesterContact: request.requesterContact || '',
            requiredBy: request.requiredBy || '',
            cctvDateFrom: request.cctvDateFrom || request.guardDateFrom || request.bamsDateFrom || '',
            cctvDateTo: request.cctvDateTo || request.guardDateTo || request.bamsDateTo || '',
            cctvDateRanges: request.cctvDateRanges || (request.cctvDateFrom ? [{ dateFrom: request.cctvDateFrom, dateTo: request.cctvDateTo, timeFrom: request.timeFrom || '', timeTo: request.timeTo || '' }] : []),
            timeFrom: request.timeFrom || '',
            timeTo: request.timeTo || '',
            numberOfGuards: request.numberOfGuards ? String(request.numberOfGuards) : '1',
            guardCompany: request.guardCompany || '',
            hardwareName: request.hardwareName || '',
            purpose: request.purpose || '',
            purposeOther: request.purposeOther || '',
            requestId: request.requestId || '',
        });

        if (request.guardDateRangeReason) setGuardDateRangeReason(request.guardDateRangeReason);
        if (request.guardRepeatReason) setGuardRepeatReason(request.guardRepeatReason);

        setShowAddModal(true);
    };

    const handleDeleteRequest = async (request: Request) => {
        if (!companyId) return;
        const confirmDelete = window.confirm(`Are you sure you want to delete request ${request.requestId || 'this request'}?`);
        if (!confirmDelete) return;

        try {
            await deleteRequest(companyId, request.id);
            setToastMessage('Request deleted successfully!');
            setToastType('success');
            setShowToast(true);

            // Refresh requests list
            if (roleDesignation) {
                const requestsList = await fetchRequests(companyId, psoId || uid || '', roleDesignation, managedPsoIds);
                setRequests(requestsList);
                const statusList = Array.from(new Set(requestsList.map(r => r.status).filter((status): status is string => Boolean(status))));
                setStatuses(statusList);
            }
        } catch (error) {
            console.error('Error deleting request:', error);
            setToastMessage('Failed to delete request');
            setToastType('error');
            setShowToast(true);
        }
    };

    const hasBranchPicked = Boolean(selectedBranch && formData.branchCode);

    const recentGuardRequestsForBranch = useMemo(() => {
        if (!selectedBranch?.id) return [] as Request[];
        const now = new Date();
        const windowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        windowStart.setDate(windowStart.getDate() - 30);

        return (requests || []).filter((r: any) => {
            const rt = String(r?.requestType || '').toLowerCase();
            if (rt !== 'guard') return false;
            if (String(r?.branchId || '') !== String(selectedBranch.id)) return false;
            const dt = getDateFields(r as any).dateTo;
            const df = getDateFields(r as any).dateFrom;
            const candidate = parseDateInput(dt) || parseDateInput(df);
            if (!candidate) return false;
            return candidate.getTime() >= windowStart.getTime();
        }) as Request[];
    }, [requests, selectedBranch]);

    const recentGuardRanges = useMemo(() => {
        const toUtc = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
        return recentGuardRequestsForBranch
            .map((r: any) => {
                const df = parseDateInput(getDateFields(r as any).dateFrom);
                const dt = parseDateInput(getDateFields(r as any).dateTo) || df;
                if (!df || !dt) return null;
                const start = Math.min(toUtc(df), toUtc(dt));
                const end = Math.max(toUtc(df), toUtc(dt));
                return { start, end, requestId: String(r.requestId || r.id || '') };
            })
            .filter(Boolean) as Array<{ start: number; end: number; requestId: string }>;
    }, [recentGuardRequestsForBranch]);

    const isCoveredByRecentGuard = (d: Dayjs | null): boolean => {
        if (!d) return false;
        const dt = d.toDate();
        const utc = Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate());
        return recentGuardRanges.some((r) => utc >= r.start && utc <= r.end);
    };

    // Filtered branches for search
    const filteredBranches = branches.filter(
        (b) =>
            b.branchCode?.toLowerCase().includes(branchSearch.toLowerCase()) ||
            b.address?.toLowerCase().includes(branchSearch.toLowerCase())
    );

    // Fetch available companies based on request type
    const loadAvailableCompanies = async () => {
        if (!companyId || !formData.requestType) return;

        try {
            const companiesList = await fetchAvailableCompanies(companyId, formData.requestType);
            setAvailableCompanies(companiesList);
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    };

    // Auto-fetch company from companyDeployment collection based on branch + request type
    const autoFetchCompanyForBranch = async (branch: any, requestType: string) => {
        if (!companyId || !branch?.id || !requestType) return;
        setFetchingCompany(true);
        try {
            // Step 1: Get all deployments on this branch
            const depSnap = await getDocs(collection(db, 'banks', companyId, 'companyDeployment'));
            const branchDeployments = depSnap.docs
                .map((d) => ({ id: d.id, ...d.data() }) as any)
                .filter((d) => String(d.branchId || '').trim() === String(branch.id).trim());

            if (branchDeployments.length > 0) {
                // Step 2: Fetch companies for this request type
                const companiesList = await fetchAvailableCompanies(companyId, requestType);

                // Step 3: Match deployment companiesId to companies list
                for (const dep of branchDeployments) {
                    const cid = String(dep.companiesId || '').trim();
                    if (!cid) continue;
                    const match = companiesList.find(c =>
                        String(c.id || '').trim() === cid ||
                        String(c.companyId || '').trim() === cid
                    );
                    if (match) {
                        setFormData(prev => ({
                            ...prev,
                            company: match.companyName || '-',
                            selectedCompanyId: match.companyId || match.id || ''
                        }));
                        setFetchingCompany(false);
                        return;
                    }
                }
            }

            // Step 4: Fallback — use branch vendor ID fields (legacy)
            const targetVendorId =
                requestType === 'CCTV' ? branch.cctvVendorId
                    : requestType === 'BAMS' ? branch.bamsVendorId
                        : requestType === 'Guard' ? branch.guardVendorId
                            : undefined;

            if (targetVendorId) {
                const companiesList = await fetchAvailableCompanies(companyId, requestType);
                const legacyMatch = companiesList.find(c =>
                    String(c.id || '').trim() === String(targetVendorId).trim() ||
                    String(c.companyId || '').trim() === String(targetVendorId).trim()
                );
                if (legacyMatch) {
                    setFormData(prev => ({
                        ...prev,
                        company: legacyMatch.companyName || '-',
                        selectedCompanyId: legacyMatch.companyId || legacyMatch.id || ''
                    }));
                    setFetchingCompany(false);
                    return;
                }
            }

            // Nothing found — clear company
            setFormData(prev => ({ ...prev, company: '', selectedCompanyId: '' }));
        } catch (err) {
            console.error('[RequestForm] autoFetchCompanyForBranch failed:', err);
            setFormData(prev => ({ ...prev, company: '', selectedCompanyId: '' }));
        } finally {
            setFetchingCompany(false);
        }
    };

    // Handle branch selection
    const handleBranchSelection = (branch: any) => {
        const branchDisplayName = [
            branch.branchName || branch.name || '',
            branch.address || '',
        ]
            .map((v: string) => (v || '').trim())
            .filter(Boolean)
            .join(' - ');

        setSelectedBranch(branch);
        setGuardRepeatReason('');
        setGuardDateRangeReason('');
        // Clear dates and company on new branch selection
        setFormData(prev => ({
            ...prev,
            branchCode: branch.branchCode,
            branchName: branchDisplayName,
            cctvDateFrom: '',
            cctvDateTo: '',
            company: '',
            selectedCompanyId: '',
        }));
        setShowBranchModal(false);

        // Auto-fetch company if request type already selected
        if (formData.requestType) {
            void autoFetchCompanyForBranch(branch, formData.requestType);
        }
    };

    // Handle company selection
    const handleCompanySelection = (company: any) => {
        setFormData({
            ...formData,
            company: company.companyName,
            selectedCompanyId: company.companyId,
        });
        setShowCompanyModal(false);
    };

    // Handle update guard rate
    const handleUpdateGuardRate = async () => {
        if (!uid || !newRate) return;

        setSettingRate(true);
        try {
            const rate = parseFloat(newRate);
            if (isNaN(rate) || rate < 0) {
                setToastMessage('Please enter a valid rate');
                setToastType('error');
                setShowToast(true);
                return;
            }

            await updateGuardRate(rate, uid);
            setGuardRate(rate);
            setShowRateModal(false);
            setToastMessage('Guard daily rate updated successfully');
            setToastType('success');
            setShowToast(true);
        } catch (error) {
            console.error('Error updating rate:', error);
            setToastMessage('Failed to update rate');
            setToastType('error');
            setShowToast(true);
        } finally {
            setSettingRate(false);
        }
    };

    function parseDateInput(v: string): Date | null {
        if (!v) return null;
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return null;
        return d;
    }

    const fromYmdToDayjs = (v: string): Dayjs | null => {
        if (!v) return null;
        const d = dayjs(v, 'YYYY-MM-DD', true);
        return d.isValid() ? d : null;
    };

    const toYmd = (d: Date): string => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const addDaysYmd = (ymd: string, days: number): string => {
        const d = parseDateInput(ymd);
        if (!d) return '';
        d.setDate(d.getDate() + days);
        return toYmd(d);
    };

    const inclusiveDaysBetween = (fromYmd: string, toYmdStr: string): number => {
        const from = parseDateInput(fromYmd);
        const to = parseDateInput(toYmdStr);
        if (!from || !to) return 0;
        const fromUtc = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
        const toUtc = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
        const diffDays = Math.floor((toUtc - fromUtc) / (1000 * 60 * 60 * 24));
        return diffDays + 1; // inclusive
    };

    const hasRecentGuardRequestForBranch = (): boolean => {
        if (!selectedBranch?.id) return false;
        const basis = parseDateInput(formData.cctvDateFrom) || new Date();
        const basisTime = basis.getTime();
        const windowMs = 30 * 24 * 60 * 60 * 1000;

        return requests.some((r: any) => {
            const rt = String(r?.requestType || '').toLowerCase();
            if (rt !== 'guard') return false;
            if (String(r?.branchId || '') !== String(selectedBranch.id)) return false;

            // Use the business date range (dateTo preferred) instead of createdAt
            const df = getDateFields(r as any).dateFrom;
            const dt = getDateFields(r as any).dateTo;
            const candidate = parseDateInput(dt) || parseDateInput(df);
            if (!candidate) return false;

            return Math.abs(basisTime - candidate.getTime()) < windowMs;
        });
    };

    // Handle save request
    const handleSaveRequest = async (bypassGuardConfirm = false) => {
        if (!uid || !selectedBranch) {
            setToastMessage('Please select a branch');
            setToastType('error');
            setShowToast(true);
            return;
        }

        if (!formData.company || formData.company.trim() === '' || formData.company === '-') {
            setToastMessage('Company is required. Please select a company or wait for auto-fetch to complete.');
            setToastType('error');
            setShowToast(true);
            return;
        }

        if (formData.requestType === 'CCTV') {
            if (!formData.cctvDateRanges || formData.cctvDateRanges.length === 0) {
                setToastMessage('Please add at least one date range');
                setToastType('error');
                setShowToast(true);
                return;
            }
        } else {
            if (!formData.cctvDateFrom || !formData.cctvDateTo) {
                setToastMessage('Please select a date range');
                setToastType('error');
                setShowToast(true);
                return;
            }
        }

        if (formData.requestType === 'Guard') {
            // Enforce minimum 1 guard count (always required)
            const guards = parseInt(formData.numberOfGuards, 10);
            if (isNaN(guards) || guards < 1) {
                setToastMessage('Number of Guards must be at least 1');
                setToastType('error');
                setShowToast(true);
                return;
            }

            // Bank users editing skip reason modals and guard count confirmation
            if (!(isBankUser && isEditing)) {
                const spanDays = inclusiveDaysBetween(formData.cctvDateFrom, formData.cctvDateTo);
                if (spanDays > 2 && !guardDateRangeReason.trim()) {
                    setGuardRangeReasonDraft('');
                    setShowGuardRangeReasonModal(true);
                    return;
                }

                if (hasRecentGuardRequestForBranch() && !guardRepeatReason.trim()) {
                    setGuardRepeatReasonDraft('');
                    setShowGuardRepeatReasonModal(true);
                    return;
                }

                // Show confirmation modal for guard count > 1
                if (guards > 1 && !bypassGuardConfirm) {
                    setShowGuardConfirmModal(true);
                    return;
                }
            }
        }

        // Required By validation - only for non-Guard requests
        if (formData.requestType !== 'Guard' && !formData.requiredBy) {
            setToastMessage('Please select Required By');
            setToastType('error');
            setShowToast(true);
            return;
        }

        // Time validation - only for non-Guard requests
        if (formData.requestType !== 'Guard' && (!formData.timeFrom || !formData.timeTo)) {
            setToastMessage('Please fill in time range');
            setToastType('error');
            setShowToast(true);
            return;
        }

        if (formData.requestType === 'Guard' && !formData.numberOfGuards) {
            setToastMessage('Please fill in number of guards');
            setToastType('error');
            setShowToast(true);
            return;
        }

        // Purpose Validation
        if (!formData.purpose) {
            setToastMessage('Please select a Purpose');
            setToastType('error');
            setShowToast(true);
            return;
        }
        if ((formData.purpose === 'Other' || formData.purpose === 'Police Pressure') && !formData.purposeOther.trim()) {
            setToastMessage(formData.purpose === 'Police Pressure' ? 'Please specify reason for Police Pressure' : 'Please specify the purpose');
            setToastType('error');
            setShowToast(true);
            return;
        }

        if (!companyId) {
            setToastMessage('Company ID not found');
            setToastType('error');
            setShowToast(true);
            return;
        }

        setSaving(true);
        try {
            if (isEditing && editingRequestId) {
                const requestData: any = {
                    branchCode: formData.branchCode,
                    branchName: formData.branchName,
                    company: formData.company,
                    companyId: formData.selectedCompanyId,
                    requesterName: formData.requesterName.trim() || '',
                    requesterContact: formData.requesterContact.trim() || '',
                    requiredBy: formData.requiredBy,
                    requestType: formData.requestType,
                    cctvDateFrom: formData.cctvDateFrom,
                    cctvDateTo: formData.cctvDateTo,
                    timeFrom: formData.timeFrom,
                    timeTo: formData.timeTo,
                    purpose: formData.purpose,
                    purposeOther: (formData.purpose === 'Other' || formData.purpose === 'Police Pressure') ? formData.purposeOther.trim() : '',
                    branchId: selectedBranch.id,
                    updatedAt: new Date(),
                    updatedBy: uid,
                    hardwareName: formData.hardwareName,
                };

                const rt = String(formData.requestType || '').toLowerCase();
                const vId =
                    rt === 'cctv' || rt.includes('cctv')
                        ? selectedBranch.cctvVendorId
                        : rt === 'bams' || rt.includes('bams')
                            ? selectedBranch.bamsVendorId
                            : rt === 'guard' || rt.includes('guard')
                                ? selectedBranch.guardVendorId
                                : undefined;
                if (vId != null && String(vId).trim() !== '') {
                    requestData.vendorId = String(vId).trim();
                }

                // Add type-specific fields
                if (formData.requestType === 'CCTV') {
                    requestData.cctvDateRanges = formData.cctvDateRanges;
                }
                if (formData.requestType === 'Guard') {
                    requestData.numberOfGuards = formData.numberOfGuards;
                    requestData.guardCompany = formData.guardCompany;
                    if (guardDateRangeReason.trim()) requestData.guardDateRangeReason = guardDateRangeReason.trim();
                    if (guardRepeatReason.trim()) requestData.guardRepeatReason = guardRepeatReason.trim();

                    const currentRate = guardRate || await fetchGuardRate();
                    const numberOfGuards = Number(formData.numberOfGuards) || 0;
                    const spanDays = inclusiveDaysBetween(formData.cctvDateFrom, formData.cctvDateTo);
                    requestData.guardDailyRate = currentRate;
                    requestData.totalAmount = numberOfGuards * currentRate * spanDays;
                }

                const { updateDoc, doc } = await import('firebase/firestore');
                await updateDoc(doc(db, 'banks', companyId, 'requests', editingRequestId), requestData);

                setToastMessage('Request updated successfully!');
                setToastType('success');
                setShowToast(true);

                resetForm();
                setShowAddModal(false);
            } else {
                const newRequestId = generateRequestId();

                const requestData: any = {
                    requestId: newRequestId,
                    branchCode: formData.branchCode,
                    branchName: formData.branchName,
                    company: formData.company,
                    companyId: formData.selectedCompanyId,
                    bankId: companyId,
                    requesterName: formData.requesterName.trim() || '',
                    requesterContact: formData.requesterContact.trim() || '',
                    requiredBy: formData.requiredBy,
                    requestType: formData.requestType,
                    cctvDateFrom: formData.cctvDateFrom,
                    cctvDateTo: formData.cctvDateTo,
                    timeFrom: formData.timeFrom,
                    timeTo: formData.timeTo,
                    purpose: formData.purpose,
                    purposeOther: (formData.purpose === 'Other' || formData.purpose === 'Police Pressure') ? formData.purposeOther.trim() : '',
                    branchId: selectedBranch.id,
                    createdBy: psoId || uid,
                    status: 'pending',
                    createdAt: new Date(),
                    hardwareName: formData.hardwareName,
                };

                const rt = String(formData.requestType || '').toLowerCase();
                const vId =
                    rt === 'cctv' || rt.includes('cctv')
                        ? selectedBranch.cctvVendorId
                        : rt === 'bams' || rt.includes('bams')
                            ? selectedBranch.bamsVendorId
                            : rt === 'guard' || rt.includes('guard')
                                ? selectedBranch.guardVendorId
                                : undefined;
                if (vId != null && String(vId).trim() !== '') {
                    requestData.vendorId = String(vId).trim();
                }

                // Add type-specific fields
                if (formData.requestType === 'CCTV') {
                    requestData.cctvDateRanges = formData.cctvDateRanges;
                }
                if (formData.requestType === 'Guard') {
                    requestData.numberOfGuards = formData.numberOfGuards;
                    requestData.guardCompany = formData.guardCompany;
                    if (guardDateRangeReason.trim()) requestData.guardDateRangeReason = guardDateRangeReason.trim();
                    if (guardRepeatReason.trim()) requestData.guardRepeatReason = guardRepeatReason.trim();
                }

                await createRequest(companyId, requestData);

                setToastMessage(`${formData.requestType} Request submitted successfully! Request ID: ${newRequestId}`);
                setToastType('success');
                setShowToast(true);

                resetForm();
                setShowAddModal(false);
            }

            // Refresh requests list
            if (companyId && roleDesignation) {
                const requestsList = await fetchRequests(companyId, psoId || uid || '', roleDesignation, managedPsoIds);
                setRequests(requestsList);
                const statusList = Array.from(new Set(requestsList.map(request => request.status).filter((status): status is string => Boolean(status))));
                setStatuses(statusList);
            }
        } catch (err) {
            console.error(err);
            setToastMessage(isEditing ? 'Failed to update request' : 'Failed to submit request');
            setToastType('error');
            setShowToast(true);
        } finally {
            setSaving(false);
        }
    };

    const getEffectiveRequestTypeFilter = (): 'All' | 'CCTV' | 'Guard' | 'BAMS' => {
        const auto = getAutoRequestTypeFilter();
        return isIncharge && !isInchargeMIS && !!designation ? auto : requestTypeFilter;
    };

    const getAppliedRequestDatePeriodLabel = (): string => {
        if (dateFromFilter || dateToFilter) {
            return `${dateFromFilter || '…'} ---> ${dateToFilter || '…'}`;
        }
        if (dateFilter === 'all') return 'All time (created)';
        return `${dateFilter.charAt(0).toUpperCase() + dateFilter.slice(1)} (created)`;
    };

    type RequestBranchExportRow = {
        branchCode: string;
        branchName: string;
        address: string;
        location: string;
        total: number;
        cctv: number;
        guard: number;
        bams: number;
        other: number;
    };

    const normalizeRequestTypeForExport = (r: Request): 'cctv' | 'guard' | 'bams' | 'other' => {
        const t = String(r.requestType || '').toLowerCase();
        if (t.includes('cctv')) return 'cctv';
        if (t.includes('bams')) return 'bams';
        if (t.includes('guard')) return 'guard';
        return 'other';
    };

    const getRequestExcelTabKey = (request: Request): 'CCTV' | 'Guard' | 'BAMS' | 'Other' => {
        const k = normalizeRequestTypeForExport(request);
        if (k === 'cctv') return 'CCTV';
        if (k === 'guard') return 'Guard';
        if (k === 'bams') return 'BAMS';
        return 'Other';
    };

    const formatRequestPurposeForExcel = (request: Request): string => {
        return getFormattedPurpose(request);
    };

    const branchNameForExcelExportRow = (request: Request, resolvedAddress: string): string => {
        const n = String(request.branchName || '').trim();
        if (n && n !== 'N/A') return n;
        return resolvedAddress || 'N/A';
    };

    const exportTimeFromForExcel = (request: Request): string => {
        const c = String((request as { cctvTimeFrom?: string }).cctvTimeFrom || '').trim();
        if (c) return c;
        const t = String(request.timeFrom || '').trim();
        return t || 'N/A';
    };

    const exportTimeToForExcel = (request: Request): string => {
        const c = String((request as { cctvTimeTo?: string }).cctvTimeTo || '').trim();
        if (c) return c;
        const t = String(request.timeTo || '').trim();
        return t || 'N/A';
    };

    const buildRequestExcelRowForExport = (
        request: Request,
        tab: 'CCTV' | 'Guard' | 'BAMS' | 'Other',
        resolvedAddress: string,
        resolvedLocation: string
    ): Record<string, unknown> => {
        const dates = getDateFields(request);
        const dateFrom = formatDate(dates.dateFrom);
        const dateTo = formatDate(dates.dateTo);
        let noOfDays = formatInclusiveNoOfDays(dates.dateFrom, dates.dateTo);
        if (request.requestType === 'CCTV' && request.cctvDateRanges && request.cctvDateRanges.length > 0) {
            let totalDays = 0;
            request.cctvDateRanges.forEach((range: any) => {
                totalDays += inclusiveDaysBetween(range.dateFrom, range.dateTo);
            });
            noOfDays = String(totalDays);
        }
        const branchName = branchNameForExcelExportRow(request, resolvedAddress);

        const forwardedByName = request.forwardedBy ? psoNameByCreatorId[request.forwardedBy] || request.forwardedBy : 'N/A';
        const forwardedByEmail = request.forwardedBy ? psoEmailById[request.forwardedBy] || 'N/A' : 'N/A';
        const forwardedByStr = request.forwardedBy ? `${forwardedByName} (${forwardedByEmail})` : 'N/A';

        const isApprovedOrRejected = request.status === 'approved' || request.status === 'rejected';
        const approveRejectByName = (isApprovedOrRejected && request.updatedBy) ? psoNameByCreatorId[request.updatedBy] || request.updatedBy : 'N/A';
        const approveRejectByEmail = (isApprovedOrRejected && request.updatedBy) ? psoEmailById[request.updatedBy] || 'N/A' : 'N/A';
        const approveRejectByStr = (isApprovedOrRejected && request.updatedBy) ? `${approveRejectByName} (${approveRejectByEmail})` : 'N/A';

        const baseIds: Record<string, unknown> = {
            'Request ID': request.requestId || 'N/A',
            'Created At': request.createdAt ? formatDate(request.createdAt) : 'N/A',
            'Forwarded Date': request.forwardedAt ? formatDate(request.forwardedAt) : 'N/A',
            'Forwarded By': forwardedByStr,
            'Approve/Reject Date': request.approvedAt ? formatDate(request.approvedAt) : (request.rejectedAt ? formatDate(request.rejectedAt) : 'N/A'),
            'Approve/Reject By': approveRejectByStr,
            Status: request.status || 'N/A',
            'Branch Code': request.branchCode || 'N/A',
            'Branch Name': branchName,
            Location: resolvedLocation || 'N/A',
            Company: request.company || 'N/A',
        };

        if (tab === 'Guard') {
            const n = request.numberOfGuards;
            return {
                ...baseIds,
                'Date From': dateFrom,
                'Date To': dateTo,
                'Number of Days': noOfDays,
                'Number of Guards': n != null && String(n).trim() !== '' ? String(n) : 'N/A',
                Purpose: formatRequestPurposeForExcel(request),
                'Date range reason': String((request as { guardDateRangeReason?: string }).guardDateRangeReason || '')
                    .trim() || '—',
                'Repeat request reason': String((request as { guardRepeatReason?: string }).guardRepeatReason || '')
                    .trim() || '—',
            };
        }

        if (tab === 'Other') {
            const ng = request.numberOfGuards;
            return {
                'Request Type': request.requestType || 'N/A',
                ...baseIds,
                'Required By': request.requiredBy || 'N/A',
                'Date From': dateFrom,
                'Date To': dateTo,
                'Number of Days': noOfDays,
                'Time From': exportTimeFromForExcel(request),
                'Time To': exportTimeToForExcel(request),
                'Number of Guards': ng != null && String(ng).trim() !== '' ? String(ng) : '—',
                Purpose: formatRequestPurposeForExcel(request),
            };
        }

        return {
            ...baseIds,
            'Required By': request.requiredBy || 'N/A',
            'Date From': dateFrom,
            'Date To': dateTo,
            'Number of Days': noOfDays,
            'Time From': exportTimeFromForExcel(request),
            'Time To': exportTimeToForExcel(request),
            Purpose: formatRequestPurposeForExcel(request),
        };
    };

    const resolveExportBranchFieldsForRequest = async (
        request: Request
    ): Promise<{ branchName: string; address: string; location: string }> => {
        const bankId = String((request as { bankId?: string }).bankId || companyId || '').trim();
        let bd: Record<string, unknown> | null = null;
        if (request.branchId && bankId) {
            bd = (await fetchBranchDetails(request.branchId, bankId)) as Record<string, unknown> | null;
        }
        if (!bd && bankId) {
            bd = await resolveBranchForVendorEmail(request, bankId, branches);
        }

        const addrFromDoc = bd ? String(bd.address || '').trim() : '';
        const nameFromDoc = bd ? String(bd.branchName || bd.name || '').trim() : '';
        let loc = bd ? String((bd as { locationName?: string }).locationName || '').trim() : '';
        if (!loc && bd && (bd as { locationId?: string }).locationId) {
            const locd = await fetchLocationDetails(String((bd as { locationId?: string }).locationId));
            loc = String(locd?.name || '').trim();
        }

        const reqName = String(request.branchName || '').trim();
        const reqAddr = String((request as { branchAddress?: string }).branchAddress || '').trim();

        const branchName =
            (reqName && reqName !== 'N/A' && reqName) || nameFromDoc || (addrFromDoc || '') || '—';
        const address = (addrFromDoc || reqAddr || (branchName !== '—' ? branchName : '')).trim() || '—';
        const location = (loc || '').trim() || '—';

        return { branchName, address, location };
    };

    const buildRequestBranchExportRowsAsync = async (
        rows: Request[],
        opts?: {
            onProgress?: (done: number, total: number) => void;
            isCancelled?: () => boolean;
        }
    ): Promise<RequestBranchExportRow[]> => {
        const byCode = new Map<string, Request[]>();
        for (const r of rows) {
            const code = String(r.branchCode || '—').trim() || '—';
            if (!byCode.has(code)) byCode.set(code, []);
            byCode.get(code)!.push(r);
        }
        const out: RequestBranchExportRow[] = [];
        const entries = Array.from(byCode.entries());
        const totalGroups = entries.length;
        let idx = 0;
        for (const [branchCode, list] of entries) {
            if (opts?.isCancelled?.()) {
                throw new ExportCancelledError();
            }
            let cctv = 0;
            let guard = 0;
            let bams = 0;
            let other = 0;
            for (const r of list) {
                const k = normalizeRequestTypeForExport(r);
                if (k === 'cctv') cctv++;
                else if (k === 'guard') guard++;
                else if (k === 'bams') bams++;
                else other++;
            }
            const sample = list.find((x: Request) => Boolean(x.branchId)) || list[0];
            const resolved = await resolveExportBranchFieldsForRequest(sample);
            let { branchName, address, location } = resolved;
            if (branchName === '—') {
                const alt = list
                    .map((x: Request) => String(x.branchName || '').trim())
                    .find((n: string) => n && n !== 'N/A');
                if (alt) branchName = alt;
            }
            out.push({
                branchCode,
                branchName,
                address,
                location,
                total: list.length,
                cctv,
                guard,
                bams,
                other,
            });
            idx += 1;
            opts?.onProgress?.(idx, totalGroups);
        }
        return out.sort((a, b) => b.total - a.total || a.branchCode.localeCompare(b.branchCode));
    };

    const buildRequestExportSummaryPairs = (totalMatching: number): (string | number)[][] => [
        ['Report type', 'Request forms'],
        ['Total rows', totalMatching],
        ['Date Range', getAppliedRequestDatePeriodLabel()],
        ['Generated', new Date().toLocaleString()],
    ];

    const isRequestExportWideOpen = (): boolean => {
        if (appliedSearchTerm.trim()) return false;
        if (psoFilterRequest !== 'all') return false;
        if (requiredByFilter !== 'All') return false;
        if (dateFromFilter || dateToFilter) return false;
        if (dateFilter !== 'all') return false;
        if (!isDefaultRequestFilterStatus(filterStatus)) return false;
        if (getEffectiveRequestTypeFilter() !== 'All') return false;
        return true;
    };

    const beginExportProgress = (format: 'excel' | 'pdf') => {
        exportCancelledRef.current = false;
        setExportProgress({
            active: true,
            format,
            message: format === 'excel' ? 'Preparing Excel workbook…' : 'Preparing PDF…',
            percent: 2,
        });
    };

    const updateExportProgress = (percent: number, message: string) => {
        if (exportCancelledRef.current) return;
        setExportProgress((p) => ({ ...p, percent: Math.min(100, Math.max(0, percent)), message }));
    };

    const endExportProgress = () => {
        setExportProgress({ active: false, format: null, message: '', percent: 0 });
    };

    const cancelOngoingExport = () => {
        if (!exportProgress.active) return;
        exportCancelledRef.current = true;
        endExportProgress();
        setToastMessage('Export cancelled');
        setToastType('warning');
        setShowToast(true);
    };

    const sizingColsForSummaryAoa = (rows: (string | number)[][]): { wch: number }[] => {
        let maxA = 8;
        let maxB = 8;
        for (const row of rows) {
            const a = row[0] != null ? String(row[0]) : '';
            const b = row[1] != null ? String(row[1]) : '';
            maxA = Math.max(maxA, Math.min(a.length, 100));
            maxB = Math.max(maxB, Math.min(b.length, 160));
        }
        return [{ wch: Math.min(maxA + 2, 44) }, { wch: Math.min(maxB + 2, 80) }];
    };

    const applyRequestExcelColsForDataSheet = (ws: XLSX.WorkSheet, dataRows: Record<string, unknown>[]) => {
        const keys = dataRows.length ? Object.keys(dataRows[0]) : [];
        const cols: { wch: number }[] = [];
        for (let c = 0; c < keys.length; c++) {
            const key = keys[c];
            let max = Math.max(8, key.length);
            for (const r of dataRows) {
                const raw = r[key];
                const s = raw != null ? String(raw) : '';
                for (const line of s.split(/\r?\n/)) {
                    max = Math.max(max, Math.min(line.length, 240));
                }
            }
            cols.push({ wch: Math.min(max + 2, 56) });
        }
        (ws as { '!cols'?: { wch: number }[] })['!cols'] = cols;
    };

    const sanitizeExcelSheetName = (name: string): string => {
        const cleaned = name
            .replace(/[\[\]:\\/?*]/g, '-')
            .trim()
            .slice(0, 31);
        return cleaned || 'Sheet';
    };

    const writeRequestStyledExcelWorkbook = (
        title: string,
        dataSheets: { name: string; rows: Record<string, unknown>[] }[],
        fileName: string,
        summaryPairs: (string | number)[][]
    ) => {
        const summaryAoa: (string | number)[][] = [[title, ''], ['Report summary', ''], ...summaryPairs];
        const summaryWs: XLSX.WorkSheet = {};
        XLSX.utils.sheet_add_aoa(summaryWs, summaryAoa, { origin: 'A1' });
        (summaryWs as { '!cols'?: { wch: number }[] })['!cols'] = sizingColsForSummaryAoa(summaryAoa);

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, summaryWs, 'Summary');
        for (const { name, rows } of dataSheets) {
            if (!rows.length) continue;
            const dataWs: XLSX.WorkSheet = {};
            XLSX.utils.sheet_add_json(dataWs, rows, { origin: 'A1', skipHeader: false });
            applyRequestExcelColsForDataSheet(dataWs, rows);
            (dataWs as { '!views'?: unknown[] })['!views'] = [
                {
                    state: 'frozen',
                    ySplit: 1,
                    topLeftCell: 'A2',
                    activePane: 'bottomLeft',
                    zoomScale: 100,
                },
            ];
            XLSX.utils.book_append_sheet(workbook, dataWs, sanitizeExcelSheetName(name));
        }
        XLSX.writeFile(workbook, fileName);
    };

    const runRequestExportExcel = async () => {
        if (filteredRequests.length === 0) {
            setToastMessage('No requests to export');
            setToastType('warning');
            setShowToast(true);
            return;
        }

        beginExportProgress('excel');
        const raf = () => new Promise<void>((r) => requestAnimationFrame(() => r()));

        try {
            updateExportProgress(4, 'Resolving branches for each row…');
            await raf();

            const list = filteredRequests;
            const total = list.length;
            const CHUNK = 6;
            const buckets: Record<'CCTV' | 'Guard' | 'BAMS' | 'Other', Record<string, unknown>[]> = {
                CCTV: [],
                Guard: [],
                BAMS: [],
                Other: [],
            };

            for (let i = 0; i < total; i += CHUNK) {
                if (exportCancelledRef.current) throw new ExportCancelledError();
                const slice = list.slice(i, i + CHUNK);
                const part = await Promise.all(
                    slice.map(async (request) => {
                        const { address: resolvedBranchAddress, location: resolvedLocation } =
                            await resolveExportBranchFieldsForRequest(request);
                        const tab = getRequestExcelTabKey(request);
                        const row = buildRequestExcelRowForExport(
                            request,
                            tab,
                            String(resolvedBranchAddress ?? ''),
                            String(resolvedLocation ?? '')
                        );
                        return { tab, row };
                    })
                );
                for (const { tab, row } of part) {
                    buckets[tab].push(row);
                }
                const done = Math.min(i + CHUNK, total);
                updateExportProgress(4 + Math.round((done / total) * 48), `Rows ${done}/${total}…`);
                await raf();
            }

            if (exportCancelledRef.current) throw new ExportCancelledError();

            updateExportProgress(56, 'Preparing workbook…');
            await raf();

            const summaryPairs = buildRequestExportSummaryPairs(filteredRequests.length);

            if (exportCancelledRef.current) throw new ExportCancelledError();

            updateExportProgress(88, 'Writing file…');
            await raf();

            const sheetOrder: ('CCTV' | 'Guard' | 'BAMS' | 'Other')[] = ['CCTV', 'Guard', 'BAMS', 'Other'];
            const dataSheets = sheetOrder
                .filter((k) => buckets[k].length > 0)
                .map((k) => ({ name: k, rows: buckets[k] }));

            const fileName = `Request_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
            const summaryPairsWithSheets = [
                ...summaryPairs,
                ['Data sheets', dataSheets.map((s) => s.name).join(', ')],
            ];
            writeRequestStyledExcelWorkbook(
                'Request export (full data — matches current filters)',
                dataSheets,
                fileName,
                summaryPairsWithSheets
            );

            if (exportCancelledRef.current) throw new ExportCancelledError();

            updateExportProgress(100, 'Download complete');
            setToastMessage('Excel file exported successfully');
            setToastType('success');
            setShowToast(true);
        } catch (err) {
            if (isExportCancelledError(err)) return;
            console.error(err);
            setToastMessage('Export failed. Please try again.');
            setToastType('error');
            setShowToast(true);
        } finally {
            setTimeout(() => endExportProgress(), 600);
        }
    };

    const runRequestExportPdf = async () => {
        if (filteredRequests.length === 0) {
            setToastMessage('No requests to export');
            setToastType('warning');
            setShowToast(true);
            return;
        }

        const rows = filteredRequests;
        const total = rows.length;
        const wideOpen = isRequestExportWideOpen();
        const pdfTruncated = !wideOpen && total > REQUEST_PDF_FILTERED_ROW_CAP;
        const tableRows = pdfTruncated ? rows.slice(0, REQUEST_PDF_FILTERED_ROW_CAP) : rows;

        beginExportProgress('pdf');
        const raf = () => new Promise<void>((r) => requestAnimationFrame(() => r()));
        const CHUNK = 6;

        try {
            updateExportProgress(5, 'Loading bank logo…');
            await raf();

            let bankLogo: { dataUrl: string; format: 'PNG'; w: number; h: number } | null = null;
            if (companyId) {
                try {
                    const bankSnap = await getDoc(fsDoc(db, 'banks', companyId));
                    const logoUrl =
                        bankSnap.exists() && typeof (bankSnap.data() as { imgUrl?: string }).imgUrl === 'string'
                            ? String((bankSnap.data() as { imgUrl?: string }).imgUrl).trim()
                            : '';
                    if (logoUrl) {
                        bankLogo = await tryLoadImageForPdf(logoUrl);
                    }
                } catch {
                    bankLogo = null;
                }
            }

            if (exportCancelledRef.current) throw new ExportCancelledError();

            updateExportProgress(16, 'Rendering PDF…');

            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const pageW = doc.internal.pageSize.getWidth();

            doc.setFillColor(41, 128, 185);
            doc.rect(0, 0, pageW, 26, 'F');

            let titleX = 12;
            if (bankLogo?.dataUrl && bankLogo.w > 0 && bankLogo.h > 0) {
                try {
                    const maxW = 26;
                    const maxH = 17;
                    let drawW = maxW;
                    let drawH = (bankLogo.h / bankLogo.w) * drawW;
                    if (drawH > maxH) {
                        drawH = maxH;
                        drawW = (bankLogo.w / bankLogo.h) * drawH;
                    }
                    doc.addImage(bankLogo.dataUrl, 'PNG', 10, 5.5, drawW, drawH);
                    titleX = 10 + drawW + 4;
                } catch {
                    titleX = 12;
                }
            }

            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(14);
            doc.text('Request reports', titleX, 11);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.text('Filtered export — PSO request management', titleX, 18);

            const summaryBody = buildRequestExportSummaryPairs(total).map((pair) => [String(pair[0]), String(pair[1])]);

            autoTable(doc, {
                startY: 22,
                head: [['Summary', 'Details']],
                body: summaryBody,
                theme: 'striped',
                styles: { fontSize: 8.5, cellPadding: 1.5, lineColor: [220, 224, 226], lineWidth: 0.1, textColor: 44 },
                headStyles: { fillColor: [52, 73, 94], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [252, 252, 253] },
                columnStyles: { 0: { cellWidth: 52, fontStyle: 'bold' }, 1: { cellWidth: pageW - 12 * 2 - 52 } },
                margin: { left: 12, right: 12 },
            });

            updateExportProgress(28, 'Summarizing by branch…');
            if (exportCancelledRef.current) throw new ExportCancelledError();

            const branchMetricsPdf = await buildRequestBranchExportRowsAsync(rows, {
                isCancelled: () => exportCancelledRef.current,
                onProgress: (done, tot) => {
                    const pct = 28 + Math.round((done / Math.max(1, tot)) * 20);
                    updateExportProgress(pct, `Branch groups ${done}/${tot}…`);
                },
            });
            doc.addPage();
            let sectionTop = 14;

            if (branchMetricsPdf.length) {
                doc.setFontSize(9);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(44, 62, 80);
                doc.text('Requests by branch', 12, sectionTop);
                autoTable(doc, {
                    startY: sectionTop + 3,
                    head: [['Branch code', 'Branch name', 'Location', 'Total', 'CCTV', 'Guard', 'BAMS', 'Other']],
                    body: branchMetricsPdf.map((m) => [
                        m.branchCode,
                        m.branchName,
                        m.location,
                        String(m.total),
                        String(m.cctv),
                        String(m.guard),
                        String(m.bams),
                        String(m.other),
                    ]),
                    theme: 'grid',
                    styles: { fontSize: 7.5, cellPadding: 1.2, lineColor: [200, 200, 200], lineWidth: 0.1 },
                    headStyles: { fillColor: [52, 73, 94], textColor: 255, fontStyle: 'bold' },
                    alternateRowStyles: { fillColor: [252, 252, 253] },
                    margin: { left: 12, right: 12 },
                });
                doc.addPage();
                sectionTop = 14;
            }

            updateExportProgress(52, 'Resolving detail rows…');
            if (exportCancelledRef.current) throw new ExportCancelledError();

            const enrichedRows: { request: Request; address: string; location: string }[] = [];
            const detailTotal = tableRows.length;
            for (let i = 0; i < detailTotal; i += CHUNK) {
                if (exportCancelledRef.current) throw new ExportCancelledError();
                const slice = tableRows.slice(i, i + CHUNK);
                const part = await Promise.all(
                    slice.map(async (request) => {
                        const { address, location } = await resolveExportBranchFieldsForRequest(request);
                        return { request, address, location };
                    })
                );
                enrichedRows.push(...part);
                const done = Math.min(i + CHUNK, detailTotal);
                updateExportProgress(52 + Math.round((done / detailTotal) * 28), `Detail rows ${done}/${detailTotal}…`);
                await raf();
            }

            updateExportProgress(84, 'Building detail table…');

            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(44, 62, 80);
            doc.text('Detail table', 12, sectionTop);

            const head = [['Request ID', 'Type', 'Branch code', 'Branch Name', 'Location', 'Date from', 'Date to', 'No. of Days']];
            const body = enrichedRows.map(({ request, address, location }) => {
                const dates = getDateFields(request);
                let daysVal = formatInclusiveNoOfDays(dates.dateFrom, dates.dateTo);
                if (request.requestType === 'CCTV' && request.cctvDateRanges && request.cctvDateRanges.length > 0) {
                    let totalDays = 0;
                    request.cctvDateRanges.forEach((range: any) => {
                        totalDays += inclusiveDaysBetween(range.dateFrom, range.dateTo);
                    });
                    daysVal = String(totalDays);
                }
                return [
                    request.requestId || 'N/A',
                    request.requestType || 'N/A',
                    request.branchCode || 'N/A',
                    address,
                    location,
                    formatDate(dates.dateFrom),
                    formatDate(dates.dateTo),
                    daysVal,
                ];
            });

            autoTable(doc, {
                startY: sectionTop + 3,
                head,
                body,
                styles: { fontSize: 8, cellPadding: 1.4, lineColor: [220, 220, 220], lineWidth: 0.1 },
                headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [248, 249, 250] },
                margin: { left: 12, right: 12 },
            });

            const finalY = ((doc as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY as number) || sectionTop + 30;
            const pageWLast = doc.internal.pageSize.getWidth();
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            if (pdfTruncated) {
                doc.setTextColor(180, 83, 9);
                doc.text(
                    `Note: Only the first ${REQUEST_PDF_FILTERED_ROW_CAP} detail rows are shown. Export to Excel for all ${total} rows.`,
                    12,
                    Math.min(finalY + 10, 280),
                    { maxWidth: pageWLast - 24 }
                );
            } else if (!wideOpen) {
                doc.setTextColor(90, 90, 90);
                doc.text(
                    'All rows that match your filters are included in the table above.',
                    12,
                    Math.min(finalY + 10, 280),
                    { maxWidth: pageWLast - 24 }
                );
            } else {
                doc.setTextColor(90, 90, 90);
                doc.text(
                    'No filters were applied. Every row is included in the table above.',
                    12,
                    Math.min(finalY + 10, 280),
                    { maxWidth: pageWLast - 24 }
                );
            }

            updateExportProgress(93, 'Saving file…');
            await raf();

            if (exportCancelledRef.current) throw new ExportCancelledError();

            const fileName = `Request_Report_${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(fileName);

            updateExportProgress(100, 'Download complete');
            setToastMessage('PDF file exported successfully');
            setToastType('success');
            setShowToast(true);
        } catch (err) {
            if (isExportCancelledError(err)) return;
            console.error(err);
            setToastMessage('PDF export failed. Please try again.');
            setToastType('error');
            setShowToast(true);
        } finally {
            setTimeout(() => endExportProgress(), 600);
        }
    };

    const handleRequestExportChoice = (format: 'excel' | 'pdf') => {
        setExportMenuOpen(false);
        if (exportProgress.active) {
            setToastMessage('Another export is already running');
            setToastType('warning');
            setShowToast(true);
            return;
        }
        void (format === 'excel' ? runRequestExportExcel() : runRequestExportPdf());
    };

    const downloadRequestRowPDF = (request: Request) => {
        const doc = new jsPDF();
        const rid = request.requestId || request.id;
        let y = 16;
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(44, 62, 80);
        doc.text('Request detail', 14, y);
        y += 10;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(60, 60, 60);
        doc.text(`Generated ${new Date().toLocaleString()}`, 14, y);
        y += 8;

        const cb = request.createdBy || '';
        const psoRow = (cb && psoNameByCreatorId[cb]) || request.requesterName || cb || 'N/A';
        const dates = getDateFields(request);
        const timeFrom =
            (request as { cctvTimeFrom?: string }).cctvTimeFrom &&
                (request as { cctvTimeFrom?: string }).cctvTimeFrom!.trim() !== ''
                ? (request as { cctvTimeFrom?: string }).cctvTimeFrom
                : request.timeFrom || 'N/A';
        const timeTo =
            (request as { cctvTimeTo?: string }).cctvTimeTo &&
                (request as { cctvTimeTo?: string }).cctvTimeTo!.trim() !== ''
                ? (request as { cctvTimeTo?: string }).cctvTimeTo
                : request.timeTo || 'N/A';

        const detailRows: string[][] = [
            ['Request ID', String(rid)],
            ['Status', request.status || 'N/A'],
            ['Type', request.requestType || 'N/A'],
            ['Branch code', request.branchCode || 'N/A'],
            ['Branch name', request.branchName || 'N/A'],
            ['Company', request.company || 'N/A'],
            ['PSO', String(psoRow)],
            ['Requester contact', request.requesterContact || 'N/A'],
            ['Required by', request.requiredBy || 'N/A'],
            ['Date from', formatDate(dates.dateFrom)],
            ['Date to', formatDate(dates.dateTo)],
            ['No. of days', formatInclusiveNoOfDays(dates.dateFrom, dates.dateTo)],
            ['Time from', String(timeFrom)],
            ['Time to', String(timeTo)],
            ['Number of guards', request.numberOfGuards != null ? String(request.numberOfGuards) : 'N/A'],
            ['Hardware', request.hardwareName || 'N/A'],
            ['Purpose', getFormattedPurpose(request)],
            ['Created', formatDateTime(request.createdAt)],
            ['Updated', request.updatedAt ? formatDateTime(request.updatedAt) : 'N/A'],
        ];

        autoTable(doc, {
            startY: y,
            body: detailRows,
            theme: 'grid',
            styles: { fontSize: 9, cellPadding: 2 },
            columnStyles: { 0: { cellWidth: 42, fontStyle: 'bold' } },
            margin: { left: 14, right: 14 },
        });

        doc.save(`Request_${String(rid).replace(/\s+/g, '_')}.pdf`);
        setToastMessage('PDF downloaded');
        setToastType('success');
        setShowToast(true);
    };

    const columns: Column<Request>[] = [
        {
            header: 'Request ID',
            accessor: 'requestId',
            className: 'whitespace-nowrap font-medium text-gray-900 dark:text-gray-100',
        },
        {
            header: 'Type',
            render: (request: Request) => getRequestTypeBadge(request.requestType),
        },
        {
            header: 'Branch Code',
            render: (request: Request) => (
                <div className="space-y-1 text-sm">
                    <div className="font-semibold text-gray-900 dark:text-gray-100">
                        {(request.branchCode || '—').toString()}
                    </div>
                    <div
                        style={{
                            maxWidth: '280px',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal',
                        }}
                        className="text-gray-600 dark:text-gray-400"
                    >
                        {(request.branchName || '—').toString()}
                    </div>
                </div>
            ),
        },
        {
            header: 'PSO',
            render: (request: Request) => {
                const id = request.createdBy || '';
                const fromPos = id ? psoNameByCreatorId[id] : '';
                return (fromPos || request.requesterName || id || 'N/A').toString();
            },
        },
        {
            header: 'Required By',
            accessor: 'requiredBy',
        },
        {
            header: 'Date From',
            render: (request: Request) => formatDate(getDateFields(request).dateFrom),
            className: 'text-gray-600 dark:text-gray-400',
        },
        {
            header: 'Date To',
            render: (request: Request) => formatDate(getDateFields(request).dateTo),
            className: 'text-gray-600 dark:text-gray-400',
        },
        {
            header: 'No. of Days',
            render: (request: Request) => {
                if (request.requestType === 'CCTV' && request.cctvDateRanges && request.cctvDateRanges.length > 0) {
                    let totalDays = 0;
                    request.cctvDateRanges.forEach((range: any) => {
                        totalDays += inclusiveDaysBetween(range.dateFrom, range.dateTo);
                    });
                    return totalDays;
                }
                const dates = getDateFields(request);
                return formatInclusiveNoOfDays(dates.dateFrom, dates.dateTo);
            },
            className: 'text-gray-600 dark:text-gray-400',
        },
        {
            header: 'Status',
            render: (request: Request) => (
                <div className="flex flex-col gap-1 items-start">
                    {getStatusBadge(request.status)}
                    {request.resubmitted && (
                        <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
                            Resubmitted
                        </span>
                    )}
                </div>
            ),
        },
        {
            header: 'Created',
            render: (request: Request) => formatDate(request.createdAt),
            className: 'text-gray-600 dark:text-gray-400',
        },
    ];

    const headerActions = (
        <div className="flex gap-3">
            {showAddRequestButton && (
                <Button
                    variant="primary"
                    onClick={() => {
                        setShowAddModal(true);
                    }}
                    className="flex items-center gap-2"
                >
                    <Icon icon={FiPlus} className="h-4 w-4" />
                    Add Request Form
                </Button>
            )}
            {(designation === 'Admin' || isIncharge || isBankUser) && (
                <Button variant="secondary" onClick={() => setShowRateModal(true)} className="flex items-center gap-2">
                    <Icon icon={FiRotateCcw} className="h-4 w-4" />
                    Set Guard Rate
                </Button>
            )}
        </div>
    );

    const filters = (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex w-full flex-1 items-stretch gap-3 md:pr-3">
                    <div className="min-w-0 flex-1">
                        <TextField
                            type="text"
                            className="!h-11"
                            placeholder="Search by request ID, branch code, PSO name, or PSO id…"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    applyRequestSearch();
                                }
                            }}
                            prefix={<Icon icon={FiSearch} />}
                            suffix={
                                searchInput ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSearchInput('');
                                            setAppliedSearchTerm('');
                                            setCurrentPage(1);
                                        }}
                                        className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                        aria-label="Clear search"
                                        title="Clear search"
                                    >
                                        <Icon icon={FiX} className="h-4 w-4" />
                                    </button>
                                ) : null
                            }
                        />
                    </div>
                    <Button onClick={applyRequestSearch} className="!h-11 !py-0 flex shrink-0 items-center gap-2">
                        <Icon icon={FiSearch} className="h-4 w-4" />
                        Search
                    </Button>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-3">
                    <div className="relative" ref={exportMenuRef}>
                        <button
                            type="button"
                            onClick={() => setExportMenuOpen((o) => !o)}
                            disabled={exportProgress.active}
                            aria-expanded={exportMenuOpen}
                            aria-haspopup="true"
                            className="inline-flex h-11 items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                        >
                            <Icon icon={FiDownload} className="h-4 w-4" />
                            Export
                            <Icon icon={FiChevronDown} className={`h-4 w-4 shrink-0 transition-transform ${exportMenuOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {exportMenuOpen && !exportProgress.active && (
                            <div className="absolute right-0 z-40 mt-1 w-52 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl ring-1 ring-black/5 dark:border-gray-600 dark:bg-gray-900">
                                <button
                                    type="button"
                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-emerald-50 dark:text-gray-100 dark:hover:bg-emerald-900/20"
                                    onClick={() => handleRequestExportChoice('excel')}
                                >
                                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600 text-xs font-bold text-white">
                                        XLS
                                    </span>
                                    <span>
                                        <span className="block">Excel workbook</span>
                                        <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                                            Full filtered columns
                                        </span>
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-rose-50 dark:text-gray-100 dark:hover:bg-rose-900/20"
                                    onClick={() => handleRequestExportChoice('pdf')}
                                >
                                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-600 text-xs font-bold text-white">
                                        PDF
                                    </span>
                                    <span>
                                        <span className="block">PDF report</span>
                                        <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                                            Summary, by branch, detail
                                        </span>
                                    </span>
                                </button>
                            </div>
                        )}
                    </div>
                    <Button variant="secondary" onClick={openRequestFilterModal} className="flex items-center gap-2">
                        <Icon icon={FiFileText} className="h-4 w-4" />
                        Filters
                    </Button>
                    {hasClearableRequestFilters && (
                        <Button variant="secondary" onClick={clearRequestFilters}>
                            Clear Filters
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <div className="p-6">
                {requestRowContextMenu && (
                    <div
                        id="requests-table-row-context"
                        role="menu"
                        className="fixed z-[200] min-w-[11rem] overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-600 dark:bg-gray-900"
                        style={{
                            left: Math.min(
                                requestRowContextMenu.clientX,
                                (typeof window !== 'undefined' ? window.innerWidth : 1200) - 180
                            ),
                            top: Math.min(
                                requestRowContextMenu.clientY,
                                (typeof window !== 'undefined' ? window.innerHeight : 800) - 8
                            ),
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            role="menuitem"
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800"
                            onClick={() => {
                                handleViewDetails(requestRowContextMenu.request);
                                setRequestRowContextMenu(null);
                            }}
                        >
                            <Icon icon={FiEye} className="h-4 w-4 shrink-0 opacity-70" />
                            View
                        </button>
                        {isBankUser && (
                            <>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800"
                                    onClick={() => {
                                        handleEditRequest(requestRowContextMenu.request);
                                        setRequestRowContextMenu(null);
                                    }}
                                >
                                    <Icon icon={FiEdit2} className="h-4 w-4 shrink-0 opacity-70" />
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                                    onClick={() => {
                                        void handleDeleteRequest(requestRowContextMenu.request);
                                        setRequestRowContextMenu(null);
                                    }}
                                >
                                    <Icon icon={FiTrash2} className="h-4 w-4 shrink-0 opacity-70" />
                                    Delete
                                </button>
                            </>
                        )}
                        {!isHead &&
                            ['approved', 'rejected'].includes(
                                (requestRowContextMenu.request.status || '').toLowerCase()
                            ) && (
                                <button
                                    type="button"
                                    role="menuitem"
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800"
                                    onClick={() => {
                                        void openStatusEmailModal(requestRowContextMenu.request);
                                        setRequestRowContextMenu(null);
                                    }}
                                >
                                    <Icon icon={FiMail} className="h-4 w-4 shrink-0 opacity-70" />
                                    Email
                                </button>
                            )}
                        <button
                            type="button"
                            role="menuitem"
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800"
                            onClick={() => {
                                downloadRequestRowPDF(requestRowContextMenu.request);
                                setRequestRowContextMenu(null);
                            }}
                        >
                            <Icon icon={FiDownload} className="h-4 w-4 shrink-0 opacity-70" />
                            PDF
                        </button>
                    </div>
                )}
                <TableComp
                    title="View Requests"
                    description="Manage and track request forms"
                    data={paginatedRequests}
                    columns={columns}
                    keyExtractor={(item) => item.id}
                    loading={loading}
                    emptyMessage={
                        appliedSearchTerm.trim() || hasClearableRequestFilters
                            ? 'Try adjusting your search or filter'
                            : 'No requests found matching your criteria.'
                    }
                    onRowClick={handleViewDetails}
                    onRowContextMenu={(e, item) => {
                        e.preventDefault();
                        setRequestRowContextMenu({ request: item, clientX: e.clientX, clientY: e.clientY });
                    }}
                    headerActions={headerActions}
                    filters={filters}
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage,
                    }}
                />
            </div>

            <Modal
                isOpen={showRequestFilterModal}
                onClose={() => setShowRequestFilterModal(false)}
                title="Filter requests"
                size="lg"
            >
                <div className="space-y-4 p-6">
                    {(!isIncharge || isInchargeMIS) && (
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Filter by Request Type
                            </label>
                            <select
                                value={draftRequestTypeFilter}
                                onChange={(e) => setDraftRequestTypeFilter(e.target.value as 'All' | 'CCTV' | 'Guard' | 'BAMS')}
                                className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                            >
                                <option value="All" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">All Types</option>
                                <option value="CCTV" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">CCTV</option>
                                <option value="Guard" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Guard</option>
                                <option value="BAMS" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">BAMS</option>
                            </select>
                        </div>
                    )}
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Quick period</label>
                        <select
                            value={draftDateFilter}
                            onChange={(e) => {
                                const v = e.target.value as 'all' | 'daily' | 'weekly' | 'monthly';
                                setDraftDateFilter(v);
                                setDraftCreatedFrom(null);
                                setDraftCreatedTo(null);
                            }}
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                        >
                            <option value="all" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">All Time</option>
                            <option value="daily" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Daily</option>
                            <option value="weekly" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Weekly</option>
                            <option value="monthly" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Monthly</option>
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Date range</label>
                        <DatePicker.RangePicker
                            className="w-full [&_.ant-picker]:min-h-[48px] [&_.ant-picker]:w-full [&_.ant-picker-input>input]:text-sm"
                            value={
                                draftCreatedFrom && draftCreatedTo
                                    ? [dayjs(draftCreatedFrom), dayjs(draftCreatedTo)]
                                    : null
                            }
                            allowClear
                            format="MMM D, YYYY"
                            onChange={(dates) => {
                                if (!dates || !dates[0] || !dates[1]) {
                                    setDraftCreatedFrom(null);
                                    setDraftCreatedTo(null);
                                    return;
                                }
                                setDraftCreatedFrom(dates[0].format('YYYY-MM-DD'));
                                setDraftCreatedTo(dates[1].format('YYYY-MM-DD'));
                                setDraftDateFilter('all');
                            }}
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">PSO</label>
                        <select
                            value={draftPsoFilterRequest}
                            onChange={(e) => setDraftPsoFilterRequest(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                        >
                            <option value="all">All PSOs</option>
                            {requestPsoFilterOptions.map((label) => (
                                <option key={label} value={label}>
                                    {label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Required by</label>
                        <select
                            value={draftRequiredByFilter}
                            onChange={(e) => setDraftRequiredByFilter(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                        >
                            <option value="All">All</option>
                            {requestRequiredByFilterOptions.map((v) => (
                                <option key={v} value={v}>
                                    {v}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Filter by Status</label>
                        <select
                            value={draftFilterStatus}
                            onChange={(e) => setDraftFilterStatus(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                        >
                            <option value="All" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">All Statuses</option>
                            {statuses.map((status) => (
                                <option key={status} value={status} className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">
                                    {status}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex flex-wrap justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                        <Button variant="secondary" type="button" onClick={() => setShowRequestFilterModal(false)}>
                            Cancel
                        </Button>
                        <Button type="button" onClick={applyRequestFiltersModal}>
                            Apply
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Request Details Modal */}
            {showDetailsModal && selectedRequest && (
                <Modal
                    isOpen={showDetailsModal}
                    onClose={closeRequestDetailsModal}
                    title={`Request Details - ${selectedRequest.requestId}`}
                    size="lg"
                    closeOnBackdropClick={false}
                    footer={
                        <div className="px-6 py-4">
                            <div className="flex flex-wrap items-center justify-end gap-3">
                                <Button variant="secondary" onClick={closeRequestDetailsModal}>
                                    Close
                                </Button>

                                {showApproveEmailStep && pendingApproveRequest && (
                                    <>
                                        <Button variant="secondary" onClick={cancelApproveEmailStep} disabled={approveSending}>
                                            Back
                                        </Button>
                                        <Button variant="primary" onClick={() => void handleApproveEmailDone()} disabled={approveSending}>
                                            {approveSending ? (
                                                <>
                                                    <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent" />
                                                    Sending…
                                                </>
                                            ) : (
                                                <>
                                                    <Icon icon={FiCheck} className="mr-1 h-4 w-4" />
                                                    Done
                                                </>
                                            )}
                                        </Button>
                                    </>
                                )}

                                {showRejectEmailStep && pendingRejectRequest && (
                                    <>
                                        <Button variant="secondary" onClick={cancelRejectEmailStep} disabled={rejectSending}>
                                            Back
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={() => void handleRejectEmailDone()}
                                            disabled={rejectSending}
                                            className="!bg-red-600 hover:!bg-red-700"
                                        >
                                            {rejectSending ? (
                                                <>
                                                    <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent" />
                                                    Sending…
                                                </>
                                            ) : (
                                                <>
                                                    <Icon icon={FiX} className="mr-1 h-4 w-4" />
                                                    Confirm reject
                                                </>
                                            )}
                                        </Button>
                                    </>
                                )}

                                {/* Regular PSO: Resubmit if rejected, otherwise view only */}
                                {!inEmailRecipientStep && isBasePsoDesignation(roleDesignation) && !isBankUser && (
                                    selectedRequest?.status === 'rejected' ? (
                                        <Button
                                            variant="primary"
                                            onClick={() => handleOpenResubmitModal(selectedRequest)}
                                            disabled={updating}
                                        >
                                            <Icon icon={FiRotateCcw} className="mr-1 h-4 w-4" />
                                            Resubmit
                                        </Button>
                                    ) : (
                                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                            <Icon icon={FiAlertCircle} className="mr-1 h-4 w-4" />
                                            View only - No actions available
                                        </div>
                                    )
                                )}

                                {/* Bank User: Edit & Delete buttons */}
                                {!inEmailRecipientStep && isBankUser && (
                                    <>
                                        <Button
                                            variant="secondary"
                                            onClick={() => {
                                                handleEditRequest(selectedRequest);
                                                closeRequestDetailsModal();
                                            }}
                                            className="flex items-center gap-2"
                                        >
                                            <Icon icon={FiEdit2} className="h-4 w-4" />
                                            Edit Request
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={async () => {
                                                await handleDeleteRequest(selectedRequest);
                                                closeRequestDetailsModal();
                                            }}
                                            className="flex items-center gap-2 !text-red-600 hover:!bg-red-50 dark:!text-red-400 dark:hover:!bg-red-950/30"
                                        >
                                            <Icon icon={FiTrash2} className="h-4 w-4" />
                                            Delete Request
                                        </Button>
                                    </>
                                )}

                                {/* Regional PSO / Regional Head: Forward to Head + Reject for pending requests */}
                                {!inEmailRecipientStep && isRegionalLeadDesignation(roleDesignation) && selectedRequest?.status === 'pending' && (
                                    <>
                                        <Button
                                            variant="secondary"
                                            onClick={() => handleForwardToHead(selectedRequest)}
                                            disabled={updating || regionalRejectSending}
                                        >
                                            {updating ? (
                                                <>
                                                    <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></div>
                                                    Forwarding...
                                                </>
                                            ) : (
                                                <>
                                                    <Icon icon={FiClock} className="mr-1 h-4 w-4" />
                                                    Forward to Head
                                                </>
                                            )}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={() => handleOpenRegionalRejectModal(selectedRequest)}
                                            disabled={updating || regionalRejectSending}
                                            className="!bg-red-600 hover:!bg-red-700 !text-white"
                                        >
                                            <Icon icon={FiX} className="mr-1 h-4 w-4" />
                                            Reject
                                        </Button>
                                    </>
                                )}

                                {/* Regional PSO / Regional Head: Hide buttons if already forwarded */}
                                {!inEmailRecipientStep && isRegionalLeadDesignation(roleDesignation) && selectedRequest?.status === 'forwarded_to_head' && (
                                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                        <Icon icon={FiCheck} className="mr-1 h-4 w-4" />
                                        Request already forwarded to Head
                                    </div>
                                )}

                                {/* Regional PSO / Regional Head: Show status message for approved/rejected requests */}
                                {!inEmailRecipientStep && isRegionalLeadDesignation(roleDesignation) &&
                                    selectedRequest?.status !== 'pending' &&
                                    selectedRequest?.status !== 'forwarded_to_head' && (
                                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                            <Icon icon={FiCheck} className="mr-1 h-4 w-4" />
                                            Request status: {selectedRequest?.status}
                                        </div>
                                    )}

                                {/* Head PSO: Approve/Reject buttons */}
                                {!inEmailRecipientStep && isHeadPsoDesignation(roleDesignation) && (
                                    <>
                                        {(selectedRequest?.status === 'pending' || selectedRequest?.status === 'forwarded_to_head') && (
                                            <>
                                                <Button
                                                    variant="primary"
                                                    onClick={() => handleUpdateStatus(selectedRequest, 'approved')}
                                                    disabled={updating || approveSending || rejectSending}
                                                >
                                                    <Icon icon={FiCheck} className="mr-1 h-4 w-4" />
                                                    Approve
                                                </Button>
                                                <Button
                                                    variant="secondary"
                                                    onClick={() => handleUpdateStatus(selectedRequest, 'rejected')}
                                                    disabled={updating || approveSending || rejectSending}
                                                    className="!bg-red-600 hover:!bg-red-700"
                                                >
                                                    <Icon icon={FiX} className="mr-1 h-4 w-4" />
                                                    Reject
                                                </Button>
                                            </>
                                        )}
                                        {selectedRequest?.status === 'approved' && (
                                            <Button
                                                variant="secondary"
                                                onClick={() => handleUpdateStatus(selectedRequest, 'in_progress')}
                                                disabled={updating}
                                            >
                                                {updating ? (
                                                    <>
                                                        <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></div>
                                                        Starting...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Icon icon={FiClock} className="mr-1 h-4 w-4" />
                                                        Start Processing
                                                    </>
                                                )}
                                            </Button>
                                        )}
                                        {selectedRequest?.status === 'in_progress' && (
                                            <Button
                                                variant="primary"
                                                onClick={() => handleUpdateStatus(selectedRequest, 'completed')}
                                                disabled={updating}
                                            >
                                                {updating ? (
                                                    <>
                                                        <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></div>
                                                        Completing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Icon icon={FiCheck} className="mr-1 h-4 w-4" />
                                                        Mark Complete
                                                    </>
                                                )}
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    }
                >
                    <div className="p-6">
                        {(() => {
                            const fs = filterStatus.toLowerCase();
                            const rs = (selectedRequest.status || '').toLowerCase();
                            const rowIsTerminal = rs === 'approved' || rs === 'rejected';
                            const filterMatchesRow =
                                (fs === 'approved' && rs === 'approved') || (fs === 'rejected' && rs === 'rejected');
                            const showStatusEmailInDetails =
                                !isHead && !inEmailRecipientStep && rowIsTerminal && (filterMatchesRow || fs === 'all');
                            if (!showStatusEmailInDetails) return null;
                            return (
                                <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200/90 bg-slate-50/90 px-4 py-3 dark:border-gray-600 dark:bg-gray-900/50">
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Resend the {rs === 'approved' ? 'approval' : 'rejection'} notification by email.
                                    </p>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        className="!py-2 !px-3 text-xs"
                                        onClick={() => void openStatusEmailModal(selectedRequest)}
                                    >
                                        <Icon icon={FiMail} className="mr-1.5 h-3.5 w-3.5" />
                                        Send email
                                    </Button>
                                </div>
                            );
                        })()}
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Request ID</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.requestId || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Request Type</label>
                                    <p className="mt-1">{getRequestTypeBadge(selectedRequest.requestType)}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Code</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.branchCode || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Name</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{displayBranchName}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Company</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.company || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Region</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{displayRegionName}</p>
                                </div>
                                {selectedRequest.requesterName && selectedRequest.requesterName !== 'N/A' && selectedRequest.requesterName !== '—' && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Requester Name</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.requesterName}</p>
                                    </div>
                                )}
                                {selectedRequest.requesterContact && selectedRequest.requesterContact !== 'N/A' && selectedRequest.requesterContact !== '—' && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Requester Contact</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.requesterContact}</p>
                                    </div>
                                )}
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Required By</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.requiredBy || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Status</label>
                                    {isBankUser ? (
                                        <div className="mt-1 flex items-center gap-2">
                                            <select
                                                value={selectedRequest.status}
                                                onChange={(e) => void handleUpdateStatus(selectedRequest, e.target.value)}
                                                className="rounded-lg border border-gray-300 bg-white py-1 px-2 text-xs font-semibold text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                                                disabled={updating}
                                            >
                                                <option value="pending">Pending</option>
                                                <option value="approved">Approved</option>
                                                <option value="rejected">Rejected</option>
                                                <option value="in_progress">In Progress</option>
                                                <option value="completed">Completed</option>
                                                <option value="forwarded_to_head">Forwarded to Head</option>
                                            </select>
                                            {updating && (
                                                <div className="h-3 w-3 animate-spin rounded-full border-2 border-solid border-blue-500 border-r-transparent" />
                                            )}
                                        </div>
                                    ) : (
                                        <p className="mt-1">{getStatusBadge(selectedRequest.status)}</p>
                                    )}
                                </div>
                                {selectedRequest.requestType === 'CCTV' && selectedRequest.cctvDateRanges && selectedRequest.cctvDateRanges.length > 0 ? (
                                    <div className="col-span-2">
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Date Ranges</label>
                                        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                            {selectedRequest.cctvDateRanges.map((range: any, idx: number) => (
                                                <div key={idx} className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-55 p-3 text-xs dark:border-gray-750 dark:bg-gray-800">
                                                    <div className="flex flex-col gap-0.5">
                                                        <div className="flex items-center gap-1.5">
                                                            <Icon icon={FiCalendar} className="text-blue-500" />
                                                            <span className="font-semibold text-gray-900 dark:text-gray-100">
                                                                {formatDate(range.dateFrom)} &rarr; {formatDate(range.dateTo)}
                                                            </span>
                                                        </div>
                                                        {range.timeFrom && range.timeTo && (
                                                            <span className="text-gray-650 dark:text-gray-355 ml-5 font-medium">
                                                                Time: {range.timeFrom} - {range.timeTo}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="rounded-lg bg-blue-50 px-2 py-0.5 font-bold text-[10px] text-blue-700 dark:bg-blue-950/20 dark:text-blue-400">
                                                        {inclusiveDaysBetween(range.dateFrom, range.dateTo)} days
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Date From</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDate(getDateFields(selectedRequest).dateFrom)}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Date To</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDate(getDateFields(selectedRequest).dateTo)}</p>
                                        </div>
                                    </>
                                )}
                                {selectedRequest.requestType !== 'Guard' && (
                                    <>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Time From</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                                {(() => {
                                                    const cctvTimeFrom = (selectedRequest as any).cctvTimeFrom;
                                                    const timeFrom = selectedRequest.timeFrom;
                                                    if (cctvTimeFrom && cctvTimeFrom.trim() !== '') {
                                                        return cctvTimeFrom;
                                                    }
                                                    if (timeFrom && timeFrom.trim() !== '') {
                                                        return timeFrom;
                                                    }
                                                    return 'N/A';
                                                })()}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Time To</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                                {(() => {
                                                    const cctvTimeTo = (selectedRequest as any).cctvTimeTo;
                                                    const timeTo = selectedRequest.timeTo;
                                                    if (cctvTimeTo && cctvTimeTo.trim() !== '') {
                                                        return cctvTimeTo;
                                                    }
                                                    if (timeTo && timeTo.trim() !== '') {
                                                        return timeTo;
                                                    }
                                                    return 'N/A';
                                                })()}
                                            </p>
                                        </div>
                                    </>
                                )}
                                {selectedRequest.requestType === 'Guard' && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Number of Guards</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.numberOfGuards || 'N/A'}</p>
                                    </div>
                                )}
                                {selectedRequest.requestType === 'BAMS' && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Hardware Name</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.hardwareName || 'N/A'}</p>
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Purpose</label>
                                <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900">
                                    <p className="text-sm text-gray-900 dark:text-gray-100">
                                        {getFormattedPurpose(selectedRequest)}
                                    </p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Created At</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDateTime(selectedRequest.createdAt)}</p>
                                </div>
                                {selectedRequest.updatedAt && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Updated At</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDateTime(selectedRequest.updatedAt)}</p>
                                    </div>
                                )}
                                {selectedRequest.forwardedAt && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Forwarded Date</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDateTime(selectedRequest.forwardedAt)}</p>
                                    </div>
                                )}
                                {selectedRequest.forwardedBy && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Forwarded By</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                            {psoNameByCreatorId[selectedRequest.forwardedBy] || selectedRequest.forwardedBy}
                                            {psoEmailById[selectedRequest.forwardedBy] && psoEmailById[selectedRequest.forwardedBy] !== 'N/A' && ` (${psoEmailById[selectedRequest.forwardedBy]})`}
                                        </p>
                                    </div>
                                )}
                                {selectedRequest.approvedAt && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Approve Date</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDateTime(selectedRequest.approvedAt)}</p>
                                    </div>
                                )}
                                {selectedRequest.rejectedAt && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Reject Date</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDateTime(selectedRequest.rejectedAt)}</p>
                                    </div>
                                )}
                                {(selectedRequest.status === 'approved' || selectedRequest.status === 'rejected') && (selectedRequest.rejectedByName || selectedRequest.updatedBy) && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            {selectedRequest.status === 'approved' ? 'Approved By' : 'Rejected By'}
                                        </label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                            {selectedRequest.status === 'rejected' && selectedRequest.rejectedByName ? (
                                                selectedRequest.rejectedByName
                                            ) : (
                                                <>
                                                    {psoNameByCreatorId[selectedRequest.updatedBy || ''] || selectedRequest.updatedBy}
                                                    {psoEmailById[selectedRequest.updatedBy || ''] && psoEmailById[selectedRequest.updatedBy || ''] !== 'N/A' && ` (${psoEmailById[selectedRequest.updatedBy || '']})`}
                                                </>
                                            )}
                                        </p>
                                    </div>
                                )}
                                {selectedRequest.status === 'rejected' && selectedRequest.rejectedReason && (
                                    <div className="col-span-2">
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Rejection Reason</label>
                                        <div className="mt-1 rounded-lg border border-red-200 bg-red-50/50 p-3 dark:border-red-900/30 dark:bg-red-950/20">
                                            <p className="text-sm text-gray-900 dark:text-gray-100">{selectedRequest.rejectedReason}</p>
                                        </div>
                                    </div>
                                )}
                                {selectedRequest.resubmitted && (
                                    <>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Resubmitted</label>
                                            <p className="mt-1 text-sm font-semibold text-amber-600 dark:text-amber-400">Yes</p>
                                        </div>
                                        {selectedRequest.resubmittedAt && (
                                            <div>
                                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Resubmitted Date</label>
                                                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDateTime(selectedRequest.resubmittedAt)}</p>
                                            </div>
                                        )}
                                        {selectedRequest.resubmissionReason && (
                                            <div className="col-span-2">
                                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Resubmission Reason</label>
                                                <div className="mt-1 rounded-lg border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-900/30 dark:bg-amber-950/20">
                                                    <p className="text-sm text-gray-900 dark:text-gray-100">{selectedRequest.resubmissionReason}</p>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="mt-6 flex flex-col gap-4">
                            {showApproveEmailStep && pendingApproveRequest && (
                                <div className="rounded-xl border border-blue-200/90 bg-gradient-to-b from-blue-50/95 via-white to-white p-4 shadow-sm dark:border-blue-900/45 dark:from-blue-950/35 dark:via-gray-950 dark:to-gray-950">
                                    <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                Approval notification
                                            </h3>
                                            <p className="mt-0.5 max-w-xl text-xs leading-relaxed text-gray-600 dark:text-gray-400">
                                                Recipients below receive the approval email. Add or remove addresses, then click{' '}
                                                <strong className="text-gray-800 dark:text-gray-200">Done</strong> to send and approve
                                                this request.
                                            </p>
                                        </div>
                                    </div>
                                    <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                        Recipients
                                    </label>
                                    {approveLoadingRecipients ? (
                                        <div className="flex min-h-[5.5rem] items-center justify-center rounded-xl border border-dashed border-blue-200/80 bg-blue-50/50 text-sm text-gray-500 dark:border-blue-900/50 dark:bg-blue-950/20 dark:text-gray-400">
                                            Loading suggested recipients…
                                        </div>
                                    ) : (
                                        <div className="min-h-[5.5rem] rounded-xl border border-gray-200/90 bg-gradient-to-b from-slate-50/90 to-white p-3 shadow-inner dark:border-gray-600 dark:from-gray-900/70 dark:to-gray-900/45">
                                            {approveRecipients.length === 0 ? (
                                                <p className="py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                                                    No recipients yet — add at least one address, or continue with none to approve without
                                                    email.
                                                </p>
                                            ) : (
                                                <div className="flex flex-wrap gap-2">
                                                    {approveRecipients.map((email) => (
                                                        <span key={email} className={recipientChipClass}>
                                                            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/45 dark:text-blue-300">
                                                                <Icon icon={FiMail} className="h-3.5 w-3.5" />
                                                            </span>
                                                            <span className={recipientChipTextClass} title={email}>
                                                                {email}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeApproveEmailRecipient(email)}
                                                                className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/35 dark:hover:text-red-300"
                                                                aria-label={`Remove ${email}`}
                                                            >
                                                                <Icon icon={FiX} className="h-3.5 w-3.5" />
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className="mt-3">
                                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                            Add recipient
                                        </label>
                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                                            <TextField
                                                type="email"
                                                className="min-w-0 flex-1"
                                                placeholder="email@example.com (commas for several)"
                                                value={approveEmailDraft}
                                                onChange={(e) => setApproveEmailDraft(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        addApproveEmailRecipient();
                                                    }
                                                }}
                                                disabled={approveLoadingRecipients}
                                            />
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                className="shrink-0 sm:self-start"
                                                onClick={addApproveEmailRecipient}
                                                disabled={approveLoadingRecipients}
                                            >
                                                Add
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {showRejectEmailStep && pendingRejectRequest && (
                                <div className="rounded-xl border border-red-200/90 bg-gradient-to-b from-red-50/90 via-white to-white p-4 shadow-sm dark:border-red-900/40 dark:from-red-950/30 dark:via-gray-950 dark:to-gray-950">
                                    <div className="mb-3">
                                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                            Rejection notification
                                        </h3>
                                        <p className="mt-0.5 max-w-xl text-xs leading-relaxed text-gray-600 dark:text-gray-400">
                                            Recipients below receive the rejection email. Add or remove addresses, then confirm to send and
                                            mark this request as rejected.
                                        </p>
                                    </div>
                                    <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                        Recipients
                                    </label>
                                    {rejectLoadingRecipients ? (
                                        <div className="flex min-h-[5.5rem] items-center justify-center rounded-xl border border-dashed border-red-200/80 bg-red-50/40 text-sm text-gray-500 dark:border-red-900/45 dark:bg-red-950/20 dark:text-gray-400">
                                            Loading suggested recipients…
                                        </div>
                                    ) : (
                                        <div className="min-h-[5.5rem] rounded-xl border border-gray-200/90 bg-gradient-to-b from-slate-50/90 to-white p-3 shadow-inner dark:border-gray-600 dark:from-gray-900/70 dark:to-gray-900/45">
                                            {rejectRecipients.length === 0 ? (
                                                <p className="py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                                                    No recipients yet — add addresses to notify by email, or continue with none to reject
                                                    without email.
                                                </p>
                                            ) : (
                                                <div className="flex flex-wrap gap-2">
                                                    {rejectRecipients.map((email) => (
                                                        <span key={email} className={recipientChipClass}>
                                                            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                                                                <Icon icon={FiMail} className="h-3.5 w-3.5" />
                                                            </span>
                                                            <span className={recipientChipTextClass} title={email}>
                                                                {email}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeRejectEmailRecipient(email)}
                                                                className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/35 dark:hover:text-red-300"
                                                                aria-label={`Remove ${email}`}
                                                            >
                                                                <Icon icon={FiX} className="h-3.5 w-3.5" />
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className="mt-3">
                                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                            Add recipient
                                        </label>
                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                                            <TextField
                                                type="email"
                                                className="min-w-0 flex-1"
                                                placeholder="email@example.com (commas for several)"
                                                value={rejectEmailDraft}
                                                onChange={(e) => setRejectEmailDraft(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        addRejectEmailRecipient();
                                                    }
                                                }}
                                                disabled={rejectLoadingRecipients}
                                            />
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                className="shrink-0 sm:self-start"
                                                onClick={addRejectEmailRecipient}
                                                disabled={rejectLoadingRecipients}
                                            >
                                                Add
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>
                </Modal>
            )}

            {showStatusEmailModal && statusEmailRequest && (
                <Modal
                    isOpen={showStatusEmailModal}
                    onClose={closeStatusEmailModal}
                    title={`Send email — ${statusEmailRequest.requestId || statusEmailRequest.id}`}
                    size="lg"
                    closeOnBackdropClick={false}
                >
                    <div className="space-y-4 p-6">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Status:{' '}
                            <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {(statusEmailRequest.status || '').toLowerCase() === 'approved'
                                    ? 'Approved'
                                    : 'Rejected'}
                            </span>
                            . Add or remove recipients, then send the notification.
                        </p>

                        <div>
                            <label className="mb-2 block text-sm font-semibold text-gray-800 dark:text-gray-200">
                                Recipients
                            </label>
                            {statusEmailLoadingDefaults ? (
                                <div className="flex min-h-[5.5rem] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-400">
                                    Loading suggested recipients…
                                </div>
                            ) : (
                                <div className="min-h-[5.5rem] rounded-xl border border-gray-200 bg-gradient-to-b from-slate-50 to-white p-3 shadow-inner dark:border-gray-600 dark:from-gray-900/70 dark:to-gray-900/40">
                                    {statusEmailRecipients.length === 0 ? (
                                        <p className="py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                                            No recipients yet — add addresses below.
                                        </p>
                                    ) : (
                                        <div className="flex flex-wrap gap-2">
                                            {statusEmailRecipients.map((email) => (
                                                <span key={email} className={recipientChipClass}>
                                                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/45 dark:text-blue-300">
                                                        <Icon icon={FiMail} className="h-3.5 w-3.5" />
                                                    </span>
                                                    <span className={recipientChipTextClass} title={email}>
                                                        {email}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeStatusEmailRecipient(email)}
                                                        className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/35 dark:hover:text-red-300"
                                                        aria-label={`Remove ${email}`}
                                                    >
                                                        <Icon icon={FiX} className="h-3.5 w-3.5" />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                Add recipient
                            </label>
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                                <TextField
                                    type="email"
                                    className="min-w-0 flex-1"
                                    placeholder="email@example.com (use commas for several)"
                                    value={statusEmailDraft}
                                    onChange={(e) => setStatusEmailDraft(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            addStatusEmailRecipient();
                                        }
                                    }}
                                />
                                <Button
                                    type="button"
                                    variant="secondary"
                                    className="shrink-0 sm:self-start"
                                    onClick={addStatusEmailRecipient}
                                    disabled={statusEmailLoadingDefaults}
                                >
                                    Add
                                </Button>
                            </div>
                        </div>

                        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <Button variant="secondary" onClick={closeStatusEmailModal} disabled={statusEmailSending}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => void sendStatusNotificationEmail()}
                                disabled={statusEmailSending || statusEmailLoadingDefaults}
                            >
                                {statusEmailSending ? 'Sending…' : 'Send email'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Add Request Form Modal */}
            {showAddModal && (
                <Modal
                    isOpen={showAddModal}
                    onClose={() => {
                        setShowAddModal(false);
                    }}
                    title={isEditing ? "Edit Request Form" : "Request Form"}
                    size="xl"
                >
                    <div className="p-6">
                        {formData.requestId && (
                            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
                                <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                                    <strong>Request ID:</strong> {formData.requestId}
                                </p>
                            </div>
                        )}

                        <div className="space-y-4">
                            {/* Request Type */}
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Request Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={formData.requestType}
                                    onChange={(e) => {
                                        const newType = e.target.value;
                                        setFormData({
                                            ...formData,
                                            requestType: newType,
                                            company: '',
                                            selectedCompanyId: '',
                                        });
                                        if (selectedBranch && newType) {
                                            void autoFetchCompanyForBranch(selectedBranch, newType);
                                        }
                                    }}
                                    className="form-control"
                                >
                                    <option value="" className="bg-white text-gray-500 dark:bg-gray-800 dark:text-gray-400">Select Request Type</option>
                                    {requestTypeOptions.map((type) => (
                                        <option key={type} value={type} className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">{type}</option>
                                    ))}
                                </select>
                            </div>

                            {formData.requestType && (
                                <>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        {/* Branch Code */}
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Branch Code <span className="text-red-500">*</span>
                                            </label>
                                            <TextField
                                                type="text"
                                                value={formData.branchCode}
                                                placeholder="Click to select branch"
                                                readOnly
                                                onClick={() => setShowBranchModal(true)}
                                                className="cursor-pointer"
                                            />
                                        </div>

                                        {/* Branch Name */}
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Branch Name
                                            </label>
                                            <TextField
                                                type="text"
                                                value={formData.branchName}
                                                placeholder="Auto-filled from branch selection"
                                                readOnly
                                            />
                                        </div>

                                        {formData.requestType === 'Guard' && hasBranchPicked && recentGuardRequestsForBranch.length > 0 && (
                                            <div className="md:col-span-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/25 dark:text-amber-200">
                                                <div className="font-semibold">Guard requests in last 30 days (this branch)</div>
                                                <ul className="mt-2 space-y-1">
                                                    {recentGuardRequestsForBranch.slice(0, 6).map((r: any) => {
                                                        const df = getDateFields(r as any).dateFrom;
                                                        const dt = getDateFields(r as any).dateTo;
                                                        return (
                                                            <li key={String(r.id || r.requestId)} className="flex flex-wrap gap-x-2 gap-y-0.5">
                                                                <span className="font-medium">{String(r.requestId || '—')}</span>
                                                                <span className="text-amber-800/90 dark:text-amber-200/90">
                                                                    {df ? String(df) : '—'} → {dt ? String(dt) : '—'}
                                                                </span>
                                                                <span className="text-xs text-amber-700/80 dark:text-amber-200/70">
                                                                    (these dates will be disabled)
                                                                </span>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        )}

                                        {/* Required By - Hidden for Guard */}
                                        {formData.requestType !== 'Guard' && (
                                            <div>
                                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                    Required By <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    value={formData.requiredBy}
                                                    onChange={(e) => setFormData({ ...formData, requiredBy: e.target.value })}
                                                    className="form-control"
                                                >
                                                    <option value="" className="bg-white text-gray-500 dark:bg-gray-800 dark:text-gray-400">Select Required By</option>
                                                    {requiredByOptions.map((option) => (
                                                        <option key={option} value={option} className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">{option}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {/* Company */}
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Company <span className="text-red-500">*</span>
                                            </label>
                                            <TextField
                                                type="text"
                                                value={fetchingCompany ? '' : formData.company}
                                                placeholder={fetchingCompany ? 'Fetching company...' : `Click to select ${formData.requestType} company`}
                                                readOnly
                                                onClick={() => {
                                                    if (fetchingCompany) return;
                                                    loadAvailableCompanies();
                                                    setShowCompanyModal(true);
                                                }}
                                                className={fetchingCompany ? 'cursor-wait opacity-70' : 'cursor-pointer'}
                                            />
                                            {fetchingCompany && (
                                                <p className="mt-1 text-xs text-blue-500 dark:text-blue-400 animate-pulse">
                                                    🔍 Fetching company for selected branch...
                                                </p>
                                            )}
                                        </div>

                                        {/* Number of Days (Read-only) */}
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Number of Days
                                            </label>
                                            <TextField
                                                type="text"
                                                value={formData.requestType === 'CCTV' ? (formData.cctvDateRanges && formData.cctvDateRanges.length > 0 ? formData.cctvDateRanges.reduce((acc: number, r: any) => acc + inclusiveDaysBetween(r.dateFrom, r.dateTo), 0) : '-') : (formData.cctvDateFrom && formData.cctvDateTo ? inclusiveDaysBetween(formData.cctvDateFrom, formData.cctvDateTo) : '-')}
                                                readOnly
                                                disabled
                                            />
                                        </div>

                                        {/* Date Range Selection */}
                                        <div className="md:col-span-2">
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                {formData.requestType === 'Guard' ? 'Date Range (Max 2 Days)' : formData.requestType === 'CCTV' ? 'Date Ranges' : 'Date Range'} <span className="text-red-500">*</span>
                                            </label>

                                            {formData.requestType === 'CCTV' ? (
                                                <div className="space-y-3">
                                                    {formData.cctvDateRanges && formData.cctvDateRanges.length > 0 ? (
                                                        <div className="space-y-2">
                                                            {formData.cctvDateRanges.map((range: any, index: number) => (
                                                                <div key={index} className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50/50 p-3 text-xs dark:border-gray-750 dark:bg-gray-800/50">
                                                                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                                                                        <div className="flex items-center gap-1.5">
                                                                            <Icon icon={FiCalendar} className="text-blue-500" />
                                                                            <span className="font-semibold text-gray-900 dark:text-gray-100">
                                                                                {range.dateFrom} &rarr; {range.dateTo}
                                                                            </span>
                                                                        </div>
                                                                        {range.timeFrom && range.timeTo && (
                                                                            <span className="text-gray-650 dark:text-gray-300 font-medium">
                                                                                ({range.timeFrom} - {range.timeTo})
                                                                            </span>
                                                                        )}
                                                                        <span className="rounded-full bg-blue-50 px-2 py-0.5 font-bold text-blue-700 dark:bg-blue-950/20 dark:text-blue-400">
                                                                            {inclusiveDaysBetween(range.dateFrom, range.dateTo)} days
                                                                        </span>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            const newList = formData.cctvDateRanges.filter((_, i) => i !== index);
                                                                            const startDates = newList.map((r: any) => r.dateFrom).filter(Boolean);
                                                                            const endDates = newList.map((r: any) => r.dateTo).filter(Boolean);
                                                                            const minDate = startDates.length > 0 ? startDates.reduce((min: string, d: string) => d < min ? d : min, startDates[0]) : '';
                                                                            const maxDate = endDates.length > 0 ? endDates.reduce((max: string, d: string) => d > max ? d : max, endDates[0]) : '';
                                                                            setFormData({
                                                                                ...formData,
                                                                                cctvDateRanges: newList,
                                                                                cctvDateFrom: minDate,
                                                                                cctvDateTo: maxDate,
                                                                                timeFrom: newList.length > 0 ? newList[0].timeFrom : '',
                                                                                timeTo: newList.length > 0 ? newList[0].timeTo : '',
                                                                            });
                                                                        }}
                                                                        className="rounded-lg p-1.5 text-red-650 hover:bg-red-50 dark:hover:bg-red-950/20 transition cursor-pointer"
                                                                        title="Remove Date Range"
                                                                    >
                                                                        <Icon icon={FiTrash2} className="h-4 w-4" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="text-xs text-gray-550 dark:text-gray-400">No date ranges added yet.</p>
                                                    )}

                                                    <div className="flex flex-col gap-2 p-3 border border-dashed border-gray-200 rounded-xl dark:border-gray-750">
                                                        <div className="w-full">
                                                            {/* Desktop Date Range Picker */}
                                                            <div className="hidden md:block w-full">
                                                                <DatePicker.RangePicker
                                                                    className="w-full"
                                                                    format="YYYY-MM-DD"
                                                                    allowClear
                                                                    disabled={!hasBranchPicked}
                                                                    placeholder={['Date From', 'Date To']}
                                                                    suffixIcon={<Icon icon={FiCalendar} className="text-gray-500 dark:text-gray-300" />}
                                                                    value={tempDateRange}
                                                                    onChange={(dates) => {
                                                                        setTempDateRange(dates);
                                                                    }}
                                                                />
                                                            </div>
                                                            {/* Mobile Date Range Pickers (Stacked) */}
                                                            <div className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
                                                                <DatePicker
                                                                    className="w-full"
                                                                    format="YYYY-MM-DD"
                                                                    allowClear
                                                                    disabled={!hasBranchPicked}
                                                                    placeholder="Date From"
                                                                    suffixIcon={<Icon icon={FiCalendar} className="text-gray-500 dark:text-gray-300" />}
                                                                    value={tempDateRange && tempDateRange[0] ? tempDateRange[0] : null}
                                                                    onChange={(date) => {
                                                                        const start = date;
                                                                        const end = tempDateRange ? tempDateRange[1] : null;
                                                                        setTempDateRange(start || end ? [start, end] : null);
                                                                    }}
                                                                />
                                                                <DatePicker
                                                                    className="w-full"
                                                                    format="YYYY-MM-DD"
                                                                    allowClear
                                                                    disabled={!hasBranchPicked}
                                                                    placeholder="Date To"
                                                                    suffixIcon={<Icon icon={FiCalendar} className="text-gray-500 dark:text-gray-300" />}
                                                                    value={tempDateRange && tempDateRange[1] ? tempDateRange[1] : null}
                                                                    onChange={(date) => {
                                                                        const start = tempDateRange ? tempDateRange[0] : null;
                                                                        const end = date;
                                                                        setTempDateRange(start || end ? [start, end] : null);
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4 items-end">
                                                            <div>
                                                                <label className="mb-0.5 block text-xs font-semibold text-gray-650 dark:text-gray-300">Time From</label>
                                                                <TimePicker
                                                                    className="w-full"
                                                                    format="hh:mm A"
                                                                    use12Hours
                                                                    allowClear
                                                                    disabled={!hasBranchPicked}
                                                                    placeholder="Select Time"
                                                                    value={tempTimeFrom ? dayjs(tempTimeFrom, 'hh:mm A') : null}
                                                                    onChange={(time) => setTempTimeFrom(time ? time.format('hh:mm A') : '')}
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="mb-0.5 block text-xs font-semibold text-gray-650 dark:text-gray-300">Time To</label>
                                                                <TimePicker
                                                                    className="w-full"
                                                                    format="hh:mm A"
                                                                    use12Hours
                                                                    allowClear
                                                                    disabled={!hasBranchPicked}
                                                                    placeholder="Select Time"
                                                                    value={tempTimeTo ? dayjs(tempTimeTo, 'hh:mm A') : null}
                                                                    onChange={(time) => setTempTimeTo(time ? time.format('hh:mm A') : '')}
                                                                />
                                                            </div>
                                                            <div className="col-span-2 md:col-span-1 w-full">
                                                                <Button
                                                                    type="button"
                                                                    variant="secondary"
                                                                    className="w-full justify-center"
                                                                    disabled={!tempDateRange || !tempDateRange[0] || !tempDateRange[1] || !tempTimeFrom || !tempTimeTo}
                                                                    onClick={() => {
                                                                        if (tempDateRange && tempDateRange[0] && tempDateRange[1] && tempTimeFrom && tempTimeTo) {
                                                                            const fromStr = tempDateRange[0].format('YYYY-MM-DD');
                                                                            const toStr = tempDateRange[1].format('YYYY-MM-DD');

                                                                            // Check duplicate
                                                                            const duplicate = formData.cctvDateRanges.some(
                                                                                (r: any) => r.dateFrom === fromStr && r.dateTo === toStr && r.timeFrom === tempTimeFrom && r.timeTo === tempTimeTo
                                                                            );
                                                                            if (duplicate) {
                                                                                setToastMessage('This date and time range has already been added.');
                                                                                setToastType('warning');
                                                                                setShowToast(true);
                                                                                return;
                                                                            }

                                                                            const newList = [...formData.cctvDateRanges, { dateFrom: fromStr, dateTo: toStr, timeFrom: tempTimeFrom, timeTo: tempTimeTo }];
                                                                            const startDates = newList.map((r: any) => r.dateFrom).filter(Boolean);
                                                                            const endDates = newList.map((r: any) => r.dateTo).filter(Boolean);
                                                                            const minDate = startDates.length > 0 ? startDates.reduce((min: string, d: string) => d < min ? d : min, startDates[0]) : '';
                                                                            const maxDate = endDates.length > 0 ? endDates.reduce((max: string, d: string) => d > max ? d : max, endDates[0]) : '';
                                                                            setFormData({
                                                                                ...formData,
                                                                                cctvDateRanges: newList,
                                                                                cctvDateFrom: minDate,
                                                                                cctvDateTo: maxDate,
                                                                                timeFrom: newList[0].timeFrom,
                                                                                timeTo: newList[0].timeTo,
                                                                            });
                                                                            setTempDateRange(null);
                                                                            setTempTimeFrom('');
                                                                            setTempTimeTo('');
                                                                        }
                                                                    }}
                                                                >
                                                                    Add Range
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    {/* Desktop Date Range Picker */}
                                                    <div className="hidden md:block">
                                                        <DatePicker.RangePicker
                                                            className="w-full"
                                                            format="YYYY-MM-DD"
                                                            allowClear
                                                            disabled={!hasBranchPicked}
                                                            placeholder={['Date From', 'Date To']}
                                                            suffixIcon={<Icon icon={FiCalendar} className="text-gray-500 dark:text-gray-300" />}
                                                            value={
                                                                formData.cctvDateFrom && formData.cctvDateTo
                                                                    ? [dayjs(formData.cctvDateFrom), dayjs(formData.cctvDateTo)]
                                                                    : formData.cctvDateFrom
                                                                        ? [dayjs(formData.cctvDateFrom), null]
                                                                        : null
                                                            }
                                                            onCalendarChange={(dates) => {
                                                                if (dates && dates[0]) {
                                                                    setActiveStartDate(dates[0]);
                                                                } else {
                                                                    setActiveStartDate(null);
                                                                }
                                                            }}
                                                            onOpenChange={(open) => {
                                                                if (!open) {
                                                                    setActiveStartDate(null);
                                                                }
                                                            }}
                                                            disabledDate={(current: Dayjs) => {
                                                                if (!current) return false;

                                                                // 1. If it's a Guard request, apply recent Guard request checks (isCoveredByRecentGuard)
                                                                if (formData.requestType === 'Guard' && hasBranchPicked) {
                                                                    if (isCoveredByRecentGuard(current)) return true;
                                                                }

                                                                // 2. Restrict to max 2 days based on activeStartDate (if set)
                                                                if (formData.requestType === 'Guard' && activeStartDate) {
                                                                    const diff = Math.abs(current.diff(activeStartDate, 'day'));
                                                                    return diff > 1; // More than 1 day difference (i.e. more than 2 days span) is disabled
                                                                }
                                                                return false;
                                                            }}
                                                            onChange={(dates) => {
                                                                setActiveStartDate(null);
                                                                const fromStr = dates && dates[0] ? dates[0].format('YYYY-MM-DD') : '';
                                                                const toStr = dates && dates[1] ? dates[1].format('YYYY-MM-DD') : '';

                                                                setFormData({
                                                                    ...formData,
                                                                    cctvDateFrom: fromStr,
                                                                    cctvDateTo: toStr,
                                                                });

                                                                // Trigger Guard specific warning/reason modals if needed
                                                                if (formData.requestType === 'Guard') {
                                                                    if (fromStr && toStr) {
                                                                        if (recentGuardRequestsForBranch.length > 0 && !guardRepeatReason.trim()) {
                                                                            setGuardRepeatReasonDraft('');
                                                                            setShowGuardRepeatReasonModal(true);
                                                                            return;
                                                                        }
                                                                        const spanDays = inclusiveDaysBetween(fromStr, toStr);
                                                                        if (spanDays > 2 && !guardRepeatReason.trim() && !guardDateRangeReason.trim()) {
                                                                            setGuardRangeReasonDraft('');
                                                                            setShowGuardRangeReasonModal(true);
                                                                        }
                                                                    } else {
                                                                        setGuardDateRangeReason('');
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    {/* Mobile Date Range Pickers (Stacked/Grid) */}
                                                    <div className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="mb-0.5 block text-xs font-semibold text-gray-550 dark:text-gray-300">Date From</label>
                                                            <DatePicker
                                                                className="w-full"
                                                                format="YYYY-MM-DD"
                                                                allowClear
                                                                disabled={!hasBranchPicked}
                                                                placeholder="Select Date From"
                                                                suffixIcon={<Icon icon={FiCalendar} className="text-gray-500 dark:text-gray-300" />}
                                                                value={formData.cctvDateFrom ? dayjs(formData.cctvDateFrom) : null}
                                                                disabledDate={(current: Dayjs) => {
                                                                    if (!current) return false;
                                                                    if (formData.requestType === 'Guard' && hasBranchPicked) {
                                                                        if (isCoveredByRecentGuard(current)) return true;
                                                                    }
                                                                    return false;
                                                                }}
                                                                onChange={(date) => {
                                                                    const fromStr = date ? date.format('YYYY-MM-DD') : '';
                                                                    let toStr = formData.cctvDateTo || '';
                                                                    
                                                                    if (fromStr && toStr) {
                                                                        const start = dayjs(fromStr);
                                                                        const end = dayjs(toStr);
                                                                        const diff = end.diff(start, 'day');
                                                                        if (diff < 0 || diff > 1) {
                                                                            toStr = '';
                                                                        }
                                                                    }
                                                                    
                                                                    setFormData({
                                                                        ...formData,
                                                                        cctvDateFrom: fromStr,
                                                                        cctvDateTo: toStr,
                                                                    });
                                                                    
                                                                    if (formData.requestType === 'Guard') {
                                                                        if (fromStr && toStr) {
                                                                            if (recentGuardRequestsForBranch.length > 0 && !guardRepeatReason.trim()) {
                                                                                setGuardRepeatReasonDraft('');
                                                                                setShowGuardRepeatReasonModal(true);
                                                                                return;
                                                                            }
                                                                            const spanDays = inclusiveDaysBetween(fromStr, toStr);
                                                                            if (spanDays > 2 && !guardRepeatReason.trim() && !guardDateRangeReason.trim()) {
                                                                                setGuardRangeReasonDraft('');
                                                                                setShowGuardRangeReasonModal(true);
                                                                            }
                                                                        } else {
                                                                            setGuardDateRangeReason('');
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="mb-0.5 block text-xs font-semibold text-gray-550 dark:text-gray-300">Date To</label>
                                                            <DatePicker
                                                                className="w-full"
                                                                format="YYYY-MM-DD"
                                                                allowClear
                                                                disabled={!hasBranchPicked || !formData.cctvDateFrom}
                                                                placeholder="Select Date To"
                                                                suffixIcon={<Icon icon={FiCalendar} className="text-gray-500 dark:text-gray-300" />}
                                                                value={formData.cctvDateTo ? dayjs(formData.cctvDateTo) : null}
                                                                disabledDate={(current: Dayjs) => {
                                                                    if (!current) return false;
                                                                    if (formData.requestType === 'Guard' && hasBranchPicked) {
                                                                        if (isCoveredByRecentGuard(current)) return true;
                                                                    }
                                                                    if (formData.requestType === 'Guard' && formData.cctvDateFrom) {
                                                                        const start = dayjs(formData.cctvDateFrom);
                                                                        const diff = current.diff(start, 'day');
                                                                        return diff < 0 || diff > 1;
                                                                    }
                                                                    return false;
                                                                }}
                                                                onChange={(date) => {
                                                                    const toStr = date ? date.format('YYYY-MM-DD') : '';
                                                                    const fromStr = formData.cctvDateFrom || '';
                                                                    
                                                                    setFormData({
                                                                        ...formData,
                                                                        cctvDateTo: toStr,
                                                                    });
                                                                    
                                                                    if (formData.requestType === 'Guard') {
                                                                        if (fromStr && toStr) {
                                                                            if (recentGuardRequestsForBranch.length > 0 && !guardRepeatReason.trim()) {
                                                                                setGuardRepeatReasonDraft('');
                                                                                setShowGuardRepeatReasonModal(true);
                                                                                return;
                                                                            }
                                                                            const spanDays = inclusiveDaysBetween(fromStr, toStr);
                                                                            if (spanDays > 2 && !guardRepeatReason.trim() && !guardDateRangeReason.trim()) {
                                                                                setGuardRangeReasonDraft('');
                                                                                setShowGuardRangeReasonModal(true);
                                                                            }
                                                                        } else {
                                                                            setGuardDateRangeReason('');
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                            {!hasBranchPicked && (
                                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                    Select a branch first to choose dates.
                                                </p>
                                            )}
                                        </div>

                                        {formData.requestType !== 'Guard' && formData.requestType !== 'CCTV' && (
                                            <div className="md:col-span-2 grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                        Time From <span className="text-red-500">*</span>
                                                    </label>
                                                    <TimePicker
                                                        className="w-full"
                                                        format="hh:mm A"
                                                        use12Hours
                                                        allowClear
                                                        disabled={!hasBranchPicked}
                                                        placeholder="Select Time"
                                                        value={formData.timeFrom ? dayjs(formData.timeFrom, 'hh:mm A') : null}
                                                        onChange={(time) => setFormData({ ...formData, timeFrom: time ? time.format('hh:mm A') : '' })}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                        Time To <span className="text-red-500">*</span>
                                                    </label>
                                                    <TimePicker
                                                        className="w-full"
                                                        format="hh:mm A"
                                                        use12Hours
                                                        allowClear
                                                        disabled={!hasBranchPicked}
                                                        placeholder="Select Time"
                                                        value={formData.timeTo ? dayjs(formData.timeTo, 'hh:mm A') : null}
                                                        onChange={(time) => setFormData({ ...formData, timeTo: time ? time.format('hh:mm A') : '' })}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Number of Guards & Purpose - Same line grid for Guard */}
                                    {formData.requestType === 'Guard' ? (
                                        <div className="grid grid-cols-2 gap-4">
                                            {/* Number of Guards */}
                                            <div>
                                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                    Number of Guards <span className="text-red-500">*</span>
                                                </label>
                                                <TextField
                                                    type="number"
                                                    min="1"
                                                    value={formData.numberOfGuards}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        if (val !== '' && parseInt(val, 10) < 1) {
                                                            setFormData({ ...formData, numberOfGuards: '1' });
                                                        } else {
                                                            setFormData({ ...formData, numberOfGuards: val });
                                                        }
                                                    }}
                                                    placeholder="Enter number of guards"
                                                />
                                            </div>

                                            {/* Purpose */}
                                            <div>
                                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                    Purpose <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    value={formData.purpose}
                                                    onChange={(e) => setFormData({ ...formData, purpose: e.target.value, purposeOther: '' })}
                                                    className="form-control"
                                                >
                                                    <option value="" className="bg-white text-gray-500 dark:bg-gray-800 dark:text-gray-400">Select Purpose</option>
                                                    {purposeOptionsGuard.map((purpose) => (
                                                        <option key={purpose} value={purpose} className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">{purpose}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    ) : (
                                        /* Purpose - Full width for BAMS/CCTV */
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Purpose <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={formData.purpose}
                                                onChange={(e) => setFormData({ ...formData, purpose: e.target.value, purposeOther: '' })}
                                                className="form-control"
                                            >
                                                <option value="" className="bg-white text-gray-500 dark:bg-gray-800 dark:text-gray-400">Select Purpose</option>
                                                {purposeOptionsCCTV.map((purpose) => (
                                                    <option key={purpose} value={purpose} className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">{purpose}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {/* Purpose Other / Police Pressure Reason */}
                                    {(formData.purpose === 'Other' || formData.purpose === 'Police Pressure') && (
                                        <div className="mt-3">
                                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                {formData.purpose === 'Police Pressure' ? 'Reason for Police Pressure' : 'Specify Purpose'} <span className="text-red-500">*</span>
                                            </label>
                                            <TextField
                                                type="text"
                                                value={formData.purposeOther}
                                                onChange={(e) => setFormData({ ...formData, purposeOther: e.target.value })}
                                                placeholder={formData.purpose === 'Police Pressure' ? 'Please specify the reason for Police Pressure...' : (formData.requestType === 'Guard' ? 'Please specify the reason...' : 'Please specify the purpose...')}
                                                className="mt-1"
                                            />
                                        </div>
                                    )}

                                    {/* Submit Button */}
                                    <div className="flex justify-end gap-3 pt-4">
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={() => {
                                                if (window.confirm("Are you sure you want to reset all form fields?")) {
                                                    resetForm();
                                                }
                                            }}
                                            disabled={saving}
                                            className="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 mr-auto"
                                        >
                                            <Icon icon={FiRotateCcw} className="mr-1.5 h-4 w-4" />
                                            Reset
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={() => {
                                                setShowAddModal(false);
                                            }}
                                            disabled={saving}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={() => handleSaveRequest()}
                                            disabled={saving}
                                        >
                                            {saving ? (
                                                <>
                                                    <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></div>
                                                    {isBankUser && isEditing ? 'Saving...' : 'Submitting...'}
                                                </>
                                            ) : (
                                                isBankUser && isEditing ? 'Save' : 'Submit Request'
                                            )}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Guard Date Range Reason Modal */}
            {showGuardRangeReasonModal && (
                <Modal
                    isOpen={showGuardRangeReasonModal}
                    onClose={() => {
                        setShowGuardRangeReasonModal(false);
                        setGuardRangeReasonDraft('');
                        setFormData((prev) => ({ ...prev, cctvDateTo: '' }));
                    }}
                    title="Reason required (Guard)"
                    size="md"
                >
                    <div className="p-6 space-y-4">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                            You selected more than <span className="font-semibold">2 days</span> for a Guard request. Please provide a reason to continue.
                        </p>
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Reason <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={guardRangeReasonDraft}
                                onChange={(e) => setGuardRangeReasonDraft(e.target.value)}
                                rows={4}
                                className="w-full rounded-xl border border-gray-300 bg-white p-3 text-sm text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                placeholder="Write the reason..."
                            />
                        </div>
                        <div className="flex justify-end gap-3">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowGuardRangeReasonModal(false);
                                    setGuardRangeReasonDraft('');
                                    setFormData((prev) => ({ ...prev, cctvDateTo: '' }));
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    const v = guardRangeReasonDraft.trim();
                                    if (!v) {
                                        setToastMessage('Reason is required');
                                        setToastType('error');
                                        setShowToast(true);
                                        return;
                                    }
                                    setGuardDateRangeReason(v);
                                    setShowGuardRangeReasonModal(false);
                                }}
                            >
                                Save Reason
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Guard Repeat (30 days) Reason Modal */}
            {showGuardRepeatReasonModal && (
                <Modal
                    isOpen={showGuardRepeatReasonModal}
                    onClose={() => {
                        setShowGuardRepeatReasonModal(false);
                        setGuardRepeatReasonDraft('');
                        setFormData((prev) => ({ ...prev, cctvDateTo: '' }));
                    }}
                    title="Warning: recent Guard request"
                    size="md"
                >
                    <div className="p-6 space-y-4">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                            A Guard request for this branch was created in the last <span className="font-semibold">30 days</span>.
                            Please provide a reason to submit another temporary guard request.
                        </p>
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Reason <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={guardRepeatReasonDraft}
                                onChange={(e) => setGuardRepeatReasonDraft(e.target.value)}
                                rows={4}
                                className="w-full rounded-xl border border-gray-300 bg-white p-3 text-sm text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                placeholder="Write the reason..."
                            />
                        </div>
                        <div className="flex justify-end gap-3">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowGuardRepeatReasonModal(false);
                                    setGuardRepeatReasonDraft('');
                                    setFormData((prev) => ({ ...prev, cctvDateTo: '' }));
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    const v = guardRepeatReasonDraft.trim();
                                    if (!v) {
                                        setToastMessage('Reason is required');
                                        setToastType('error');
                                        setShowToast(true);
                                        return;
                                    }
                                    setGuardRepeatReason(v);
                                    setShowGuardRepeatReasonModal(false);
                                }}
                            >
                                Save Reason
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Rate Setting Modal */}
            {showRateModal && (
                <Modal
                    isOpen={showRateModal}
                    onClose={() => setShowRateModal(false)}
                    title="Set Guard Daily Rate"
                    size="md"
                >
                    <div className="p-6">
                        <div className="mb-4">
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Current Daily Rate (PKR)
                            </label>
                            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
                                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                    {guardRate ? guardRate.toLocaleString() : 'Not Set'}
                                </p>
                            </div>
                        </div>
                        <div className="mb-6">
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                New Daily Rate (PKR)
                            </label>
                            <TextField
                                type="number"
                                placeholder="Enter daily rate"
                                value={newRate}
                                onChange={(e) => setNewRate(e.target.value)}
                            />
                            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                Note: Updating the rate will only affect new requests. Existing requests will keep their historical rate.
                            </p>
                        </div>
                        <div className="flex justify-end gap-3">
                            <Button
                                variant="secondary"
                                onClick={() => setShowRateModal(false)}
                                disabled={settingRate}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleUpdateGuardRate}
                                disabled={settingRate}
                            >
                                {settingRate ? 'Saving...' : 'Save Rate'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Branch Selection Modal */}
            {showBranchModal && (
                <Modal
                    isOpen={showBranchModal}
                    onClose={() => setShowBranchModal(false)}
                    title="Select Branch"
                    size="md"
                >
                    <div className="p-6">
                        <div className="mb-4">
                            <TextField
                                type="text"
                                placeholder="Search branch by code or address"
                                value={branchSearch}
                                onChange={(e) => setBranchSearch(e.target.value)}
                                prefix={<Icon icon={FiSearch} />}
                            />
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                            {filteredBranches.length === 0 ? (
                                <p className="py-4 text-center text-gray-500 dark:text-gray-400">No branches found</p>
                            ) : (
                                <div className="space-y-2">
                                    {filteredBranches.map((branch) => (
                                        <button
                                            key={branch.id}
                                            type="button"
                                            onClick={() => handleBranchSelection(branch)}
                                            className="w-full cursor-pointer rounded-lg border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:border-blue-300 hover:bg-blue-50/50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-blue-500/40 dark:hover:bg-gray-700"
                                        >
                                            <div className="font-semibold text-gray-900 dark:text-gray-100">{branch.branchCode}</div>
                                            <div className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                                                {(branch.branchName || branch.name || 'N/A').toString()}
                                            </div>
                                            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                {branch.address || 'No address available'}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Company Selection Modal */}
            {showCompanyModal && (
                <Modal
                    isOpen={showCompanyModal}
                    onClose={() => setShowCompanyModal(false)}
                    title={`Select ${formData.requestType} Company`}
                    size="md"
                >
                    <div className="p-6">
                        <div className="mb-4">
                            <TextField
                                type="text"
                                placeholder="Search company by name or ID"
                                value={companySearch}
                                onChange={(e) => setCompanySearch(e.target.value)}
                                prefix={<Icon icon={FiSearch} />}
                            />
                        </div>
                        <div className="mb-3">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Found {availableCompanies.length} companies for {formData.requestType} requests
                            </p>
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                            {availableCompanies.length === 0 ? (
                                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                                    <p className="text-sm text-blue-900 dark:text-blue-100">
                                        No {formData.requestType} companies found. Please add companies first.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {availableCompanies
                                        .filter(company =>
                                            company.companyName?.toLowerCase().includes(companySearch.toLowerCase()) ||
                                            company.companyId?.toLowerCase().includes(companySearch.toLowerCase())
                                        )
                                        .map((company) => (
                                            <button
                                                key={company.id}
                                                type="button"
                                                onClick={() => handleCompanySelection(company)}
                                                className="w-full rounded-lg border border-gray-200 bg-white p-4 text-left transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
                                            >
                                                <div className="font-semibold text-gray-900 dark:text-gray-100">{company.companyName}</div>
                                                <div className="mt-1 space-y-1 text-sm text-gray-500 dark:text-gray-400">
                                                    <div>ID: {company.companyId}</div>
                                                    <div>Type: {company.companyType}</div>
                                                    {company.companyAddress && <div>Address: {company.companyAddress}</div>}
                                                    {company.email && <div>Email: {company.email}</div>}
                                                </div>
                                            </button>
                                        ))}
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>
            )}
            {exportProgress.active && (
                <div className="pointer-events-auto fixed bottom-4 left-4 z-[100] w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 shadow-2xl shadow-slate-900/10 backdrop-blur-md dark:border-slate-600 dark:bg-slate-900/95">
                    <div className="border-b border-slate-100 bg-gradient-to-r from-slate-800 to-slate-700 px-4 py-3 dark:from-slate-900 dark:to-slate-800">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 text-white">
                                <Icon icon={FiDownload} className="h-4 w-4 opacity-90" />
                                <span className="text-sm font-semibold tracking-tight">
                                    {exportProgress.format === 'excel' ? 'Exporting Excel' : 'Exporting PDF'}
                                </span>
                            </div>
                            <span className="text-sm font-bold tabular-nums text-cyan-300">
                                {Math.round(exportProgress.percent)}%
                            </span>
                        </div>
                    </div>
                    <div className="space-y-3 p-4">
                        <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">{exportProgress.message}</p>
                        <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                            <div
                                className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 transition-[width] duration-300 ease-out"
                                style={{ width: `${Math.min(100, Math.max(2, exportProgress.percent))}%` }}
                            />
                        </div>
                        <p className="flex items-start gap-2 text-xs font-medium text-amber-800 dark:text-amber-200/90">
                            <Icon icon={FiAlertCircle} className="mt-0.5 h-4 w-4 shrink-0" />
                            <span>
                                Do not close this tab or log out until the export finishes. The browser may ask you to
                                confirm if you try to leave.
                            </span>
                        </p>
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={cancelOngoingExport}
                                className="text-xs font-semibold text-slate-500 underline decoration-slate-300 underline-offset-2 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Guard Count Confirmation Modal */}
            {showGuardConfirmModal && (
                <Modal
                    isOpen={showGuardConfirmModal}
                    onClose={() => {
                        setShowGuardConfirmModal(false);
                    }}
                    title="Confirm Guard Count"
                    size="md"
                >
                    <div className="p-6 space-y-4">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                            You are requesting for deployment of <span className="font-semibold text-lg text-blue-600 dark:text-blue-400">{formData.numberOfGuards}</span> Guards, please confirm.
                        </p>
                        <div className="flex justify-end gap-3 pt-2">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowGuardConfirmModal(false);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={async () => {
                                    setShowGuardConfirmModal(false);
                                    await handleSaveRequest(true);
                                }}
                            >
                                Confirm
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Resubmit Modal */}
            {showResubmitModal && (
                <Modal
                    isOpen={showResubmitModal}
                    onClose={handleCloseResubmitModal}
                    title="Resubmit Request"
                    size="md"
                >
                    <div className="space-y-4 p-6">
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Reason for Resubmission
                            </label>
                            <textarea
                                value={resubmitReasonDraft}
                                onChange={(e) => setResubmitReasonDraft(e.target.value)}
                                className="w-full min-h-[100px] rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                placeholder="Enter the reason why you are resubmitting this request (optional)..."
                            />
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <Button variant="secondary" onClick={handleCloseResubmitModal}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleConfirmResubmit}
                                disabled={updating}
                            >
                                {updating ? 'Resubmitting...' : 'Confirm Resubmit'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Regional Rejection Modal */}
            {showRegionalRejectModal && (
                <Modal
                    isOpen={showRegionalRejectModal}
                    onClose={handleCloseRegionalRejectModal}
                    title="Reject Request"
                    size="md"
                >
                    <div className="space-y-4 p-6">
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Reason for Rejection <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={regionalRejectReasonDraft}
                                onChange={(e) => setRegionalRejectReasonDraft(e.target.value)}
                                className="w-full min-h-[100px] rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                placeholder="Enter the reason why you are rejecting this request..."
                                required
                            />
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <Button variant="secondary" onClick={handleCloseRegionalRejectModal} disabled={regionalRejectSending}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleConfirmRegionalReject}
                                disabled={regionalRejectSending || !regionalRejectReasonDraft.trim()}
                                className="!bg-red-600 hover:!bg-red-700 !text-white"
                            >
                                {regionalRejectSending ? 'Rejecting...' : 'Confirm Reject'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Toast */}
            {showToast && (
                <div className="pointer-events-none fixed right-4 top-4 z-[9999]">
                    <div className="pointer-events-auto">
                        <Toast
                            message={toastMessage}
                            type={toastType}
                            onClose={() => setShowToast(false)}
                        />
                    </div>
                </div>
            )}

            {exportProgress.active && (
                <div className="pointer-events-auto fixed bottom-4 left-4 z-[100] w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 shadow-2xl shadow-slate-900/10 backdrop-blur-md dark:border-slate-600 dark:bg-slate-900/95">
                    <div className="border-b border-slate-100 bg-gradient-to-r from-slate-800 to-slate-700 px-4 py-3 dark:from-slate-900 dark:to-slate-800">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 text-white">
                                <Icon icon={FiDownload} className="h-4 w-4 opacity-90" />
                                <span className="text-sm font-semibold tracking-tight">
                                    {exportProgress.format === 'excel' ? 'Exporting Excel' : 'Exporting PDF'}
                                </span>
                            </div>
                            <span className="text-sm font-bold tabular-nums text-cyan-300">{Math.round(exportProgress.percent)}%</span>
                        </div>
                    </div>
                    <div className="space-y-3 p-4">
                        <div className="flex flex-col gap-1 text-xs text-slate-600 dark:text-slate-300">
                            <p className="font-medium text-slate-800 dark:text-slate-200">{exportProgress.message}</p>
                        </div>
                        <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                            <div
                                className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 transition-[width] duration-300 ease-out"
                                style={{ width: `${Math.min(100, Math.max(2, exportProgress.percent))}%` }}
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            {exportProgress.percent < 100 ? (
                                <button
                                    type="button"
                                    onClick={cancelOngoingExport}
                                    className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 transition dark:border-red-900/30 dark:bg-red-950/20 dark:text-red-400 dark:hover:bg-red-900/30"
                                >
                                    Cancel
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={endExportProgress}
                                    className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                                >
                                    Done
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}




